import * as React from 'react';
import { useState } from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import InputGroup from 'react-bootstrap/InputGroup';
import UpdateIcon from '@mui/icons-material/Update';
import Tooltip from '@mui/material/Tooltip';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import FormControl from '@mui/material/FormControl';
import CheckIcon from '@mui/icons-material/Check';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import Modal from 'react-bootstrap/Modal';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dropdown from 'react-bootstrap/Dropdown';
import { useParams } from 'react-router-dom';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import { getRequest, postRequest, putRequest, deleteRequest } from '../../../lib/api';
import { API_BASE_URL, CUSTOMER_PORTAL_URL } from '../../../lib/config';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AuthContext } from '../../../authContext/context';
import { checkPermission } from '../../../utils/checkPermissions';
import Swal from 'sweetalert2';
import Notes from '../Notes/note';
import jsPDF from 'jspdf';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import './conveyancing-badges.css';
import ViewQuoteDetailModal from '../../../components/ViewQuoteDetailModal';
import ViewQuoteDetailModalSale from '../../../components/ViewQuoteDetailModalSale';

function ConveyancingQuoteModal({ show, onHide, quoteData, leadData }) {
  console.log('🎯 === VIEW QUOTE DETAILS POPUP ===');
  console.log('QuoteData:', quoteData);
  console.log('Fixed Fees:', quoteData?.fixedFees);
  console.log('Disbursements:', quoteData?.disbursements);
  console.log('Breakdown:', quoteData?.breakdown);
  console.log('Branch Name:', quoteData?.branchName);
  console.log('Service Type:', quoteData?.serviceType);
  
  // Calculate totals if breakdown is missing
  const calculateTotals = () => {
    const base = quoteData?.base || leadData?.legal_fee || 0;
    const legalFeeVAT = base * 0.20;
    
    let fixedFeesTotal = 0;
    let fixedFeesVAT = 0;
    
    if (quoteData?.fixedFees) {
      quoteData.fixedFees.forEach(fee => {
        fixedFeesTotal += fee.amount;
        if (!fee.label.toLowerCase().includes('identity')) {
          fixedFeesVAT += fee.amount * 0.20;
        }
      });
    }
    
    const disbursementsTotal = quoteData?.disbursementsTotal || 
      (quoteData?.disbursements?.reduce((sum, d) => sum + d.amount, 0) || 0);
    
    const totalVAT = legalFeeVAT + fixedFeesVAT;
    const grandTotal = base + legalFeeVAT + fixedFeesTotal + fixedFeesVAT + disbursementsTotal;
    
    return {
      base,
      legalFeeVAT,
      fixedFeesTotal,
      fixedFeesVAT,
      disbursementsTotal,
      totalVAT,
      grandTotal
    };
  };
  
  const totals = calculateTotals();
  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Quote for ${leadData?.service_type?.replace(/_/g, ' ')} #${leadData?.lead_id || 'N/A'}`, 10, 20);
    
    // Subheading with legal fees total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const totalText = `Legal Fees from My Law Matters of £${(quoteData?.total || leadData?.total_quote || 0).toFixed(2)} inc. VAT`;
    doc.text(totalText, 10, 35);
    
    // Thank you message
    doc.text(`Thank you for choosing My Law Matters for your ${leadData?.service_type?.replace(/_/g, ' ')} needs.`, 10, 50);
    
    // Create manual table since autoTable might have issues
    let yPosition = 65;
    
    // Table header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.rect(10, yPosition, 60, 8); // Description column
    doc.rect(70, yPosition, 30, 8); // Net column
    doc.rect(100, yPosition, 30, 8); // VAT column
    doc.rect(130, yPosition, 40, 8); // Gross column
    
    doc.text('Description', 12, yPosition + 5);
    doc.text('Net', 82, yPosition + 5);
    doc.text('VAT', 112, yPosition + 5);
    doc.text('Total', 142, yPosition + 5);
    
    yPosition += 8;
    
    // Table data rows
    doc.setFont('helvetica', 'normal');
    const legalFee = parseFloat(leadData?.legal_fee || quoteData?.base || 0);
    const vat = legalFee * 0.20;
    const legalTotal = legalFee + vat;
    
    // Legal Fee row
    doc.rect(10, yPosition, 60, 8);
    doc.rect(70, yPosition, 30, 8);
    doc.rect(100, yPosition, 30, 8);
    doc.rect(130, yPosition, 40, 8);
    
    doc.text('Legal Fees', 12, yPosition + 5);
    doc.text(`£${legalFee.toFixed(2)}`, 72, yPosition + 5);
    doc.text(`£${vat.toFixed(2)}`, 102, yPosition + 5);
    doc.text(`£${legalTotal.toFixed(2)}`, 132, yPosition + 5);
    
    yPosition += 8;
    
    // Disbursements
    doc.rect(10, yPosition, 60, 8);
    doc.rect(70, yPosition, 30, 8);
    doc.rect(100, yPosition, 30, 8);
    doc.rect(130, yPosition, 40, 8);
    
    doc.text('Disbursements', 12, yPosition + 5);
    doc.text('£340.00', 72, yPosition + 5);
    doc.text('£0.00', 102, yPosition + 5);
    doc.text('£340.00', 132, yPosition + 5);
    
    yPosition += 8;
    
    // Total row
    doc.setFont('helvetica', 'bold');
    doc.rect(10, yPosition, 60, 8);
    doc.rect(70, yPosition, 30, 8);
    doc.rect(100, yPosition, 30, 8);
    doc.rect(130, yPosition, 40, 8);
    
    doc.text('TOTAL', 12, yPosition + 5);
    doc.text('', 72, yPosition + 5);
    doc.text('', 102, yPosition + 5);
    doc.text(`£${(legalTotal + 340).toFixed(2)}`, 132, yPosition + 5);
    
    // Additional details if needed
    const finalY = yPosition + 20;
    doc.setFontSize(10);
    doc.text('This quote is valid for 30 days from the date of issue.', 10, finalY);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 10, finalY + 10);
    
    // Footer
    doc.setFontSize(8);
    doc.text('My Law Matters - Professional Legal Services', 10, doc.internal.pageSize.height - 20);
    
    // Save the PDF
    const filename = `${leadData?.service_type?.replace(/_/g, '_')}_Quote_${leadData?.lead_id || 'Quote'}.pdf`;
    doc.save(filename);
    
    Swal.fire('Success', 'Quote downloaded successfully!', 'success');
  };

  const handleDownload = () => {
    generatePDF();
  };
  
  // Check if this is a Purchase_Sale type
  const isPurchaseSale = leadData?.service_type === 'Purchase_Sale';

  // Helper function to format fee labels from camelCase to Title Case
  const formatFeeLabel = (label) => {
    if (!label) return '';

    // Convert camelCase to space-separated words
    let formatted = label
      // Add space before capital letters
      .replace(/([A-Z])/g, ' $1')
      // Capitalize first letter
      .replace(/^./, str => str.toUpperCase())
      // Trim extra spaces
      .trim();

    // Handle special acronyms and words
    formatted = formatted
      .replace(/\bIsa\b/g, 'ISA')
      .replace(/\bTt\b/g, 'TT')
      .replace(/\bHm\b/g, 'HM')
      .replace(/\bLand Registry\b/gi, 'Land Registry')
      .replace(/\bLand Charges\b/gi, 'Land Charges');

    return formatted;
  };

  // Render function for quote table
  const renderQuoteTable = (data, type = 'purchase') => {
    const base = data?.base || data?.legal_fee || 0;
    const fixedFees = data?.fixedFees || [];
    const disbursements = data?.disbursements || [];

    // Calculate totals
    let fixedFeesTotal = 0;
    let fixedFeesVAT = 0;

    fixedFees.forEach(fee => {
      fixedFeesTotal += fee.amount;
      if (!fee.label.toLowerCase().includes('identity')) {
        fixedFeesVAT += fee.amount * 0.20;
      }
    });

    const legalFeeVAT = base * 0.20;
    const disbursementsTotal = disbursements.reduce((sum, d) => sum + d.amount, 0);
    const totalVAT = legalFeeVAT + fixedFeesVAT;
    const grandTotal = base + legalFeeVAT + fixedFeesTotal + fixedFeesVAT + disbursementsTotal;

    return (
      <>
        {/* Total Cost Display */}
        <div className="mb-3 p-3" style={{
          backgroundColor: type === 'sale' ? '#fee' : '#f0f8ff',
          borderRadius: '8px',
          border: `2px solid ${type === 'sale' ? '#dc3545' : '#0d6efd'}`
        }}>
          <div className="d-flex align-items-baseline justify-content-center gap-3">
            <span className="text-muted">Total Cost</span>
            <span style={{
              color: type === 'sale' ? '#dc3545' : '#0d6efd',
              fontWeight: '700',
              fontSize: '1.3rem'
            }}>
              £{grandTotal.toFixed(2)}
            </span>
            <span className="text-muted">
              (£{(grandTotal - totalVAT).toFixed(2)} + £{totalVAT.toFixed(2)} VAT)
            </span>
          </div>
        </div>

        {data?.branchName && (
          <div className={`alert alert-${type === 'sale' ? 'danger' : 'info'} mb-3`}>
            <strong>Branch:</strong> {data.branchName}
            {data.branchAddress?.postcode && ` (${data.branchAddress.postcode})`}
          </div>
        )}

        <div className="table-responsive">
          <Table bordered hover style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: type === 'sale' ? '#f8d7da' : '#cfe2ff' }}>
              <tr>
                <th style={{ fontWeight: '600', width: '50%' }}>Description</th>
                <th style={{ fontWeight: '600', textAlign: 'center' }}>Net</th>
                <th style={{ fontWeight: '600', textAlign: 'center' }}>VAT</th>
                <th style={{ fontWeight: '600', textAlign: 'center' }}>Gross Total</th>
              </tr>
            </thead>
            <tbody>
              {/* Legal Fees */}
              <tr>
                <td>Legal Fees</td>
                <td style={{ textAlign: 'center' }}>£{base.toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>£{legalFeeVAT.toFixed(2)}</td>
                <td style={{ textAlign: 'center', fontWeight: '600' }}>£{(base * 1.20).toFixed(2)}</td>
              </tr>

              {/* Fixed Fees Section */}
              {fixedFees.length > 0 && (
                <>
                  <tr>
                    <td colSpan="4" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                      Fixed Fees
                    </td>
                  </tr>
                  {fixedFees.map((fee, index) => {
                    const isIdentityCheck = fee.label.toLowerCase().includes('identity');
                    const vatRate = isIdentityCheck ? 0 : 0.20;

                    return (
                      <tr key={`fixed-${index}`}>
                        <td>{formatFeeLabel(fee.label)}</td>
                        <td style={{ textAlign: 'center' }}>£{fee.amount.toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>£{(fee.amount * vatRate).toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>£{(fee.amount * (1 + vatRate)).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </>
              )}

              {/* Disbursements Section */}
              <tr>
                <td colSpan="4" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                  Disbursements
                </td>
              </tr>

              {disbursements.map((disb, index) => (
                <tr key={`disb-${index}`}>
                  <td>{formatFeeLabel(disb.label)}</td>
                  <td style={{ textAlign: 'center' }}>£{disb.amount.toFixed(2)}</td>
                  <td style={{ textAlign: 'center' }}>£0.00</td>
                  <td style={{ textAlign: 'center' }}>£{disb.amount.toFixed(2)}</td>
                </tr>
              ))}

              {/* Summary Section */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ fontWeight: 'bold' }}>
                  Summary
                </td>
              </tr>

              {/* Subtotal */}
              <tr>
                <td>Subtotal (Legal Fees + Fixed Fees)</td>
                <td style={{ textAlign: 'center' }}>£{(base + fixedFeesTotal).toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>£{(legalFeeVAT + fixedFeesVAT).toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>
                  £{(base + legalFeeVAT + fixedFeesTotal + fixedFeesVAT).toFixed(2)}
                </td>
              </tr>

              {/* Total Disbursements */}
              <tr>
                <td>Total Disbursements</td>
                <td style={{ textAlign: 'center' }}>£{disbursementsTotal.toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>£0.00</td>
                <td style={{ textAlign: 'center' }}>£{disbursementsTotal.toFixed(2)}</td>
              </tr>

              {/* Grand Total */}
              <tr style={{
                backgroundColor: type === 'sale' ? '#dc3545' : '#28a745',
                color: 'white',
                fontWeight: 'bold'
              }}>
                <td>GRAND TOTAL</td>
                <td colSpan="3" style={{ textAlign: 'center', fontSize: '1.1rem' }}>
                  £{grandTotal.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size={isPurchaseSale ? "lg" : "md"} aria-labelledby="contained-modal-title-vcenter" centered>
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title id="contained-modal-title-vcenter">
          View Quote {isPurchaseSale && '- Sale & Purchase'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isPurchaseSale ? (
          <>
            {/* Sale & Purchase Combined View */}
            <div className="row">
              {/* Sale Section */}
              <div className="col-md-6 border-end">
                <h5 className="mb-3 text-danger" style={{ fontWeight: '600' }}>
                  Sale Details
                </h5>
                {leadData?.saleQuote ? (
                  renderQuoteTable(leadData.saleQuote, 'sale')
                ) : (
                  <div className="alert alert-warning">
                    Sale quote data not available
                  </div>
                )}
              </div>

              {/* Purchase Section */}
              <div className="col-md-6">
                <h5 className="mb-3 text-primary" style={{ fontWeight: '600' }}>
                  Purchase Details
                </h5>
                {leadData?.purchaseQuote || quoteData ? (
                  renderQuoteTable(leadData?.purchaseQuote || quoteData, 'purchase')
                ) : (
                  <div className="alert alert-warning">
                    Purchase quote data not available
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 text-center border-top pt-3">
              <Button
                variant="primary"
                onClick={handleDownload}
                style={{
                  minWidth: '120px',
                  fontSize: '0.875rem'
                }}
              >
                <i className="fas fa-download me-2"></i>
                Download
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Single Service Type View (Purchase or Sale only) */}
            {renderQuoteTable(quoteData, leadData?.service_type?.toLowerCase() === 'sale' ? 'sale' : 'purchase')}

            {quoteData?.propertyValue && (
              <div className="mt-3 text-muted text-center">
                <small>Property Value: £{quoteData.propertyValue.toLocaleString('en-GB')}</small>
              </div>
            )}

            <div className="mt-4 text-center">
              <Button
                variant="primary"
                onClick={handleDownload}
                style={{
                  minWidth: '120px',
                  fontSize: '0.875rem'
                }}
              >
                <i className="fas fa-download me-2"></i>
                Download
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

// Calculate Stamp Duty Land Tax based on purchase price (tiered)
const calculateStampDutyLandTax = (purchasePrice) => {
  if (!purchasePrice || purchasePrice <= 0) return 0;

  const price = Number(purchasePrice);
  let stampDuty = 0;

  // Tiered calculation
  if (price > 1500000) {
    stampDuty += (price - 1500000) * 0.12;
    stampDuty += (1500000 - 925000) * 0.10;
    stampDuty += (925000 - 250000) * 0.05;
    stampDuty += (250000 - 125000) * 0.02;
  } else if (price > 925000) {
    stampDuty += (price - 925000) * 0.10;
    stampDuty += (925000 - 250000) * 0.05;
    stampDuty += (250000 - 125000) * 0.02;
  } else if (price > 250000) {
    stampDuty += (price - 250000) * 0.05;
    stampDuty += (250000 - 125000) * 0.02;
  } else if (price > 125000) {
    stampDuty += (price - 125000) * 0.02;
  }

  return Math.round(stampDuty * 100) / 100;
};

// Revise Quote Modal Component
function ReviseQuoteModal({ show, onHide, leadData, authToken, onReviseQuote, legalFeeData, quoteFeeData }) {
  // 🎯 Check if this is Purchase_Sale
  const isPurchaseSale = leadData?.service_type === 'Purchase_Sale';

  // State for single service types
  const [fees, setFees] = useState({
    legal_fee: 0,
    // Fixed Fees
    leasehold_fee: 0,
    mortgage_admin_fee: 0,
    tt_fee: 0,
    help_to_buy_isa_fee: 0,
    help_to_buy_fee: 0,
    right_to_buy_fee: 0,
    shared_ownership_fee: 0,
    new_build_fee: 0,
    islamic_mortgage_fee: 0,
    auction_repossession_fee: 0,
    gifted_deposit_fee: 0,
    gifted_deposit_fee_additional: 0,
    buy_to_let_fee: 0,
    arranging_indemnity_policy: 0,
    identity_check: 0,
    // Disbursements
    hm_land_registry_fee: 0,
    stamp_duty: 0,
    land_registry_search: 0,
    land_charges_search: 0,
    search_pack: 0
  });

  // 🎯 Separate state for Purchase_Sale
  const [purchaseFees, setPurchaseFees] = useState({ ...fees });
  const [saleFees, setSaleFees] = useState({ ...fees });

  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0,
    legalCost: { net: 0, vat: 0, gross: 0 },
    disbursementCost: { net: 0, vat: 0, gross: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch calculation data from leads_calculations table (NESTED FORMAT)
  // Uses same approach as View Quote Detail modal
  React.useEffect(() => {
    console.log('🔍 ReviseQuoteModal useEffect triggered', {
      show,
      leadData: !!leadData,
      leadId: leadData?.lead_id,
      authToken: !!authToken
    });

    if (show && leadData?.lead_id) {
      const fetchCalculationData = async () => {
        try {
          setIsLoading(true);

          // Use authToken prop or fallback to localStorage
          const token = authToken || localStorage.getItem('token');

          console.log('📋 ReviseQuoteModal: Fetching calculation data for lead:', leadData.lead_id);
          console.log('🔑 Using token:', token ? 'Token present' : 'No token!');

          // Get partner_id if exists
          const partnerId = leadData.partner_id?._id || leadData.partner_id;
          console.log('🤝 [Revise Modal] Partner ID:', partnerId);

          // Fetch from database (same as View Quote Detail)
          const fetchUrl = partnerId
            ? `${API_BASE_URL}private/fetch_lead_calculations/${leadData.lead_id}?partner_id=${partnerId}`
            : `${API_BASE_URL}private/fetch_lead_calculations/${leadData.lead_id}`;

          console.log('📞 [Revise Modal] Fetching from:', fetchUrl);

          const res = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          const result = await res.json();

          console.log('📡 ReviseQuoteModal API Response:', result);

          if (result.status === 200 && result.data) {
            // 🎯 Check if Purchase_Sale with separate breakdowns
            if (result.data.purchase_breakdown && result.data.sale_breakdown) {
              console.log('📊 Purchase_Sale detected - loading separate Purchase and Sale data');

              // Helper function to parse fees/disbursements
              const parseFees = (calcFees, disbursements, purchaserCount) => {
                const identityCheckBase = calcFees?.identityCheck?.amount || disbursements?.identityCheck?.amount || 0;
                const identityCheckTotal = identityCheckBase * purchaserCount;

                return {
                  legal_fee: calcFees?.legalFee?.amount || 0,
                  leasehold_fee: calcFees?.leaseholdFee?.amount || 0,
                  mortgage_admin_fee: calcFees?.mortgageAdminFee?.amount || 0,
                  tt_fee: calcFees?.ttFee?.amount || calcFees?.telegraphicTransferFee?.amount || 0,
                  help_to_buy_isa_fee: calcFees?.helpToBuyISAFee?.amount || 0,
                  help_to_buy_fee: calcFees?.helpToBuyFee?.amount || 0,
                  help_to_buy_equity_loan_fee: calcFees?.helpToBuyEquityLoanFee?.amount || 0,
                  right_to_buy_fee: calcFees?.rightToBuyFee?.amount || 0,
                  first_time_buyer_fee: calcFees?.firstTimeBuyerFee?.amount || 0,
                  shared_ownership_fee: calcFees?.sharedOwnershipFee?.amount || 0,
                  new_build_fee: calcFees?.newBuildFee?.amount || 0,
                  islamic_mortgage_fee: calcFees?.islamicMortgageFee?.amount || 0,
                  auction_repossession_fee: calcFees?.auctionRepossessionFee?.amount || 0,
                  gifted_deposit_fee: calcFees?.giftedDepositFee?.amount || calcFees?.giftedDepositFeeFirstGiftor?.amount || 0,
                  gifted_deposit_fee_additional: calcFees?.giftedDepositFeeAdditionalGiftors?.amount || 0,
                  buy_to_let_fee: calcFees?.buyToLetFee?.amount || 0,
                  arranging_indemnity_policy: calcFees?.arrangingIndemnityPolicy?.amount || 0,
                  identity_check: identityCheckTotal,
                  hm_land_registry_fee: disbursements?.hmLandRegistryFee?.amount || 0,
                  stamp_duty: disbursements?.stampDuty?.amount || disbursements?.stamp_duty_land_tax?.amount || 0,
                  land_registry_search: disbursements?.landRegistrySearch?.amount || 0,
                  land_charges_search: disbursements?.landChargesSearch?.amount || 0,
                  search_pack: disbursements?.searchPack?.amount || 0,
                  official_copies: disbursements?.officialCopies?.amount || 0
                };
              };

              // Parse Purchase data
              const purchaserCount = leadData?.customer_ids?.length || 1;
              const purchaseFeesData = parseFees(
                result.data.purchase_breakdown.fees,
                result.data.purchase_breakdown.disbursements,
                purchaserCount
              );
              setPurchaseFees(purchaseFeesData);

              // Parse Sale data
              const saleFeesData = parseFees(
                result.data.sale_breakdown.fees,
                result.data.sale_breakdown.disbursements,
                purchaserCount
              );
              setSaleFees(saleFeesData);

              console.log('💰 Purchase fees:', purchaseFeesData);
              console.log('💰 Sale fees:', saleFeesData);

            } else {
              // Single service type (existing code)
              const { fees: calcFees, disbursements } = result.data;
              console.log('✅ Calculation data fetched (nested format):', { calcFees, disbursements });

              const purchaserCount = leadData?.customer_ids?.length || leadData?.customers?.length || leadData?.purchasers?.length || 1;
              console.log('👥 [Revise Modal] Purchaser Count for Identity Check:', purchaserCount);

              const identityCheckBase = calcFees?.identityCheck?.amount || disbursements?.identityCheck?.amount || 0;
              const identityCheckTotal = identityCheckBase * purchaserCount;

              const initialFees = {
                legal_fee: calcFees?.legalFee?.amount || 0,
                leasehold_fee: calcFees?.leaseholdFee?.amount || 0,
                mortgage_admin_fee: calcFees?.mortgageAdminFee?.amount || 0,
                tt_fee: calcFees?.ttFee?.amount || calcFees?.telegraphicTransferFee?.amount || 0,
                help_to_buy_isa_fee: calcFees?.helpToBuyISAFee?.amount || 0,
                help_to_buy_fee: calcFees?.helpToBuyFee?.amount || 0,
                help_to_buy_equity_loan_fee: calcFees?.helpToBuyEquityLoanFee?.amount || 0,
                right_to_buy_fee: calcFees?.rightToBuyFee?.amount || 0,
                first_time_buyer_fee: calcFees?.firstTimeBuyerFee?.amount || 0,
                shared_ownership_fee: calcFees?.sharedOwnershipFee?.amount || 0,
                new_build_fee: calcFees?.newBuildFee?.amount || 0,
                islamic_mortgage_fee: calcFees?.islamicMortgageFee?.amount || 0,
                auction_repossession_fee: calcFees?.auctionRepossessionFee?.amount || 0,
                gifted_deposit_fee: calcFees?.giftedDepositFee?.amount || calcFees?.giftedDepositFeeFirstGiftor?.amount || 0,
                gifted_deposit_fee_additional: calcFees?.giftedDepositFeeAdditionalGiftors?.amount || 0,
                buy_to_let_fee: calcFees?.buyToLetFee?.amount || 0,
                arranging_indemnity_policy: calcFees?.arrangingIndemnityPolicy?.amount || 0,
                identity_check: identityCheckTotal,
                hm_land_registry_fee: disbursements?.hmLandRegistryFee?.amount || 0,
                stamp_duty: disbursements?.stampDuty?.amount || disbursements?.stamp_duty_land_tax?.amount || 0,
                land_registry_search: disbursements?.landRegistrySearch?.amount || 0,
                land_charges_search: disbursements?.landChargesSearch?.amount || 0,
                search_pack: disbursements?.searchPack?.amount || 0
              };

              console.log('💰 Parsed fees for modal:', initialFees);
              setFees(initialFees);
              calculateTotals(initialFees);
            }
          } else {
            console.warn('⚠️ No calculation data found, using default values');
          }
        } catch (error) {
          console.error('❌ Error fetching calculation data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchCalculationData();
    }
  }, [show, leadData?.lead_id, authToken]);
  
  // Calculate totals whenever fees change
  const calculateTotals = (currentFees) => {
    // Legal Fees
    const legalFeeNet = Number(currentFees.legal_fee) || 0;
    const legalFeeVAT = legalFeeNet * 0.20;

    // Fixed Fees (with VAT)
    const leaseholdFeeNet = Number(currentFees.leasehold_fee) || 0;
    const leaseholdFeeVAT = leaseholdFeeNet * 0.20;

    const mortgageAdminFeeNet = Number(currentFees.mortgage_admin_fee) || 0;
    const mortgageAdminFeeVAT = mortgageAdminFeeNet * 0.20;

    const ttFeeNet = Number(currentFees.tt_fee) || 0;
    const ttFeeVAT = ttFeeNet * 0.20;

    const helpToBuyISAFeeNet = Number(currentFees.help_to_buy_isa_fee) || 0;
    const helpToBuyISAFeeVAT = helpToBuyISAFeeNet * 0.20;

    const helpToBuyFeeNet = Number(currentFees.help_to_buy_fee) || 0;
    const helpToBuyFeeVAT = helpToBuyFeeNet * 0.20;

    const rightToBuyFeeNet = Number(currentFees.right_to_buy_fee) || 0;
    const rightToBuyFeeVAT = rightToBuyFeeNet * 0.20;

    const sharedOwnershipFeeNet = Number(currentFees.shared_ownership_fee) || 0;
    const sharedOwnershipFeeVAT = sharedOwnershipFeeNet * 0.20;

    const newBuildFeeNet = Number(currentFees.new_build_fee) || 0;
    const newBuildFeeVAT = newBuildFeeNet * 0.20;

    const islamicMortgageFeeNet = Number(currentFees.islamic_mortgage_fee) || 0;
    const islamicMortgageFeeVAT = islamicMortgageFeeNet * 0.20;

    const auctionRepossessionFeeNet = Number(currentFees.auction_repossession_fee) || 0;
    const auctionRepossessionFeeVAT = auctionRepossessionFeeNet * 0.20;

    const giftedDepositFeeNet = Number(currentFees.gifted_deposit_fee) || 0;
    const giftedDepositFeeVAT = giftedDepositFeeNet * 0.20;

    const buyToLetFeeNet = Number(currentFees.buy_to_let_fee) || 0;
    const buyToLetFeeVAT = buyToLetFeeNet * 0.20;

    const arrangingIndemnityPolicyNet = Number(currentFees.arranging_indemnity_policy) || 0;
    const arrangingIndemnityPolicyVAT = arrangingIndemnityPolicyNet * 0.20;

    const identityCheckNet = Number(currentFees.identity_check) || 0;
    const identityCheckVAT = 0; // No VAT on Identity Check

    // Disbursements (No VAT)
    const hmLandRegistryFeeNet = Number(currentFees.hm_land_registry_fee) || 0;
    const hmLandRegistryFeeVAT = 0;

    const stampDutyNet = Number(currentFees.stamp_duty) || 0;
    const stampDutyVAT = 0;

    const landRegistrySearchNet = Number(currentFees.land_registry_search) || 0;
    const landRegistrySearchVAT = landRegistrySearchNet * 0.20;

    const landChargesSearchNet = Number(currentFees.land_charges_search) || 0;
    const landChargesSearchVAT = landChargesSearchNet * 0.20;

    const searchPackNet = Number(currentFees.search_pack) || 0;
    const searchPackVAT = 0;

    // Calculate Legal Cost (all fees before disbursements)
    const legalCostNet = legalFeeNet + leaseholdFeeNet + mortgageAdminFeeNet + ttFeeNet +
                        helpToBuyISAFeeNet + helpToBuyFeeNet + rightToBuyFeeNet + sharedOwnershipFeeNet +
                        newBuildFeeNet + islamicMortgageFeeNet + auctionRepossessionFeeNet +
                        giftedDepositFeeNet + buyToLetFeeNet + arrangingIndemnityPolicyNet + identityCheckNet;

    const legalCostVAT = legalFeeVAT + leaseholdFeeVAT + mortgageAdminFeeVAT + ttFeeVAT +
                        helpToBuyISAFeeVAT + helpToBuyFeeVAT + rightToBuyFeeVAT + sharedOwnershipFeeVAT +
                        newBuildFeeVAT + islamicMortgageFeeVAT + auctionRepossessionFeeVAT +
                        giftedDepositFeeVAT + buyToLetFeeVAT + arrangingIndemnityPolicyVAT + identityCheckVAT;

    const legalCostGross = legalCostNet + legalCostVAT;

    // Calculate Disbursement Cost (Third Party Expenditure)
    const disbursementCostNet = hmLandRegistryFeeNet + stampDutyNet + landRegistrySearchNet +
                               landChargesSearchNet + searchPackNet;

    const disbursementCostVAT = hmLandRegistryFeeVAT + stampDutyVAT + landRegistrySearchVAT +
                               landChargesSearchVAT + searchPackVAT;

    const disbursementCostGross = disbursementCostNet + disbursementCostVAT;

    // Calculate Grand Totals
    const totalNet = legalCostNet + disbursementCostNet;
    const totalVAT = legalCostVAT + disbursementCostVAT;
    const totalGross = totalNet + totalVAT;

    setTotals({
      net: totalNet,
      vat: totalVAT,
      gross: totalGross,
      legalCost: { net: legalCostNet, vat: legalCostVAT, gross: legalCostGross },
      disbursementCost: { net: disbursementCostNet, vat: disbursementCostVAT, gross: disbursementCostGross }
    });
  };
  
  const handleFeeChange = (field, value) => {
    const newFees = { ...fees, [field]: value };
    setFees(newFees);
    calculateTotals(newFees);
  };
  
  const handleReviseQuote = async () => {
    try {
      setIsLoading(true);

      console.log('🧮 ============ REVISE QUOTE MODAL ============');
      console.log('📊 Calculated Totals from Modal:');
      console.log('✅ TOTAL NET:', totals.net);
      console.log('✅ TOTAL VAT:', totals.vat);
      console.log('✅ TOTAL GROSS:', totals.gross);
      console.log('=============================================');

      const payload = {
        lead_id: leadData.lead_id || leadData._id,
        legal_fee: totals.net,    // Store TOTAL NET (Legal Cost + Disbursement Cost)
        vat_amount: totals.vat,   // Store TOTAL VAT
        total_quote: totals.gross, // Store TOTAL GROSS (NET + VAT)
        // Include all fees for updating ConveyancingBranchManage
        fees: {
          legal_fee: Number(fees.legal_fee),
          // Fixed Fees
          leasehold_fee: Number(fees.leasehold_fee),
          mortgage_admin_fee: Number(fees.mortgage_admin_fee),
          tt_fee: Number(fees.tt_fee),
          help_to_buy_isa_fee: Number(fees.help_to_buy_isa_fee),
          help_to_buy_fee: Number(fees.help_to_buy_fee),
          right_to_buy_fee: Number(fees.right_to_buy_fee),
          shared_ownership_fee: Number(fees.shared_ownership_fee),
          new_build_fee: Number(fees.new_build_fee),
          islamic_mortgage_fee: Number(fees.islamic_mortgage_fee),
          auction_repossession_fee: Number(fees.auction_repossession_fee),
          gifted_deposit_fee: Number(fees.gifted_deposit_fee),
          gifted_deposit_fee_additional: Number(fees.gifted_deposit_fee_additional),
          buy_to_let_fee: Number(fees.buy_to_let_fee),
          arranging_indemnity_policy: Number(fees.arranging_indemnity_policy),
          identity_check: Number(fees.identity_check),
          // Disbursements
          hm_land_registry_fee: Number(fees.hm_land_registry_fee),
          stamp_duty: Number(fees.stamp_duty),
          land_registry_search: Number(fees.land_registry_search),
          land_charges_search: Number(fees.land_charges_search),
          search_pack: Number(fees.search_pack)
        },
        // Include branch and product info for updating ConveyancingBranchManage
        branch_id: legalFeeData?.branch_id || leadData?.branch_id,
        property_value: leadData?.property_value
      };
      
      console.log('📝 Modal submitting revised quote:', payload);
      
      // Call the onReviseQuote callback
      await onReviseQuote(payload);
      
      // Close modal
      onHide();
    } catch (error) {
      console.error('❌ Modal error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} size={isPurchaseSale ? "xl" : "lg"} centered className="revise-quote-modal">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600' }}>
          Revise Quote {isPurchaseSale && '- Sale & Purchase'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '1.5rem' }}>
        {isPurchaseSale ? (
          <div>
            <div className="alert alert-info mb-3">
              <strong>Note:</strong> For Purchase & Sale leads, please revise the quote from the Quote Summary section for now.
              Full Purchase_Sale revise functionality is coming soon!
            </div>
            <div className="row">
              <div className="col-md-6">
                <h5 style={{ color: '#0d6efd' }}>PURCHASE</h5>
                <ul className="list-unstyled">
                  <li>Legal Fee: £{purchaseFees.legal_fee}</li>
                  <li>TT Fee: £{purchaseFees.tt_fee}</li>
                  <li>Identity Check: £{purchaseFees.identity_check}</li>
                  <li>Stamp Duty: £{purchaseFees.stamp_duty}</li>
                </ul>
              </div>
              <div className="col-md-6">
                <h5 style={{ color: '#dc3545' }}>SALE</h5>
                <ul className="list-unstyled">
                  <li>Legal Fee: £{saleFees.legal_fee}</li>
                  <li>TT Fee: £{saleFees.tt_fee}</li>
                  <li>Identity Check: £{saleFees.identity_check}</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div>
        <div className="table-responsive">
          <Table bordered hover style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ 
                  fontWeight: '600', 
                  fontSize: '0.95rem',
                  padding: '12px 15px',
                  borderRight: '2px solid #dee2e6',
                  width: '40%'
                }}>
                  Purchase <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Net</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>VAT</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Gross</th>
              </tr>
            </thead>
            <tbody>
              {/* Legal Fees */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>
                  Legal Fees
                </td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ width: '140px', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>£</span>
                    <input
                      type="number"
                      value={fees.legal_fee}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFees(prev => ({ ...prev, legal_fee: newValue }));
                        calculateTotals({ ...fees, legal_fee: newValue });
                      }}
                      style={{ 
                        width: '100px', 
                        padding: '5px',
                        textAlign: 'right',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.legal_fee) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.legal_fee) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Leasehold Fee */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Leasehold Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.leasehold_fee} onChange={(e) => { setFees(prev => ({ ...prev, leasehold_fee: e.target.value })); calculateTotals({ ...fees, leasehold_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.leasehold_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.leasehold_fee) * 1.20).toFixed(2)}</td>
              </tr>

              {/* Mortgage Admin Fee */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Mortgage Admin Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.mortgage_admin_fee} onChange={(e) => { setFees(prev => ({ ...prev, mortgage_admin_fee: e.target.value })); calculateTotals({ ...fees, mortgage_admin_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.mortgage_admin_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.mortgage_admin_fee) * 1.20).toFixed(2)}</td>
              </tr>

              {/* Telegraphic Transfer (TT) Fee */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Telegraphic Transfer (TT) Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.tt_fee} onChange={(e) => { setFees(prev => ({ ...prev, tt_fee: e.target.value })); calculateTotals({ ...fees, tt_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.tt_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.tt_fee) * 1.20).toFixed(2)}</td>
              </tr>

              {/* Help to Buy ISA Fee - show only if info field is true */}
              {(leadData?.info?.helpToBuyISA === true || leadData?.conveyancing_details?.help_to_buy_isa === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Help to Buy ISA Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.help_to_buy_isa_fee} onChange={(e) => { setFees(prev => ({ ...prev, help_to_buy_isa_fee: e.target.value })); calculateTotals({ ...fees, help_to_buy_isa_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.help_to_buy_isa_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.help_to_buy_isa_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Help to Buy Fee - show only if info field is true */}
              {(leadData?.info?.helpToBuy === true || leadData?.conveyancing_details?.help_to_buy === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Help to Buy Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.help_to_buy_fee} onChange={(e) => { setFees(prev => ({ ...prev, help_to_buy_fee: e.target.value })); calculateTotals({ ...fees, help_to_buy_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.help_to_buy_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.help_to_buy_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Right to Buy Fee - show only if info field is true */}
              {(leadData?.info?.rightToBuy === true || leadData?.conveyancing_details?.right_to_buy === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Right to Buy Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.right_to_buy_fee} onChange={(e) => { setFees(prev => ({ ...prev, right_to_buy_fee: e.target.value })); calculateTotals({ ...fees, right_to_buy_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.right_to_buy_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.right_to_buy_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Shared Ownership Fee - show only if info field is true */}
              {(leadData?.info?.sharedOwnership === true || leadData?.conveyancing_details?.shared_ownership === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Shared Ownership Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.shared_ownership_fee} onChange={(e) => { setFees(prev => ({ ...prev, shared_ownership_fee: e.target.value })); calculateTotals({ ...fees, shared_ownership_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.shared_ownership_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.shared_ownership_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* New Build Fee - show only if info field is true */}
              {(leadData?.info?.newBuild === true || leadData?.conveyancing_details?.new_build === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>New Build Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.new_build_fee} onChange={(e) => { setFees(prev => ({ ...prev, new_build_fee: e.target.value })); calculateTotals({ ...fees, new_build_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.new_build_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.new_build_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Islamic Mortgage Fee - show only if info field is true */}
              {(leadData?.info?.islamicMortgage === true || leadData?.conveyancing_details?.islamic_mortgage === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Islamic Mortgage Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.islamic_mortgage_fee} onChange={(e) => { setFees(prev => ({ ...prev, islamic_mortgage_fee: e.target.value })); calculateTotals({ ...fees, islamic_mortgage_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.islamic_mortgage_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.islamic_mortgage_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Auction/Repossession Fee - show only if info field is true */}
              {(leadData?.info?.auctionRepossession === true || leadData?.conveyancing_details?.auction_repossession === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Auction / Repossession Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.auction_repossession_fee} onChange={(e) => { setFees(prev => ({ ...prev, auction_repossession_fee: e.target.value })); calculateTotals({ ...fees, auction_repossession_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.auction_repossession_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.auction_repossession_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Gifted Deposit Fee - show only if info field > 0 */}
              {((leadData?.info?.giftedDeposit > 0 || leadData?.conveyancing_details?.gifted_deposit > 0)) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Gifted Deposit Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.gifted_deposit_fee} onChange={(e) => { setFees(prev => ({ ...prev, gifted_deposit_fee: e.target.value })); calculateTotals({ ...fees, gifted_deposit_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.gifted_deposit_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.gifted_deposit_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Buy to Let Fee - show only if info field is true */}
              {(leadData?.info?.buyToLet === true || leadData?.conveyancing_details?.buy_to_let === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Buy to Let Fee</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.buy_to_let_fee} onChange={(e) => { setFees(prev => ({ ...prev, buy_to_let_fee: e.target.value })); calculateTotals({ ...fees, buy_to_let_fee: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.buy_to_let_fee) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.buy_to_let_fee) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Arranging an Indemnity Policy - show only if info field is true */}
              {(leadData?.info?.arrangingIndemnityPolicy === true || leadData?.conveyancing_details?.arranging_indemnity_policy === true) && (
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Arranging an Indemnity Policy</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <input type="number" value={fees.arranging_indemnity_policy} onChange={(e) => { setFees(prev => ({ ...prev, arranging_indemnity_policy: e.target.value })); calculateTotals({ ...fees, arranging_indemnity_policy: e.target.value }); }} style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.arranging_indemnity_policy) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.arranging_indemnity_policy) * 1.20).toFixed(2)}</td>
              </tr>
              )}

              {/* Identity Check */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Identity Check</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.identity_check).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{Number(fees.identity_check).toFixed(2)}</td>
              </tr>
              
              {/* Disbursement - Third Party Expenditure Section */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ padding: '8px 15px', fontWeight: '600', fontSize: '0.95rem' }}>
                  Disbursement - Third Party Expenditure <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </td>
              </tr>
              
              {/* HM Land Registry Fee */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>HM Land Registry Fee</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.hm_land_registry_fee).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{Number(fees.hm_land_registry_fee).toFixed(2)}</td>
              </tr>

              {/* Stamp Duty Land Tax */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Stamp Duty Land Tax</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.stamp_duty).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{Number(fees.stamp_duty).toFixed(2)}</td>
              </tr>

              {/* Land Registry Priority Search */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Land Registry Priority Search</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.land_registry_search).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.land_registry_search) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.land_registry_search) * 1.20).toFixed(2)}</td>
              </tr>

              {/* Land Charges Search */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Land Charges Search</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.land_charges_search).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{(Number(fees.land_charges_search) * 0.20).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{(Number(fees.land_charges_search) * 1.20).toFixed(2)}</td>
              </tr>

              {/* Search Pack */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Search Pack - Local Authority, Water & Drainage, Environmental Search (FCI Premium)</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{Number(fees.search_pack).toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>£{Number(fees.search_pack).toFixed(2)}</td>
              </tr>

              {/* TOTAL LEGAL COST */}
              <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL LEGAL COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.gross.toFixed(2)}</td>
              </tr>

              {/* TOTAL DISBURSEMENT COST */}
              <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL DISBURSEMENT COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.gross.toFixed(2)}</td>
              </tr>

              {/* Total (Legal cost + disbursement) */}
              <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', fontSize: '1rem', borderTop: '2px solid #28a745' }}>
                <td style={{ padding: '15px', borderRight: '2px solid #dee2e6' }}>Total (Legal cost + disbursement)</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.gross.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', padding: '0 20px 20px 20px', backgroundColor: '#f8f9fa' }}>
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          style={{ 
            paddingLeft: '20px', 
            paddingRight: '20px',
            fontWeight: '500'
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleReviseQuote}
          disabled={isLoading}
          style={{ 
            paddingLeft: '20px', 
            paddingRight: '20px',
            fontWeight: '500',
            backgroundColor: '#dc3545',
            borderColor: '#dc3545'
          }}
        >
          {isLoading ? 'Updating...' : 'Revise Quote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers = [], serviceType = 'Purchase' }) {
  const [searchFields, setSearchFields] = React.useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{serviceType === 'Sale' ? 'Add Vendor' : 'Add Purchaser'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field === 'phone_number' ? 'Phone Number' : field.charAt(0).toUpperCase() + field.slice(1)}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                          style={{
                            fontSize: '12px',
                            padding: '4px 12px'
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

// Edit Modal Component  
function EditConveyancingModal({ show, onHide, leadData, authToken, onUpdate }) {
  const [formData, setFormData] = React.useState({
    // Property Address
    line1: '',
    line2: '',
    town: '',
    county: '',
    postcode: '',

    // Property Details
    property_value: '',
    service_type: 'Purchase',
    ownership_type: 'Freehold',
    completion_date: '',

    // Purchasers
    purchasers: [],

    // Info fields - Purchase (18 fields)
    firstTimeBuyer: false,
    helpToBuyISA: false,
    helpToBuy: false,
    helpToBuyEquityLoan: false,
    rightToBuy: false,
    sharedOwnership: false,
    buyingWithMortgage: false,
    newBuild: false,
    islamicMortgage: false,
    auctionRepossession: false,
    giftedDeposit: '',
    multipleProperties: false,
    buyToLet: false,
    arrangingIndemnityPolicy: false,
    unregisteredProperty: false,
    staircasing: false,
    isNonUKResident: false,
    nonResidentialMixed: false,

    // Info fields - Sale specific (5 fields)
    sellingWithMortgage: false
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [showCustomerModal, setShowCustomerModal] = React.useState(false);

  // Auto-fill form when modal opens or leadData changes
  React.useEffect(() => {
    if (leadData && show) {
      console.log('Auto-filling edit form with lead data:', leadData);
      
      // Parse property address
      let addressData = {
        line1: '',
        line2: '',
        town: '',
        county: '',
        postcode: ''
      };

      if (leadData.property_address) {
        if (typeof leadData.property_address === 'string') {
          const parts = leadData.property_address.split(',').map(p => p.trim());
          addressData.line1 = parts[0] || '';
          addressData.line2 = parts[1] || '';
          addressData.town = parts[2] || '';
          addressData.county = parts[3] || '';
          addressData.postcode = parts[parts.length - 1] || '';
        } else if (typeof leadData.property_address === 'object') {
          addressData.line1 = leadData.property_address.address1 || leadData.property_address.line1 || '';
          addressData.line2 = leadData.property_address.address2 || leadData.property_address.line2 || '';
          addressData.town = leadData.property_address.town || leadData.property_address.city || '';
          addressData.county = leadData.property_address.county || leadData.property_address.state || '';
          addressData.postcode = leadData.property_address.postcode || leadData.property_address.postal_code || '';
        }
      }

      setFormData({
        ...addressData,
        property_value: leadData.property_value || '',
        service_type: leadData.service_type || 'Purchase',
        ownership_type: leadData.leasehold ? 'Leasehold' : 'Freehold',
        completion_date: leadData.completion_date ? new Date(leadData.completion_date).toISOString().split('T')[0] : '',
        purchasers: leadData.customer_ids || [],
        // Info fields - Purchase
        firstTimeBuyer: leadData.first_time_buyer || false,
        helpToBuyISA: leadData.help_to_buy_isa || false,
        helpToBuy: leadData.help_to_buy || false,
        helpToBuyEquityLoan: leadData.help_to_buy_equity_loan || false,
        rightToBuy: leadData.right_to_buy || false,
        sharedOwnership: leadData.shared_ownership || false,
        buyingWithMortgage: !!leadData.mortgage_lender,
        newBuild: leadData.new_build || false,
        islamicMortgage: leadData.islamic_mortgage || false,
        auctionRepossession: leadData.auction_repossession || false,
        giftedDeposit: leadData.gifted_deposit || '',
        multipleProperties: leadData.multiple_properties || false,
        buyToLet: leadData.buy_to_let || false,
        arrangingIndemnityPolicy: leadData.arranging_indemnity_policy || false,
        unregisteredProperty: leadData.unregistered_property || false,
        staircasing: leadData.staircasing || false,
        isNonUKResident: leadData.is_non_uk_resident || false,
        nonResidentialMixed: leadData.non_residential_mixed || false,
        // Info fields - Sale
        sellingWithMortgage: leadData.selling_with_mortgage || leadData.info?.sellingWithMortgage || leadData.conveyancing_details?.selling_with_mortgage || false
      });
    }
  }, [leadData, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const updatePayload = {
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          county: formData.county,
          postcode: formData.postcode
        },
        property_value: parseFloat(formData.property_value) || 0,
        ownership_type: formData.ownership_type,
        completion_date: formData.completion_date,
        
        // Customer fields
        customer_ids: formData.purchasers.map(p => p._id || p),
        
        // Info fields - Purchase
        first_time_buyer: formData.firstTimeBuyer,
        help_to_buy_isa: formData.helpToBuyISA,
        help_to_buy: formData.helpToBuy,
        help_to_buy_equity_loan: formData.helpToBuyEquityLoan,
        right_to_buy: formData.rightToBuy,
        shared_ownership: formData.sharedOwnership,
        buying_with_mortgage: formData.buyingWithMortgage,
        new_build: formData.newBuild,
        islamic_mortgage: formData.islamicMortgage,
        auction_repossession: formData.auctionRepossession,
        gifted_deposit: formData.giftedDeposit,
        multiple_properties: formData.multipleProperties,
        buy_to_let: formData.buyToLet,
        arranging_indemnity_policy: formData.arrangingIndemnityPolicy,
        unregistered_property: formData.unregisteredProperty,
        staircasing: formData.staircasing,
        is_non_uk_resident: formData.isNonUKResident,
        non_residential_mixed: formData.nonResidentialMixed,

        // Info fields - Sale
        selling_with_mortgage: formData.sellingWithMortgage
      };

      console.log('Updating conveyancing lead:', updatePayload);

      // Use appropriate API endpoint based on service_type
      const apiEndpoint = formData.service_type === 'Sale'
        ? `private/editConveyancingSale/${leadData._id}`
        : `private/editConveyancingPurchase/${leadData._id}`;

      const response = await putRequest(apiEndpoint, updatePayload);

      if (response?.status === 'success' || response?.status === 200) {
        Swal.fire('Success', 'Conveyancing details updated successfully!', 'success');
        onUpdate(updatePayload);
        onHide();
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating conveyancing:', error);
      Swal.fire('Error', error.message || 'Failed to update conveyancing details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleInfoClick = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleRemovePurchaser = (index) => {
    setFormData(prev => ({
      ...prev,
      purchasers: prev.purchasers.filter((_, i) => i !== index)
    }));
  };

  const handleAddPurchaser = (customer) => {
    setFormData(prev => ({
      ...prev,
      purchasers: [...prev.purchasers, customer]
    }));
  };

  // Info button style
  const infoButtonStyle = (isActive) => ({
    padding: '6px 12px',
    margin: '3px',
    border: isActive ? '1px solid #6f42c1' : '1px solid #ced4da',
    borderRadius: '3px',
    backgroundColor: isActive ? '#6f42c1' : 'white',
    color: isActive ? 'white' : '#495057',
    fontSize: '13px',
    fontWeight: '400',
    cursor: 'pointer',
    transition: 'all 0.15s',
    outline: 'none',
    textTransform: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '500' }}>
          Edit Conveyancing #{leadData?.lead_id || 'L5633'} - {leadData?.service_type?.replace(/_/g, ' ') || 'Purchase'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '0' }}>
        {/* Purchase/Sale Details Header */}
        <div style={{ 
          backgroundColor: '#e9ecef', 
          padding: '10px 20px', 
          marginBottom: '0',
          fontSize: '14px',
          fontWeight: '500',
          color: '#495057'
        }}>
          {formData.service_type === 'Sale' ? 'Sale' : 'Purchase'} Details
        </div>

        <Form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* Property Section */}
          <div style={{ marginBottom: '25px' }}>
            <h6 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#212529' }}>Property</h6>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontSize: '13px', marginBottom: '5px', color: '#495057', fontWeight: '500' }}>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="line1"
                    value={formData.line1}
                    onChange={handleChange}
                    placeholder="Address Line 1"
                    style={{ marginBottom: '8px', fontSize: '14px' }}
                  />
                  <Form.Control
                    type="text"
                    name="line2"
                    value={formData.line2}
                    onChange={handleChange}
                    placeholder="Address Line 2"
                    style={{ marginBottom: '8px', fontSize: '14px' }}
                  />
                  <Form.Control
                    type="text"
                    name="town"
                    value={formData.town}
                    onChange={handleChange}
                    placeholder="Town/City"
                    style={{ marginBottom: '8px', fontSize: '14px' }}
                  />
                  <Form.Control
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleChange}
                    placeholder="County"
                    style={{ marginBottom: '8px', fontSize: '14px' }}
                  />
                  <Form.Control
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    placeholder="Postcode"
                    style={{ fontSize: '14px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '13px', marginBottom: '5px', color: '#495057', fontWeight: '500' }}>Service Type</Form.Label>
                  <Form.Select
                    name="service_type"
                    value={formData.service_type || 'Purchase'}
                    onChange={handleChange}
                  >
                    <option value="Purchase">Purchase</option>
                    <option value="Sale">Sale</option>
                    <option value="Remortgage">Remortgage</option>
                  </Form.Select>
                </Form.Group>
                
                {/* Put Purchase/Sale Price and Ownership Type in same row */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: '13px', marginBottom: '5px', color: '#495057', fontWeight: '500' }}>
                        {formData.service_type === 'Sale' ? 'Sale Price' : 'Purchase Price'}
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text>£</InputGroup.Text>
                        <Form.Control
                          type="number"
                          name="property_value"
                          value={formData.property_value}
                          onChange={handleChange}
                          placeholder="0.00"
                          style={{ fontSize: '14px' }}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontSize: '13px', marginBottom: '5px', color: '#495057', fontWeight: '500' }}>Ownership Type</Form.Label>
                      <Form.Select
                        name="ownership_type"
                        value={formData.ownership_type}
                        onChange={handleChange}
                      >
                        <option value="Freehold">Freehold</option>
                        <option value="Leasehold">Leasehold</option>
                        
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                {/* Only show Est. Completion Date for Purchase and Remortgage, NOT for Sale */}
                {(formData.service_type === 'Purchase' || formData.service_type === 'Remortgage' || !formData.service_type) && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '13px', marginBottom: '5px', color: '#495057', fontWeight: '500' }}>Est. Completion Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="completion_date"
                      value={formData.completion_date}
                      onChange={handleChange}
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>
          </div>

          {/* Purchasers/Vendors Section */}
          <div style={{ marginBottom: '25px' }}>
            <h6 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#212529' }}>
              {formData.service_type === 'Sale' ? 'Vendors' : 'Purchasers'}
            </h6>
            
            <div style={{ marginBottom: '15px' }}>
              {formData.purchasers.map((purchaser, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '10px',
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  <span style={{ flex: 1 }}>
                    {purchaser.title || ''} {purchaser.forename || purchaser.firstName || ''} {purchaser.surname || purchaser.lastName || ''}
                  </span>
                  <span style={{ marginRight: '20px', color: '#6c757d' }}>
                    <MailOutlineIcon fontSize="small" style={{ marginRight: '5px' }} />
                    {purchaser.email || ''}
                  </span>
                  <span style={{ marginRight: '20px', color: '#6c757d' }}>
                    <PhoneIcon fontSize="small" style={{ marginRight: '5px' }} />
                    {purchaser.phone_number || purchaser.phone || ''}
                  </span>
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => handleRemovePurchaser(index)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              
              <Button
                variant="text"
                size="small"
                style={{ color: '#dc3545', textDecoration: 'underline' }}
                onClick={() => setShowCustomerModal(true)}
              >
                {formData.service_type === 'Sale' ? 'Add Vendor' : 'Add Purchaser'}
              </Button>
            </div>
          </div>

          {/* Info Section */}
          <div style={{ marginBottom: '25px' }}>
            <h6 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#212529' }}>Info</h6>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(formData.service_type === 'Sale'
                ? [
                    // Sale Info - Only 5 fields
                    { field: 'sellingWithMortgage', label: 'Selling with Mortgage' },
                    { field: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                    { field: 'unregisteredProperty', label: 'Unregistered Property' },
                    { field: 'sharedOwnership', label: 'Shared Ownership' },
                    { field: 'auctionRepossession', label: 'Auction / Repossession' }
                  ]
                : [
                    // Purchase Info - All 18 fields
                    { field: 'firstTimeBuyer', label: 'First Time Buyer', hasCheck: true },
                    { field: 'helpToBuyISA', label: 'Help to Buy ISA' },
                    { field: 'helpToBuy', label: 'Help to Buy' },
                    { field: 'helpToBuyEquityLoan', label: 'Help to Buy Equity Loan' },
                    { field: 'rightToBuy', label: 'Right to Buy' },
                    { field: 'sharedOwnership', label: 'Shared Ownership' },
                    { field: 'buyingWithMortgage', label: 'Buying with Mortgage' },
                    { field: 'newBuild', label: 'New Build' },
                    { field: 'islamicMortgage', label: 'Islamic Mortgage' },
                    { field: 'auctionRepossession', label: 'Auction / Repossession' },
                    { field: 'giftedDeposit', label: 'Gifted Deposit - Num Giftors', hasInput: true },
                    { field: 'multipleProperties', label: 'Multiple Properties' },
                    { field: 'buyToLet', label: 'Buy to Let' },
                    { field: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                    { field: 'unregisteredProperty', label: 'Unregistered Property' },
                    { field: 'staircasing', label: 'Staircasing' },
                    { field: 'isNonUKResident', label: 'Is Non-UK Resident' },
                    { field: 'nonResidentialMixed', label: 'Non-Residential / Mixed Use' }
                  ]
              ).map((item) => (
                <div key={item.field} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => handleInfoClick(item.field)}
                    style={infoButtonStyle(formData[item.field])}
                  >
                    {item.hasCheck && formData[item.field] && <span>✓ </span>}
                    {item.label}
                  </button>
                  {item.hasInput && formData[item.field] && (
                    <input
                      type="number"
                      value={formData.giftedDeposit}
                      onChange={(e) => setFormData(prev => ({ ...prev, giftedDeposit: e.target.value }))}
                      style={{
                        width: '60px',
                        marginLeft: '8px',
                        padding: '4px 8px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', padding: '0 20px 20px 20px', backgroundColor: '#f8f9fa', justifyContent: 'flex-start' }}>
        <Button
          variant="danger"
          onClick={handleSubmit}
          disabled={isLoading}
          style={{
            backgroundColor: '#dc3545',
            border: 'none',
            padding: '8px 24px',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '3px'
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        authToken={authToken}
        onCustomerAdd={handleAddPurchaser}
        serviceType={formData.service_type}
        selectedCustomers={formData.purchasers}
      />
    </Modal>
  );
}

export default function ViewLeadConveyancing() {
  const [modalShow, setModalShow] = React.useState(false);
  const [reviseModalShow, setReviseModalShow] = React.useState(false);
  const [leadData, setLeadData] = React.useState(null);
  const [contactAttempts, setContactAttempts] = React.useState(leadData?.contact_attempts || 0);
  const [quoteHistory, setQuoteHistory] = React.useState([]);
  const [revisionCount, setRevisionCount] = React.useState(0);
  const [selectedQuote, setSelectedQuote] = React.useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = React.useState(false);
  const [editModalShow, setEditModalShow] = React.useState(false);
  const [legalFeeData, setLegalFeeData] = React.useState(null);
  const [calculationTotals, setCalculationTotals] = React.useState(null); // 🎯 Totals from database
  const [lastFetchedLeadId, setLastFetchedLeadId] = React.useState(null); // Track which lead's data was fetched
  const [quoteFeeData, setQuoteFeeData] = React.useState(null);
  const [loadingFee, setLoadingFee] = React.useState(false);
  const [showQuoteModal, setShowQuoteModal] = React.useState(false);

  // Branch and cost calculation states
  const [branchInfo, setBranchInfo] = React.useState(null);
  const [conveyancingCosts, setConveyancingCosts] = React.useState(null);
  const [matchedBranch, setMatchedBranch] = React.useState(null);
  const [calculatedQuote, setCalculatedQuote] = React.useState(null);

  // Populated vendors and purchasers state
  const [populatedVendors, setPopulatedVendors] = React.useState([]);
  const [populatedPurchasers, setPopulatedPurchasers] = React.useState([]);
  const [isLoadingVendors, setIsLoadingVendors] = React.useState(false);
  const [isLoadingPurchasers, setIsLoadingPurchasers] = React.useState(false);

  // Communication / Email states
  const [emailTemplates, setEmailTemplates] = React.useState([]);
  const [emailLogs, setEmailLogs] = React.useState([]);
  const [loadingEmails, setLoadingEmails] = React.useState(false);
  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [viewEmailModal, setViewEmailModal] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState(null);

  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const { lead_id } = useParams();
  const userPermissions = authCtx.permissions;
  
  // Check permissions
  const canEditLeads = checkPermission(userPermissions, 'leads', 'edit');
  const canDeleteLeads = checkPermission(userPermissions, 'leads', 'delete');
  const canReviseQuotes = checkPermission(userPermissions, 'leads', 'revise_quotes');
  const canChangeGroupCode = checkPermission(userPermissions, 'leads', 'change_group_code');

  console.log('✅ Lead ID from params:', lead_id);

  const handleUpdateContactAttempt = async (type) => {
    let updatedCount = contactAttempts;

    if (type === 'increase') updatedCount += 1;
    else if (type === 'reset') updatedCount = 0;

    try {
      const res = await postRequest('private/change-contact-status', {
        lead_id: lead_id,
        contact_attempts: updatedCount,
      }, authCtx.token);

      if (res.status === 200 || res.status === 'success') {
        setContactAttempts(updatedCount);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Fetch email templates from Automation actions - Only for Conveyancing product
  const fetchEmailTemplates = async () => {
    try {
      console.log('📧 === FETCHING CONVEYANCING AUTOMATION EMAIL TEMPLATES ===');
      console.log('Auth token:', authCtx.token ? 'Present' : 'Missing');
      console.log('Full leadData:', leadData);
      console.log('Lead data product_id:', leadData?.product_id);

      // Get conveyancing product ID from leadData
      let productId = leadData?.product_id?._id || leadData?.product_id;
      console.log('🔍 Product ID from leadData:', productId);

      // If productId is not available, try to fetch it from products API
      if (!productId) {
        console.log('⚠️ Product ID not found in leadData, fetching products...');
        try {
          const productsRes = await postRequest('private/getAllProducts', {}, authCtx.token);
          console.log('📦 Products response:', productsRes);

          if (productsRes?.data) {
            // Find Conveyancing product (case-insensitive search)
            const conveyancingProduct = productsRes.data.find(p =>
              p.name?.toLowerCase().includes('conveyancing') ||
              p.product_name?.toLowerCase().includes('conveyancing')
            );

            if (conveyancingProduct) {
              productId = conveyancingProduct._id || conveyancingProduct.product_id;
              console.log('✅ Found Conveyancing product:', conveyancingProduct);
              console.log('✅ Using product ID:', productId);
            } else {
              console.log('⚠️ Conveyancing product not found in products list');
            }
          }
        } catch (prodErr) {
          console.log('❌ Error fetching products:', prodErr);
        }
      }

      // Fetch automation actions for this product
      console.log('📤 Fetching automation actions...');

      const automationUrl = `${API_BASE_URL}automationList`;
      console.log('🌐 Automation API URL:', automationUrl);

      const requestBody = {
        productId: productId,
        page: 1,
        limit: 100
      };
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(automationUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      console.log('📡 Automations response:', responseData);
      console.log('📡 Response status:', response.status);
      console.log('📡 Response OK:', response.ok);
      console.log('📡 Response data status:', responseData?.status);

      if (response.ok && (responseData?.status === 200 || responseData?.status === 'success')) {
        const automations = responseData.data?.docs || responseData.data || [];
        console.log('✅ Automations found:', automations.length);
        console.log('✅ Full automations array:', JSON.stringify(automations, null, 2));

        // Extract email templates from automation actions
        const emailTemplates = [];

        automations.forEach((automation, index) => {
          console.log(`🔍 [${index}] Checking automation:`, automation.name);
          console.log(`   Full automation data:`, automation);

          // Check if automation has effect with "Send Email"
          if (automation.effect && automation.effect.type?.toLowerCase().includes('send email')) {
            console.log(`   ✅ Found Send Email effect:`, automation.effect.type);

            // Only add if we have a valid email template ID
            if (automation.effect.emailTemplateId?._id) {
              const template = {
                _id: automation.effect.emailTemplateId._id,
                name: automation.effect.emailTemplateId?.name || 'Unnamed Template',
                template_id: automation.effect.emailTemplateId._id,
                automation_id: automation._id,
                effect: automation.effect
              };

              emailTemplates.push(template);
              console.log('  ✅ Added email automation to dropdown:', template.name);
            } else {
              console.log('  ⚠️  Skipping automation - no email template ID found');
            }
          } else {
            console.log(`   ⏭️  Skipping automation (not Send Email type), effect type:`, automation.effect?.type);
          }
        });

        console.log('📧 Total email templates extracted:', emailTemplates.length);
        console.log('📋 Templates list:', emailTemplates);

        setEmailTemplates(emailTemplates);
      } else {
        console.log('⚠️ API returned non-200 status:', responseData);
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching automation email templates:', error);
      setEmailTemplates([]);
    }
  };

  // Fetch email logs/communication history
  const fetchEmailLogs = async () => {
    try {
      setLoadingEmails(true);
      console.log('📧 Fetching email logs for lead:', lead_id);

      // Fetch actual sent email logs
      const emailLogsResponse = await postRequest('private/getEmailLogs', {
        lead_id: lead_id,
        page: 1,
        limit: 100
      }, authCtx.token);

      console.log('📧 Email logs response:', emailLogsResponse);

      const sentEmails = (emailLogsResponse?.status === 200 || emailLogsResponse?.status === 'success')
        ? (emailLogsResponse.data || [])
        : [];

      // Fetch "Immediately" automations for this lead's product
      let immediateAutomations = [];
      if (leadData?.product_id) {
        try {
          console.log('🔄 Fetching Immediately automations for product:', leadData.product_id);

          const automationsResponse = await postRequest('private/getImmediateAutomations', {
            product_id: leadData.product_id,
            trigger: 'Quotes/Generate New Quote'
          }, authCtx.token);

          console.log('🔄 Automations response:', automationsResponse);

          if (automationsResponse?.status === 200 || automationsResponse?.status === 'success') {
            // Transform automations to match email log format for display
            immediateAutomations = (automationsResponse.data || []).map(automation => ({
              _id: `automation-${automation._id}`,
              automation_id: automation._id,
              automation_name: automation.name,
              template_name: automation.emailTemplate?.name || automation.emailTemplate?.title || 'Email Template',
              subject: automation.emailTemplate?.subject || '',
              content: automation.emailTemplate?.content || '',
              recipient_name: 'Configured Automation',
              recipient_email: 'Triggers: Immediately',
              sent_at: null, // Not sent yet, this is just showing configured automations
              status: 'configured',
              isAutomation: true // Flag to distinguish from actual sent emails
            }));
            console.log('✅ Immediate automations loaded:', immediateAutomations.length);
          }
        } catch (autoError) {
          console.error('Error fetching automations:', autoError);
        }
      }

      // Show only sent emails in communication tab
      // Don't show "Not triggered" configured automations
      const allLogs = [...sentEmails];
      setEmailLogs(allLogs);
      console.log('✅ Email logs loaded - Sent:', sentEmails.length);

      setLoadingEmails(false);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
      setLoadingEmails(false);
    }
  };

  // Generate dynamic fee table for email templates
  const generatePurchaseQuoteDetailTable = (leadDataParam, legalFeeDataParam) => {
    const useLegalFeeData = legalFeeDataParam || leadDataParam;
    const branchName = leadDataParam?.branch_id?.branchname || leadDataParam?.branch_id?.name || 'My Law Matters';
    const ownershipType = leadDataParam?.ownership_type || 'Freehold';
    const serviceType = leadDataParam?.service_type || 'Purchase';

    // Format currency helper
    const formatCurrency = (amount) => {
      const value = Number(amount) || 0;
      return `£${value.toFixed(2)}`;
    };

    // Calculate Stamp Duty (same logic as backend)
    const calculateStampDuty = (price) => {
      if (!price || price <= 0) return 0;
      let stampDuty = 0;
      if (price > 1500000) {
        stampDuty += (price - 1500000) * 0.12;
        stampDuty += (1500000 - 925000) * 0.10;
        stampDuty += (925000 - 250000) * 0.05;
        stampDuty += (250000 - 125000) * 0.02;
      } else if (price > 925000) {
        stampDuty += (price - 925000) * 0.10;
        stampDuty += (925000 - 250000) * 0.05;
        stampDuty += (250000 - 125000) * 0.02;
      } else if (price > 250000) {
        stampDuty += (price - 250000) * 0.05;
        stampDuty += (250000 - 125000) * 0.02;
      } else if (price > 125000) {
        stampDuty += (price - 125000) * 0.02;
      }
      return Math.round(stampDuty * 100) / 100;
    };

    // Legal Fees
    const legalFeeNet = Number(useLegalFeeData.legal_fee || 0);
    const legalFeeVAT = legalFeeNet * 0.20;
    const legalFeeGross = legalFeeNet + legalFeeVAT;

    // Leasehold Fee
    const leaseholdFeeNet = ownershipType.toLowerCase() === 'leasehold' ? Number(useLegalFeeData.leasehold_fee || 0) : 0;
    const leaseholdFeeVAT = leaseholdFeeNet * 0.20;
    const leaseholdFeeGross = leaseholdFeeNet + leaseholdFeeVAT;

    // TT Fee
    const ttFeeNet = Number(useLegalFeeData.tt_fee || 30);
    const ttFeeVAT = ttFeeNet * 0.20;
    const ttFeeGross = ttFeeNet + ttFeeVAT;

    // Get purchaser count for identity check
    const purchaserCount = leadDataParam?.customer_ids?.length || leadDataParam?.customers?.length || leadDataParam?.purchasers?.length || 1;

    // Identity Check (multiplied by purchaser count)
    const identityCheckBase = Number(useLegalFeeData.identity_check || 10);
    const identityCheckNet = identityCheckBase * purchaserCount;
    const identityCheckVAT = 0;
    const identityCheckGross = identityCheckNet;

    // Additional Fixed Fees
    const mortgageAdminFeeNet = Number(useLegalFeeData.mortgage_admin_fee || 0);
    const mortgageAdminFeeVAT = mortgageAdminFeeNet * 0.20;
    const mortgageAdminFeeGross = mortgageAdminFeeNet + mortgageAdminFeeVAT;

    const helpToBuyISAFeeNet = Number(useLegalFeeData.help_to_buy_isa_fee || 0);
    const helpToBuyISAFeeVAT = helpToBuyISAFeeNet * 0.20;
    const helpToBuyISAFeeGross = helpToBuyISAFeeNet + helpToBuyISAFeeVAT;

    const helpToBuyFeeNet = Number(useLegalFeeData.help_to_buy_fee || 0);
    const helpToBuyFeeVAT = helpToBuyFeeNet * 0.20;
    const helpToBuyFeeGross = helpToBuyFeeNet + helpToBuyFeeVAT;

    const rightToBuyFeeNet = Number(useLegalFeeData.right_to_buy_fee || 0);
    const rightToBuyFeeVAT = rightToBuyFeeNet * 0.20;
    const rightToBuyFeeGross = rightToBuyFeeNet + rightToBuyFeeVAT;

    const islamicMortgageFeeNet = Number(useLegalFeeData.islamic_mortgage_fee || 0);
    const islamicMortgageFeeVAT = islamicMortgageFeeNet * 0.20;
    const islamicMortgageFeeGross = islamicMortgageFeeNet + islamicMortgageFeeVAT;

    const buyToLetFeeNet = Number(useLegalFeeData.buy_to_let_fee || 0);
    const buyToLetFeeVAT = buyToLetFeeNet * 0.20;
    const buyToLetFeeGross = buyToLetFeeNet + buyToLetFeeVAT;

    const sharedOwnershipFeeNet = Number(useLegalFeeData.shared_ownership_fee || 0);
    const sharedOwnershipFeeVAT = sharedOwnershipFeeNet * 0.20;
    const sharedOwnershipFeeGross = sharedOwnershipFeeNet + sharedOwnershipFeeVAT;

    const newBuildFeeNet = Number(useLegalFeeData.new_build_fee || 0);
    const newBuildFeeVAT = newBuildFeeNet * 0.20;
    const newBuildFeeGross = newBuildFeeNet + newBuildFeeVAT;

    const auctionRepossessionFeeNet = Number(useLegalFeeData.auction_repossession_fee || 0);
    const auctionRepossessionFeeVAT = auctionRepossessionFeeNet * 0.20;
    const auctionRepossessionFeeGross = auctionRepossessionFeeNet + auctionRepossessionFeeVAT;

    // Calculate gifted deposit fee
    const giftedDepositCount = Number(leadDataParam?.gifted_deposit || 0);
    let giftedDepositFeeNet = 0;
    if (giftedDepositCount > 0) {
      const firstGiftorFee = Number(useLegalFeeData.gifted_deposit_fee_first_giftor || 0);
      giftedDepositFeeNet = firstGiftorFee;
      if (giftedDepositCount > 1) {
        const additionalGiftorFee = Number(useLegalFeeData.gifted_deposit_fee_additional_giftors || 0);
        giftedDepositFeeNet += additionalGiftorFee * (giftedDepositCount - 1);
      }
    }
    const giftedDepositFeeVAT = giftedDepositFeeNet * 0.20;
    const giftedDepositFeeGross = giftedDepositFeeNet + giftedDepositFeeVAT;

    // Disbursements (for Purchase)
    let stampDutyNet = 0, stampDutyVAT = 0, stampDutyGross = 0;
    let hmLandRegistryFeeNet = 0, hmLandRegistryFeeVAT = 0, hmLandRegistryFeeGross = 0;
    let landRegistrySearchNet = 0, landRegistrySearchVAT = 0, landRegistrySearchGross = 0;
    let landChargesSearchNet = 0, landChargesSearchVAT = 0, landChargesSearchGross = 0;
    let searchPackNet = 0, searchPackVAT = 0, searchPackGross = 0;
    let officialCopiesNet = 0, officialCopiesVAT = 0, officialCopiesGross = 0;

    if (serviceType.toLowerCase() === 'purchase' || serviceType.toLowerCase() === 'sale_purchase') {
      stampDutyNet = Number(useLegalFeeData.stamp_duty || calculateStampDuty(Number(leadDataParam.property_value || 0)));
      stampDutyVAT = 0;
      stampDutyGross = stampDutyNet;

      hmLandRegistryFeeNet = Number(useLegalFeeData.hm_land_registry_fee || 0);
      hmLandRegistryFeeVAT = 0;
      hmLandRegistryFeeGross = hmLandRegistryFeeNet;

      landRegistrySearchNet = Number(useLegalFeeData.land_registry_search || 0);
      landRegistrySearchVAT = 0;
      landRegistrySearchGross = landRegistrySearchNet;

      landChargesSearchNet = Number(useLegalFeeData.land_charges_search || 0);
      landChargesSearchVAT = 0;
      landChargesSearchGross = landChargesSearchNet;

      searchPackNet = Number(useLegalFeeData.search_pack || 0);
      searchPackVAT = 0;
      searchPackGross = searchPackNet;
    } else if (serviceType.toLowerCase() === 'sale') {
      // For Sale, show official copies
      officialCopiesNet = Number(useLegalFeeData.official_copies || 14);
      officialCopiesVAT = 0;
      officialCopiesGross = officialCopiesNet;
    }

    // Calculate Legal Cost totals (same as backend)
    const legalCostNet = legalFeeNet + leaseholdFeeNet + ttFeeNet + identityCheckNet + mortgageAdminFeeNet +
      helpToBuyISAFeeNet + helpToBuyFeeNet + rightToBuyFeeNet + islamicMortgageFeeNet + giftedDepositFeeNet +
      buyToLetFeeNet + sharedOwnershipFeeNet + newBuildFeeNet + auctionRepossessionFeeNet;
    const legalCostVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + identityCheckVAT + mortgageAdminFeeVAT +
      helpToBuyISAFeeVAT + helpToBuyFeeVAT + rightToBuyFeeVAT + islamicMortgageFeeVAT + giftedDepositFeeVAT +
      buyToLetFeeVAT + sharedOwnershipFeeVAT + newBuildFeeVAT + auctionRepossessionFeeVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    // Calculate Distribution Cost totals (disbursements)
    const distributionCostNet = stampDutyNet + hmLandRegistryFeeNet + landRegistrySearchNet + landChargesSearchNet + searchPackNet + officialCopiesNet;
    const distributionCostVAT = stampDutyVAT + hmLandRegistryFeeVAT + landRegistrySearchVAT + landChargesSearchVAT + searchPackVAT + officialCopiesVAT;
    const distributionCostGross = distributionCostNet + distributionCostVAT;

    // Calculate Grand totals
    const totalNet = legalCostNet + distributionCostNet;
    const totalVAT = legalCostVAT + distributionCostVAT;
    const totalGross = totalNet + totalVAT;

    // Build table rows
    let tableRows = '';

    // Legal Cost Section Header
    tableRows += `
      <tr style="background-color: #f8f9fa;">
        <td colspan="4" style="border: 1px solid #dee2e6; padding: 10px; font-weight: 600;">Legal Cost</td>
      </tr>
    `;

    // Legal Fees (always show)
    tableRows += `
      <tr>
        <td style="border: 1px solid #dee2e6; padding: 12px;">Legal Fees</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeNet)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeVAT)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalFeeGross)}</td>
      </tr>
    `;

    // Leasehold Fee (only if applicable)
    if (leaseholdFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Leasehold Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(leaseholdFeeGross)}</td>
        </tr>
      `;
    }

    // TT Fee
    tableRows += `
      <tr>
        <td style="border: 1px solid #dee2e6; padding: 12px;">TT Fee</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeNet)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeVAT)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(ttFeeGross)}</td>
      </tr>
    `;

    // Identity Check
    tableRows += `
      <tr>
        <td style="border: 1px solid #dee2e6; padding: 12px;">Identity Check</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckNet)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckVAT)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(identityCheckGross)}</td>
      </tr>
    `;

    // Conditional fees - only show if > 0
    if (mortgageAdminFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Mortgage Admin Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(mortgageAdminFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(mortgageAdminFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(mortgageAdminFeeGross)}</td>
        </tr>
      `;
    }

    if (helpToBuyISAFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Help to Buy ISA Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyISAFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyISAFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyISAFeeGross)}</td>
        </tr>
      `;
    }

    if (helpToBuyFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Help to Buy Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(helpToBuyFeeGross)}</td>
        </tr>
      `;
    }

    if (rightToBuyFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Right to Buy Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(rightToBuyFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(rightToBuyFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(rightToBuyFeeGross)}</td>
        </tr>
      `;
    }

    if (islamicMortgageFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Islamic Mortgage Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(islamicMortgageFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(islamicMortgageFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(islamicMortgageFeeGross)}</td>
        </tr>
      `;
    }

    if (giftedDepositFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Gifted Deposit Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(giftedDepositFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(giftedDepositFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(giftedDepositFeeGross)}</td>
        </tr>
      `;
    }

    if (buyToLetFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Buy to Let Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(buyToLetFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(buyToLetFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(buyToLetFeeGross)}</td>
        </tr>
      `;
    }

    if (sharedOwnershipFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Shared Ownership Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(sharedOwnershipFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(sharedOwnershipFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(sharedOwnershipFeeGross)}</td>
        </tr>
      `;
    }

    if (newBuildFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">New Build Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(newBuildFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(newBuildFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(newBuildFeeGross)}</td>
        </tr>
      `;
    }

    if (auctionRepossessionFeeNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Auction / Repossession Fee</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(auctionRepossessionFeeNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(auctionRepossessionFeeVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(auctionRepossessionFeeGross)}</td>
        </tr>
      `;
    }

    // TOTAL LEGAL COST row (matching backend exactly)
    tableRows += `
      <tr style="background-color: #e3f2fd; font-weight: 700;">
        <td style="border: 1px solid #dee2e6; padding: 12px;">TOTAL LEGAL COST</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostNet)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostVAT)}</td>
        <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(legalCostGross)}</td>
      </tr>
    `;

    // Disbursements section
    if (serviceType.toLowerCase() === 'purchase' || serviceType.toLowerCase() === 'sale_purchase') {
      // Disbursement Section Header
      tableRows += `
        <tr style="background-color: #f8f9fa;">
          <td colspan="4" style="border: 1px solid #dee2e6; padding: 10px; font-weight: 600;">Disbursement - Third Party Expenditure</td>
        </tr>
      `;

      if (stampDutyNet > 0) {
        tableRows += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Stamp Duty Land Tax</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(stampDutyNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(stampDutyVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(stampDutyGross)}</td>
          </tr>
        `;
      }

      if (hmLandRegistryFeeNet > 0) {
        tableRows += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">HM Land Registry Fee</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(hmLandRegistryFeeNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(hmLandRegistryFeeVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(hmLandRegistryFeeGross)}</td>
          </tr>
        `;
      }

      if (landRegistrySearchNet > 0) {
        tableRows += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Land Registry Search</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landRegistrySearchNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landRegistrySearchVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landRegistrySearchGross)}</td>
          </tr>
        `;
      }

      if (landChargesSearchNet > 0) {
        tableRows += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Land Charges Search</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landChargesSearchNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landChargesSearchVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(landChargesSearchGross)}</td>
          </tr>
        `;
      }

      if (searchPackNet > 0) {
        tableRows += `
          <tr>
            <td style="border: 1px solid #dee2e6; padding: 12px;">Search Pack</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(searchPackNet)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(searchPackVAT)}</td>
            <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(searchPackGross)}</td>
          </tr>
        `;
      }

      // TOTAL DISBURSEMENT COST row (matching screenshot 2 - VAT shows "-")
      tableRows += `
        <tr style="background-color: #e3f2fd; font-weight: 700;">
          <td style="border: 1px solid #dee2e6; padding: 12px;">TOTAL DISBURSEMENT COST</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(distributionCostNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">-</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(distributionCostGross)}</td>
        </tr>
      `;
    } else if (serviceType.toLowerCase() === 'sale' && officialCopiesNet > 0) {
      tableRows += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 12px;">Official Copies</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesNet)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesVAT)}</td>
          <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right;">${formatCurrency(officialCopiesGross)}</td>
        </tr>
      `;
    }

    // Return complete HTML table
    return `
      <div style="margin: 20px 0; font-family: Arial, sans-serif;">
        <h2 style="color: #2c3e50; font-size: 20px; font-weight: 600; margin-bottom: 10px;">📋 Your Conveyancing Quotation Summary</h2>
        <p style="color: #555; font-size: 14px; margin-bottom: 20px;">
          We aim to complete your transaction within the fixed fees set out below.
        </p>
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0; background-color:white;;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #dee2e6; padding: 12px; text-align: left; font-weight: 600;"></th>
              <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right; font-weight: 600;">Net</th>
              <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right; font-weight: 600;">VAT</th>
              <th style="border: 1px solid #dee2e6; padding: 12px; text-align: right; font-weight: 600;">Gross</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
            <tr style="background-color: #d32f2f; color:white;; font-weight: 700;">
              <td style="border: 1px solid #dee2e6; padding: 12px; color:white;;">TOTAL (Legal Cost + Disbursement Cost)</td>
              <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right; color:white;;">${formatCurrency(totalNet)}</td>
              <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right; color:white;;">${formatCurrency(totalVAT)}</td>
              <td style="border: 1px solid #dee2e6; padding: 12px; text-align: right; color:white;;">${formatCurrency(totalGross)}</td>
            </tr>
          </tbody>
        </table>

        <p style="font-size: 14px; color: #666; margin-top: 20px; line-height: 1.6;">
          We endeavour to complete the transaction within the cost summary based on information provided by you.
          In certain cases, additional work needs to be carried which may attract additional legal cost.
        </p>
      </div>
    `;
  };

  // Handle view email template
  const handleViewEmailTemplate = async (emailLog) => {
    // Replace placeholders with actual data
    let content = emailLog.content || '';

    console.log('📧 Original email content:', content.substring(0, 500));
    console.log('📧 leadData.lead_id:', leadData?.lead_id);
    console.log('📧 lead_id variable:', lead_id);

    // Get customer data (first purchaser or first customer)
    const customer = leadData?.purchasers?.[0] || leadData?.customers?.[0] || {};
    const fullName = `${customer.forename || ''} ${customer.surname || ''}`.trim() || 'Customer';

    // Format current date
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Get property address
    let propertyAddress = '';
    if (leadData?.address) {
      if (typeof leadData.address === 'string') {
        propertyAddress = leadData.address;
      } else if (typeof leadData.address === 'object') {
        const addressParts = [
          leadData.address.line1,
          leadData.address.line2,
          leadData.address.city || leadData.address.town,
          leadData.address.county || leadData.address.country,
          leadData.address.postcode
        ].filter(part => part && part.trim() !== '');
        propertyAddress = addressParts.join(', ');
      }
    } else if (leadData?.property_address) {
      if (typeof leadData.property_address === 'string') {
        propertyAddress = leadData.property_address;
      } else if (typeof leadData.property_address === 'object') {
        const addressParts = [
          leadData.property_address.address1 || leadData.property_address.line1,
          leadData.property_address.address2 || leadData.property_address.line2,
          leadData.property_address.city || leadData.property_address.town,
          leadData.property_address.county || leadData.property_address.country,
          leadData.property_address.postcode
        ].filter(part => part && part.trim() !== '');
        propertyAddress = addressParts.join(', ');
      }
    }

    // Format property price
    const propertyPrice = leadData?.property_value
      ? `£${Number(leadData.property_value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '';

    // Replace customer placeholders (both single and double brace)
    content = content.replace(/\{\{customer_title\}\}/g, customer.title || '');
    content = content.replace(/\{customer_title\}/g, customer.title || '');
    content = content.replace(/\{\{customer_forename\}\}/g, customer.forename || '');
    content = content.replace(/\{customer_forename\}/g, customer.forename || '');
    content = content.replace(/\{\{customer_surname\}\}/g, customer.surname || '');
    content = content.replace(/\{customer_surname\}/g, customer.surname || '');
    content = content.replace(/\{\{customer_email\}\}/g, customer.email || '');
    content = content.replace(/\{customer_email\}/g, customer.email || '');
    content = content.replace(/\{\{customer_phone\}\}/g, customer.phone_number || '');
    content = content.replace(/\{customer_phone\}/g, customer.phone_number || '');
    content = content.replace(/\{\{customer_name\}\}/g, fullName);
    content = content.replace(/\{customer_name\}/g, fullName);

    // Replace client placeholders (both single and double brace)
    content = content.replace(/\{\{client_name\}\}/g, fullName);
    content = content.replace(/\{client_name\}/g, fullName);
    content = content.replace(/\{\{client\}\}/g, fullName);
    content = content.replace(/\{client\}/g, fullName);
    content = content.replace(/\{\{ClientName\}\}/g, fullName);
    content = content.replace(/\{ClientName\}/g, fullName);

    // Replace CustomerTitle, CustomerForename, CustomerSurname
    content = content.replace(/\{\{CustomerTitle\}\}/g, customer.title || '');
    content = content.replace(/\{CustomerTitle\}/g, customer.title || '');
    content = content.replace(/\{\{CustomerForename\}\}/g, customer.forename || '');
    content = content.replace(/\{CustomerForename\}/g, customer.forename || '');
    content = content.replace(/\{\{CustomerSurname\}\}/g, customer.surname || '');
    content = content.replace(/\{CustomerSurname\}/g, customer.surname || '');

    // Replace date placeholders
    content = content.replace(/\{\{date\}\}/g, currentDate);
    content = content.replace(/\{date\}/g, currentDate);

    // Replace reference/lead_id placeholders
    content = content.replace(/\{\{reference\}\}/g, leadData?.lead_id || '');
    content = content.replace(/\{reference\}/g, leadData?.lead_id || '');
    content = content.replace(/\{\{lead_id\}\}/g, leadData?.lead_id || '');
    content = content.replace(/\{lead_id\}/g, leadData?.lead_id || '');

    // Replace property placeholders
    content = content.replace(/\{\{property\}\}/g, propertyAddress);
    content = content.replace(/\{property\}/g, propertyAddress);
    content = content.replace(/\{\{property_address\}\}/g, propertyAddress);
    content = content.replace(/\{property_address\}/g, propertyAddress);
    content = content.replace(/\{\{price\}\}/g, propertyPrice);
    content = content.replace(/\{price\}/g, propertyPrice);
    content = content.replace(/\{\{property_value\}\}/g, propertyPrice);
    content = content.replace(/\{property_value\}/g, propertyPrice);

    // Replace tenure/ownership placeholders
    const tenure = leadData?.ownership_type || 'Freehold';
    content = content.replace(/\{\{tenure\}\}/g, tenure);
    content = content.replace(/\{tenure\}/g, tenure);
    content = content.replace(/\{\{ownership_type\}\}/g, tenure);
    content = content.replace(/\{ownership_type\}/g, tenure);

    // Replace ConveyancingType placeholder with formatted service type
    const conveyancingType = leadData?.service_type
      ? leadData.service_type
          .replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      : 'Purchase';
    content = content.replace(/\{\{ConveyancingType\}\}/g, conveyancingType);
    content = content.replace(/\{ConveyancingType\}/g, conveyancingType);
    content = content.replace(/\{\{conveyancing_type\}\}/g, conveyancingType);
    content = content.replace(/\{conveyancing_type\}/g, conveyancingType);
    content = content.replace(/\{\{service_type\}\}/g, conveyancingType);
    content = content.replace(/\{service_type\}/g, conveyancingType);

    // Generate actual autologin token for preview
    let tokenLoginUrl = `${CUSTOMER_PORTAL_URL}/login?token=PREVIEW_TOKEN`;

    if (customer.email) {
      try {
        const tokenResponse = await postRequest('../public/customer-portal/generate-preview-token', {
          customer_email: customer.email
        }, authCtx.token);

        if (tokenResponse?.status === 200 && tokenResponse?.data?.url) {
          tokenLoginUrl = tokenResponse.data.url;
          console.log('📧 Generated token URL:', tokenLoginUrl);
        }
      } catch (error) {
        console.error('Error generating preview token:', error);
      }
    }

    // Replace all URL placeholders with token-based login
    content = content.replace(/\{\{LeadInstructUrl\}\}/g, tokenLoginUrl);
    content = content.replace(/\{LeadInstructUrl\}/g, tokenLoginUrl);
    content = content.replace(/\{\{AutoLoginUrl\}\}/g, tokenLoginUrl);
    content = content.replace(/\{AutoLoginUrl\}/g, tokenLoginUrl);
    content = content.replace(/\{\{confirmation_link\}\}/g, tokenLoginUrl);
    content = content.replace(/\{confirmation_link\}/g, tokenLoginUrl);
    content = content.replace(/\{\{customer_portal_link\}\}/g, tokenLoginUrl);
    content = content.replace(/\{customer_portal_link\}/g, tokenLoginUrl);

    console.log('📧 After URL replacement:', content.substring(0, 500));

    // Generate dynamic fee table for {customer_fee} placeholder
    const feeTable = generatePurchaseQuoteDetailTable(leadData, legalFeeData);
    console.log('📊 Generated fee table for email template');

    // Replace {customer_fee} and {{customer_fee}} placeholders with the generated table
    content = content.replace(/\{\{customer_fee\}\}/g, feeTable);
    content = content.replace(/\{customer_fee\}/g, feeTable);
    content = content.replace(/\{\{fee_table\}\}/g, feeTable);
    content = content.replace(/\{fee_table\}/g, feeTable);

    // Replace branch placeholders
    const branchName = leadData?.branch_id?.branchname || leadData?.branch_id?.name || 'My Law Matters';
    content = content.replace(/\{\{branch_name\}\}/g, branchName);
    content = content.replace(/\{branch_name\}/g, branchName);
    content = content.replace(/\{\{branch_phone\}\}/g, leadData?.branch_id?.phone || '');
    content = content.replace(/\{branch_phone\}/g, leadData?.branch_id?.phone || '');
    content = content.replace(/\{\{branch_email\}\}/g, leadData?.branch_id?.email || '');
    content = content.replace(/\{branch_email\}/g, leadData?.branch_id?.email || '');

    // Convert markdown links [text](url) to HTML links
    content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="color: #667eea; text-decoration: underline;">$1</a>');

    setSelectedEmail({
      ...emailLog,
      content: content
    });
    setViewEmailModal(true);
  };

  // Download email template as HTML file
  const handleDownloadEmailTemplate = () => {
    if (!selectedEmail) return;

    // Use the exact HTML that was sent to the customer
    // This already includes the complete email with theme components (greeting, footer, etc.)
    const htmlContent = selectedEmail.content || 'No content available';

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename with lead ID and template name
    const leadId = leadData?.lead_id || lead_id || 'unknown';
    const templateName = (selectedEmail.template_name || selectedEmail.subject || 'email-template')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const timestamp = new Date().toISOString().split('T')[0];

    link.download = `${leadId}_${templateName}_${timestamp}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('📥 Email template downloaded successfully');
  };

  // Send email using template
  const handleSendEmail = async (templateId, templateName) => {
    try {
      // First show confirmation
      const confirmResult = await Swal.fire({
        icon: 'question',
        title: 'Send Email?',
        html: `<p>Are you sure you want to send <strong>"${templateName}"</strong> to the customer?</p>`,
        showCancelButton: true,
        confirmButtonText: 'Yes, Send Email',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      // If user cancels, return
      if (!confirmResult.isConfirmed) {
        return;
      }

      setSendingEmail(true);

      console.log('📧 Send Email clicked:', {
        templateId,
        templateName,
        lead_id: lead_id
      });

      // Backend will automatically fetch customer emails from lead data
      // So we just send the request
      const response = await postRequest('private/sendEmailFromTemplate', {
        lead_id: lead_id,
        template_id: templateId
      }, authCtx.token);

      console.log('Email send response:', response);

      if (response?.status === 200 || response?.status === 'success') {
        const successCount = response?.data?.successCount || 0;
        const recipients = successCount > 1 ? `${successCount} customers` : 'customer';

        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: `Email "${templateName}" has been sent successfully to ${recipients}!`
        });
        fetchEmailLogs();
      } else {
        // Handle non-success response
        Swal.fire({
          icon: 'error',
          title: 'Failed to Send Email',
          text: response?.message || response?.error || 'Failed to send email. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || error?.response?.data?.message || 'Failed to send email. Please try again.'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteLead = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this conveyancing lead #${lead_id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log('Attempting to delete lead with ID:', lead_id);
        
        // Use the DELETE method with fetch API
        const response = await fetch(`/api/v1/admin/private/delete-lead/${lead_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Delete response status:', response.status);
        
        let res;
        if (response.ok) {
          res = await response.json();
          console.log('Delete response data:', res);
        } else {
          // If the specific delete endpoint fails, log the error
          const errorText = await response.text();
          console.error('Delete failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Delete failed with status ${response.status}`);
        }
        
        if (res && (res.status === 200 || res.status === 'success')) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'The conveyancing lead has been deleted successfully.',
            timer: 1500,
            showConfirmButton: false
          });
          
          // Navigate back to leads list after a short delay
          setTimeout(() => {
            navigate('/leads');
          }, 1500);
        } else {
          console.error('Delete failed with response:', res);
          Swal.fire('Error!', res?.message || 'Failed to delete conveyancing lead.', 'error');
        }
      } catch (error) {
        console.error('Error deleting conveyancing lead:', error);
        console.error('Error details:', error.message, error.stack);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'An error occurred while deleting the conveyancing lead.'
        });
      }
    }
  };

  // Handle revise quote
  const handleReviseQuote = async (payload) => {
    try {
      console.log('🔄 ===== REVISE QUOTE (NEW API) =====');
      console.log('📦 Received payload from modal:', payload);

      // 🎯 Transform flat fees to NESTED format
      const nestedFees = {
        legalFee: { amount: Number(payload.fees.legal_fee || 0), vat: Number(payload.fees.legal_fee || 0) * 0.20, gross: Number(payload.fees.legal_fee || 0) * 1.20, label: 'Legal Fee' },
        ttFee: { amount: Number(payload.fees.tt_fee || 0), vat: Number(payload.fees.tt_fee || 0) * 0.20, gross: Number(payload.fees.tt_fee || 0) * 1.20, label: 'TT Fee' },
        identityCheck: { amount: Number(payload.fees.identity_check || 0), vat: 0, gross: Number(payload.fees.identity_check || 0), label: 'Identity Check' },
        mortgageAdminFee: { amount: Number(payload.fees.mortgage_admin_fee || 0), vat: Number(payload.fees.mortgage_admin_fee || 0) * 0.20, gross: Number(payload.fees.mortgage_admin_fee || 0) * 1.20, label: 'Mortgage Admin Fee' },
        leaseholdFee: { amount: Number(payload.fees.leasehold_fee || 0), vat: Number(payload.fees.leasehold_fee || 0) * 0.20, gross: Number(payload.fees.leasehold_fee || 0) * 1.20, label: 'Leasehold Fee' },
        helpToBuyISAFee: { amount: Number(payload.fees.help_to_buy_isa_fee || 0), vat: Number(payload.fees.help_to_buy_isa_fee || 0) * 0.20, gross: Number(payload.fees.help_to_buy_isa_fee || 0) * 1.20, label: 'Help to Buy ISA Fee' },
        helpToBuyFee: { amount: Number(payload.fees.help_to_buy_fee || 0), vat: Number(payload.fees.help_to_buy_fee || 0) * 0.20, gross: Number(payload.fees.help_to_buy_fee || 0) * 1.20, label: 'Help to Buy Fee' },
        rightToBuyFee: { amount: Number(payload.fees.right_to_buy_fee || 0), vat: Number(payload.fees.right_to_buy_fee || 0) * 0.20, gross: Number(payload.fees.right_to_buy_fee || 0) * 1.20, label: 'Right to Buy Fee' },
        sharedOwnershipFee: { amount: Number(payload.fees.shared_ownership_fee || 0), vat: Number(payload.fees.shared_ownership_fee || 0) * 0.20, gross: Number(payload.fees.shared_ownership_fee || 0) * 1.20, label: 'Shared Ownership Fee' },
        newBuildFee: { amount: Number(payload.fees.new_build_fee || 0), vat: Number(payload.fees.new_build_fee || 0) * 0.20, gross: Number(payload.fees.new_build_fee || 0) * 1.20, label: 'New Build Fee' },
        islamicMortgageFee: { amount: Number(payload.fees.islamic_mortgage_fee || 0), vat: Number(payload.fees.islamic_mortgage_fee || 0) * 0.20, gross: Number(payload.fees.islamic_mortgage_fee || 0) * 1.20, label: 'Islamic Mortgage Fee' },
        auctionRepossessionFee: { amount: Number(payload.fees.auction_repossession_fee || 0), vat: Number(payload.fees.auction_repossession_fee || 0) * 0.20, gross: Number(payload.fees.auction_repossession_fee || 0) * 1.20, label: 'Auction/Repossession Fee' },
        giftedDepositFeeFirstGiftor: { amount: Number(payload.fees.gifted_deposit_fee || 0), vat: Number(payload.fees.gifted_deposit_fee || 0) * 0.20, gross: Number(payload.fees.gifted_deposit_fee || 0) * 1.20, label: 'Gifted Deposit Fee (First)' },
        buyToLetFee: { amount: Number(payload.fees.buy_to_let_fee || 0), vat: Number(payload.fees.buy_to_let_fee || 0) * 0.20, gross: Number(payload.fees.buy_to_let_fee || 0) * 1.20, label: 'Buy to Let Fee' },
        arrangingIndemnityPolicy: { amount: Number(payload.fees.arranging_indemnity_policy || 0), vat: Number(payload.fees.arranging_indemnity_policy || 0) * 0.20, gross: Number(payload.fees.arranging_indemnity_policy || 0) * 1.20, label: 'Arranging Indemnity Policy' }
      };

      const nestedDisbursements = {
        stampDuty: { amount: Number(payload.fees.stamp_duty || 0), vat: 0, gross: Number(payload.fees.stamp_duty || 0), label: 'Stamp Duty' },
        hmLandRegistryFee: { amount: Number(payload.fees.hm_land_registry_fee || 0), vat: 0, gross: Number(payload.fees.hm_land_registry_fee || 0), label: 'HM Land Registry Fee' },
        landRegistrySearch: { amount: Number(payload.fees.land_registry_search || 0), vat: Number(payload.fees.land_registry_search || 0) * 0.20, gross: Number(payload.fees.land_registry_search || 0) * 1.20, label: 'Land Registry Search' },
        landChargesSearch: { amount: Number(payload.fees.land_charges_search || 0), vat: Number(payload.fees.land_charges_search || 0) * 0.20, gross: Number(payload.fees.land_charges_search || 0) * 1.20, label: 'Land Charges Search' },
        searchPack: { amount: Number(payload.fees.search_pack || 0), vat: 0, gross: Number(payload.fees.search_pack || 0), label: 'Search Pack' }
      };

      // Calculate Legal Cost Total
      const legalCostNet = Object.values(nestedFees).reduce((sum, fee) => sum + fee.amount, 0);
      const legalCostVat = Object.values(nestedFees).reduce((sum, fee) => sum + fee.vat, 0);
      const legalCostGross = Object.values(nestedFees).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Disbursement Cost Total
      const disbursementCostNet = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.amount, 0);
      const disbursementCostVat = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.vat, 0);
      const disbursementCostGross = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Grand Total
      const totalNet = legalCostNet + disbursementCostNet;
      const totalVat = legalCostVat + disbursementCostVat;
      const totalGross = legalCostGross + disbursementCostGross;

      const nestedTotals = {
        legalCost: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross },
        disbursementCost: { net: disbursementCostNet, vat: disbursementCostVat, gross: disbursementCostGross },
        total: { net: totalNet, vat: totalVat, gross: totalGross }
      };

      console.log('✅ Nested format created:', { nestedFees, nestedDisbursements, nestedTotals });

      // 🔄 Call NEW reviseQuote API
      const apiUrl = `${API_BASE_URL}private/reviseQuote/${lead_id}`;
      console.log('📡 Calling NEW reviseQuote API:', apiUrl);

      // 🎯 Check if Purchase_Sale - if so, structure as purchase_breakdown and sale_breakdown
      const requestBody = {};

      if (leadData?.service_type === 'Purchase_Sale' && legalFeeData?.purchase_breakdown && legalFeeData?.sale_breakdown) {
        console.log('📊 Purchase_Sale detected - sending with purchase_breakdown and sale_breakdown');

        // For Purchase_Sale, send the existing breakdowns (they're already in correct format)
        requestBody.purchase_breakdown = legalFeeData.purchase_breakdown;
        requestBody.sale_breakdown = legalFeeData.sale_breakdown;

        // Also send combined fees/disbursements/totals for compatibility
        requestBody.fees = nestedFees;
        requestBody.disbursements = nestedDisbursements;
        requestBody.totals = nestedTotals;
      } else {
        // Single service type
        requestBody.fees = nestedFees;
        requestBody.disbursements = nestedDisbursements;
        requestBody.totals = nestedTotals;
      }

      console.log('📦 Request body:', requestBody);

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Revise Quote API Response:', result);

      if (result?.status === 200) {
        console.log('✅ Quote revised successfully!');

        // Close modal
        setReviseModalShow(false);

        // Simple approach: Page reload (reliable & works for all service types)
        Swal.fire({
          title: 'Success',
          text: 'Quote revised successfully!',
          icon: 'success',
          timer: 1000,
          showConfirmButton: false
        });

        // Reload after 1 second
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        Swal.fire('Error', result?.message || 'Failed to revise quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error revising quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // 🔁 Handle Regenerate Quote - Restore Original Quote
  const handleRegenerateQuote = async () => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Regenerate Original Quote?',
        text: 'This will restore the original quote values. Current changes will be saved to history.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate it!'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      console.log('🔁 ===== REGENERATE QUOTE =====');
      console.log('📋 Lead ID:', lead_id);

      // Call regenerateQuote API
      const apiUrl = `${API_BASE_URL}private/regenerateQuote/${lead_id}`;
      console.log('📡 Calling regenerateQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Regenerate Quote API Response:', result);

      if (result.status === 200) {
        // Show success and reload
        Swal.fire({
          title: 'Success',
          text: 'Original quote restored successfully!',
          icon: 'success',
          timer: 1000,
          showConfirmButton: false
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        Swal.fire('Error', result?.message || 'Failed to regenerate quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error regenerating quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // 📚 Fetch Quote History from Database
  const fetchQuoteHistory = async () => {
    try {
      console.log('📚 Fetching quote history from database...');

      const response = await fetch(`${API_BASE_URL}private/getQuoteHistory/${lead_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      const result = await response.json();

      if (result.status === 200 && result.data) {
        console.log('✅ Quote history fetched:', result.data);
        setQuoteHistory(result.data);
      } else {
        console.warn('⚠️ No quote history found');
        setQuoteHistory([]);
      }
    } catch (error) {
      console.error('❌ Error fetching quote history:', error);
      setQuoteHistory([]);
    }
  };

  const slugify = (text) =>
    text
      ?.toLowerCase()
      ?.replace(/ /g, '-')
      ?.replace(/[^\w-]+/g, '')
      ?.replace(/-+/g, '-');

  React.useEffect(() => {
    async function fetchConveyancingLead() {
      console.log('🔍 === FETCHING CONVEYANCING LEAD DATA ===');
      console.log('Lead ID from params:', lead_id);
      console.log('Current URL:', window.location.href);
      console.log('Auth token present:', !!authCtx.token);
      console.log('Auth token length:', authCtx.token?.length);
      
      try {
        // Try conveyancing-specific API first
        console.log('🔄 Trying getConveyancingLead API...');
  const apiUrl = `${API_BASE_URL}private/getConveyancingLead/${lead_id}`;
        console.log('🌐 Full API URL:', apiUrl);

        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', res.status);
        console.log('📡 Response statusText:', res.statusText);
        console.log('📡 Response ok:', res.ok);
        console.log('📡 Response headers:', [...res.headers.entries()]);

        const data = await res.json();
        console.log('📡 Raw Conveyancing API Response:', data);
        console.log('📡 Response data type:', typeof data);
        console.log('📡 Response keys:', Object.keys(data));
        
        if (data.status === 200 && data.data) {
          console.log('✅ Conveyancing data received successfully:', data.data);
          console.log('📋 Data._id:', data.data._id);
          console.log('📋 Data.lead_id:', data.data.lead_id);
          console.log('📋 Available fields:', Object.keys(data.data));
          console.log('💰 Property value:', data.data.property_value);
          console.log('🏠 Property address:', data.data.property_address);
          console.log('📋 Service type:', data.data.service_type);
          console.log('👥 Customers:', data.data.customer_ids);

          // Infer service_type from product_title if not set
          let leadDataWithServiceType = { ...data.data };
          if (!leadDataWithServiceType.service_type && leadDataWithServiceType.product_title) {
            const productTitle = leadDataWithServiceType.product_title.toLowerCase();
            console.log('🔍 Product title for inference:', productTitle);

            if (productTitle.includes('review of legal pack') || productTitle.includes('review legal pack')) {
              leadDataWithServiceType.service_type = 'Review_Legal_Pack';
              console.log('✅ Inferred service_type: Review_Legal_Pack');
            } else if (productTitle.includes('purchase') && productTitle.includes('sale')) {
              leadDataWithServiceType.service_type = 'Purchase_Sale';
              console.log('✅ Inferred service_type: Purchase_Sale');
            } else if (productTitle.includes('sale')) {
              leadDataWithServiceType.service_type = 'Sale';
              console.log('✅ Inferred service_type: Sale');
            } else if (productTitle.includes('purchase')) {
              leadDataWithServiceType.service_type = 'Purchase';
              console.log('✅ Inferred service_type: Purchase');
            }
          }

          setLeadData(leadDataWithServiceType);
          setContactAttempts(leadDataWithServiceType?.contact_attempts || 0);
          
          // Also fetch conveyancing-specific details
          try {
            console.log('🔄 Fetching conveyancing-specific details...');
            
            // Try multiple endpoints for conveyancing details
            const conveyancingEndpoints = [
              { url: `private/getConveyancingDetails/${lead_id}`, name: 'getConveyancingDetails' },
              { url: `private/getConveyancingPurchase/${lead_id}`, name: 'getConveyancingPurchase' },
              { url: `private/conveyancing-details/${lead_id}`, name: 'conveyancing-details' }
            ];
            
            let conveyancingData = null;
            
            for (const endpoint of conveyancingEndpoints) {
              try {
                console.log(`🔄 Trying ${endpoint.name}...`);
                const convRes = await postRequest(endpoint.url, {}, authCtx.token);
                console.log(`📋 ${endpoint.name} response:`, convRes);
                
                if (convRes?.status === 200 && convRes?.data) {
                  console.log(`✅ Conveyancing details found via ${endpoint.name}:`, convRes.data);
                  conveyancingData = convRes.data;
                  break;
                }
              } catch (err) {
                console.log(`❌ ${endpoint.name} failed:`, err.message);
              }
            }
            
            if (conveyancingData) {
              // Merge conveyancing details directly with lead data
              console.log('🔄 Merging conveyancing data with lead data...');
              console.log('📊 Conveyancing vendors:', conveyancingData.vendors);
              console.log('📊 Conveyancing purchasers:', conveyancingData.purchasers);
              console.log('📊 Conveyancing salePropertyDetails:', conveyancingData.salePropertyDetails);
              console.log('📊 Conveyancing purchasePropertyDetails:', conveyancingData.purchasePropertyDetails);

              setLeadData(prev => {
                // Preserve populated fields from prev that might be overwritten by unpopulated ones from conveyancingData
                const preservedLenderPanel = prev?.lender_panel_id && typeof prev.lender_panel_id === 'object' && prev.lender_panel_id.name
                  ? prev.lender_panel_id // Keep populated version
                  : conveyancingData.lender_panel_id; // Use new one if prev wasn't populated

                return {
                  ...prev,
                  ...conveyancingData, // Merge all conveyancing fields directly
                  lender_panel_id: preservedLenderPanel, // Preserve populated lender_panel_id
                  conveyancingDetails: conveyancingData // Also keep original for reference
                };
              });

              console.log('✅ Conveyancing data merged successfully');
            } else {
              console.log('ℹ️ No conveyancing details found from any endpoint');
            }
          } catch (convErr) {
            console.log('❌ Error fetching conveyancing details:', convErr);
          }
        } else {
          console.log('❌ No conveyancing data found for ID:', lead_id);
          console.log('📊 Full API response:', JSON.stringify(data, null, 2));
          
          // Debug: Check if there are any conveyancing leads at all
          console.log('🔍 Checking all conveyancing leads in database...');
          console.log('dkednks')
          try {
            const allLeadsRes = await fetch(`${API_BASE_URL}private/getAllConveyancingLeads?page=1&limit=50`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authCtx.token}`,
                'Content-Type': 'application/json'
              }
            });
            const allLeadsData = await allLeadsRes.json();
            console.log('📊 All conveyancing leads in database:', allLeadsData);
            console.log('📊 Total leads found:', allLeadsData.data?.length || 0);
            if (allLeadsData.data?.length > 0) {
              console.log('📊 First lead example:', allLeadsData.data[0]);
              console.log('📊 Lead IDs in database:', allLeadsData.data.map(lead => ({ _id: lead._id, lead_id: lead.lead_id })));
            }
          } catch (debugErr) {
            console.log('❌ Debug query failed:', debugErr);
          }
          
          // If this lead is a Sale, prefer the sale-details endpoint so we get server-side Sale calculations
          if (leadData?.service_type === 'Sale') {
            console.log('🔄 Lead is Sale - trying Sale Details API endpoint...');
            try {
              const saleRes = await fetch(`/api/v1/admin/private/sale-details/${lead_id}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${authCtx.token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (saleRes.ok) {
                const saleData = await saleRes.json();
                if (saleData.status === 'success' && saleData.data) {
                  console.log('✅ Sale Details from API:', saleData.data);
                  const sd = saleData.data;

                  // Merge sale details into leadData
                  if (leadData) {
                    const merged = {
                      ...leadData,
                      property_address: sd.property_address || leadData.property_address || leadData.address || {},
                      address: sd.property_address || sd.address || leadData.address || leadData.property_address || {},
                      purchasers: sd.purchasers || leadData.purchasers || leadData.customers || [],
                      customers: sd.purchasers || leadData.customers || leadData.purchasers || [],
                      customer_ids: (sd.purchasers && sd.purchasers.map(p => p._id || p)) || leadData.customer_ids || [],
                      property_value: sd.property_value || sd.purchasePrice || leadData.property_value || 0,
                      purchase_price: sd.purchasePrice || sd.property_value || leadData.purchase_price || leadData.property_value || 0,
                      ownership_type: sd.ownershipType || leadData.ownership_type || (leadData.leasehold ? 'Leasehold' : 'Freehold'),
                      completion_date: sd.completionDate || leadData.completion_date || null,
                      conveyancing_info: sd.info || leadData.conveyancing_info || {},
                      matchedBranch: sd.matchedBranch || null,
                      calculatedQuote: sd.calculatedQuote || null,
                      conveyancingData: sd.conveyancingData || leadData.conveyancingData || null
                    };
                    console.log('📊 Merged sale-details into leadData:', merged);
                    setLeadData(merged);
                  }

                  // Build legalFeeData from calculatedQuote so the modal can display it like Purchase
                  if (sd.calculatedQuote) {
                    const cq = sd.calculatedQuote;
                    const disbs = cq.disbursements || [];
                    const findDisb = (match) => (disbs.find(d => (d.label||d.name||'').toLowerCase().includes(match)) || {}).amount || 0;
                    const saleFeeData = {
                      legal_fee: Number(cq.base) || 0,
                      branch_name: sd.matchedBranch?.name,
                      branch_id: sd.matchedBranch?._id,
                      tt_fee: Number(cq.ttFee) || 30,
                      identity_check: Number(cq.identityCheck) || 10,
                      stamp_duty: 0,
                      land_registry_search: findDisb('land registry') || 0,
                      land_charges_search: findDisb('land charges') || 0,
                      hm_land_registry_fee: findDisb('hm land registry') || 0,
                      search_pack: findDisb('search pack') || 0,
                      property_value: sd.property_value || 0,
                      service_type: 'Sale',
                      all_fixed_fees: cq.fixedFees || [],
                      all_disbursements: cq.disbursements || []
                    };

                    console.log('📦 saleFeeData prepared from calculatedQuote:', saleFeeData);
                    setLegalFeeData(saleFeeData);
                  }

                  // We did the Sale merge; skip trying purchase-details
                }
              }
            } catch (err) {
              console.log('❌ Sale Details API failed:', err);
            }
          } else {
            console.log('🔄 Trying Purchase Details API endpoint...');

            // Try the Purchase Details API first
            try {
              const purchaseRes = await fetch(`/api/v1/admin/private/purchase-details/${lead_id}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${authCtx.token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (purchaseRes.ok) {
                const purchaseData = await purchaseRes.json();
                if (purchaseData.status === 'success' && purchaseData.data) {
                  console.log('✅ Purchase Details from API:', purchaseData.data);
                  console.log('📦 Property Address object:', purchaseData.data.property_address);
                  console.log('💰 Calculated Quote:', purchaseData.data.calculatedQuote);
                  console.log('🏢 Matched Branch:', purchaseData.data.matchedBranch);
                  console.log('📋 Conveyancing Data:', purchaseData.data.conveyancingData);
                  window.purchaseDetailsData = purchaseData.data;

                  // Merge purchase details into the main leadData so the view can render Purchase/Sale fields
                  if (leadData) {
                    const pd = purchaseData.data;
                    const merged = {
                      ...leadData,
                      // prefer property_address from purchase details when available
                      property_address: pd.property_address || leadData.property_address || leadData.address || {},
                      address: pd.property_address || pd.address || leadData.address || leadData.property_address || {},
                      purchasers: pd.purchasers || leadData.purchasers || leadData.customers || [],
                      customers: pd.purchasers || leadData.customers || leadData.purchasers || [],
                      customer_ids: (pd.purchasers && pd.purchasers.map(p => p._id || p)) || leadData.customer_ids || [],
                      property_value: pd.purchasePrice || pd.property_value || leadData.property_value || 0,
                      purchase_price: pd.purchasePrice || pd.purchase_price || leadData.purchase_price || leadData.property_value || 0,
                      ownership_type: pd.ownershipType || leadData.ownership_type || leadData.leasehold ? 'Leasehold' : 'Freehold',
                      completion_date: pd.completionDate || leadData.completion_date || null,
                      conveyancing_info: pd.info || leadData.conveyancing_info || {},
                      matchedBranch: pd.matchedBranch || null,
                      calculatedQuote: pd.calculatedQuote || null,
                      conveyancingData: pd.conveyancingData || leadData.conveyancingData || null
                    };

                    console.log('📊 Merged purchase-details into leadData:', merged);
                    setLeadData(merged);
                  }
                }
              }
            } catch (err) {
              console.log('❌ Purchase Details API failed:', err);
            }
          }
          
          console.log('🔄 Trying new conveyancing-view GET endpoint...');
          
          // Try the new GET endpoint
          try {
            const getRes = await fetch(`${API_BASE_URL}private/conveyancing-view/${lead_id}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authCtx.token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (getRes.ok) {
              const getData = await getRes.json();
              if (getData.status === 'success' && getData.data) {
                console.log('✅ Conveyancing view data from GET endpoint:', getData.data);
                console.log('📊 Address:', getData.data.address);
                console.log('👥 Purchasers:', getData.data.purchasers);
                console.log('💰 Purchase Price:', getData.data.purchase_price);
                console.log('🏠 Ownership Type:', getData.data.ownership_type);
                console.log('📅 Completion Date:', getData.data.completion_date);
                console.log('ℹ️ Info:', getData.data.info);
                console.log('🏢 Group Code:', getData.data.group_code);
                console.log('📞 Acquisition:', getData.data.acquisition);
                console.log('📄 Full Data:', getData.data.fullData);
                
                // Store the API response data for easy access
                window.conveyancingApiData = getData.data;
                
                // Merge the API response with fullData
                const mergedData = {
                  ...getData.data.fullData,
                  // Override with specific API response fields
                  // Keep the property_address object if it exists in fullData
                  property_address: getData.data.fullData?.property_address || getData.data.fullData?.address || getData.data.address,
                  property: getData.data.address,
                  address: getData.data.fullData?.address || getData.data.fullData?.property_address,
                  purchasers: getData.data.purchasers,
                  customers: getData.data.purchasers,
                  property_value: getData.data.purchase_price,
                  ownership_type: getData.data.ownership_type,
                  completion_date: getData.data.completion_date,
                  conveyancing_info: getData.data.info,
                  group_code: getData.data.group_code,
                  acquisition: getData.data.acquisition
                };
                
                console.log('📊 Merged data for view:', {
                  property_address: mergedData.property_address,
                  address: mergedData.address,
                  fullData_address: getData.data.fullData?.address,
                  fullData_property_address: getData.data.fullData?.property_address
                });
                
                setLeadData(mergedData);
                setContactAttempts(mergedData?.contact_attempts || 0);
                setQuoteHistory(mergedData?.quote_history || []);
                setRevisionCount(mergedData?.revision_count || 0);
                return;
              }
            }
          } catch (getErr) {
            console.log('❌ GET endpoint failed:', getErr);
          }
          
          console.log('🔄 Trying fallback lead-overview-data API...');
          
          try {
            const fallbackRes = await postRequest(`private/lead-overview-data/${lead_id}`, {}, authCtx.token);
            console.log('📡 Fallback API Response:', fallbackRes);
            
            if (fallbackRes.status === 'success' && fallbackRes.data) {
              console.log('✅ Lead data received from fallback:', fallbackRes.data);

              // Infer service_type from product_title if not set
              let leadDataWithServiceType = { ...fallbackRes.data };
              if (!leadDataWithServiceType.service_type && leadDataWithServiceType.product_title) {
                const productTitle = leadDataWithServiceType.product_title.toLowerCase();
                console.log('🔍 Product title for inference (fallback):', productTitle);

                if (productTitle.includes('review of legal pack') || productTitle.includes('review legal pack')) {
                  leadDataWithServiceType.service_type = 'Review_Legal_Pack';
                  console.log('✅ Inferred service_type: Review_Legal_Pack (fallback)');
                } else if (productTitle.includes('purchase') && productTitle.includes('sale')) {
                  leadDataWithServiceType.service_type = 'Purchase_Sale';
                  console.log('✅ Inferred service_type: Purchase_Sale (fallback)');
                } else if (productTitle.includes('sale')) {
                  leadDataWithServiceType.service_type = 'Sale';
                  console.log('✅ Inferred service_type: Sale (fallback)');
                } else if (productTitle.includes('purchase')) {
                  leadDataWithServiceType.service_type = 'Purchase';
                  console.log('✅ Inferred service_type: Purchase (fallback)');
                }
              }

              setLeadData(leadDataWithServiceType);
              setContactAttempts(leadDataWithServiceType?.contact_attempts || 0);
              setQuoteHistory(leadDataWithServiceType.quote_history || []);
              setRevisionCount(leadDataWithServiceType.revision_count || 0);
            } else {
              console.log('❌ Fallback API also failed or returned no data');
            }
          } catch (fallbackErr) {
            console.error("❌ Error in fallback API:", fallbackErr);
          }
        }
      } catch (err) {
        console.error("❌ Error fetching conveyancing lead data", err);
        console.log('🔄 Attempting fallback due to error...');
        
        try {
          const fallbackRes = await postRequest(`private/lead-overview-data/${lead_id}`, {}, authCtx.token);
          console.log('📡 Fallback API Response after error:', fallbackRes);
          
          if (fallbackRes.status === 'success' && fallbackRes.data) {
            console.log('✅ Lead data received from fallback after error:', fallbackRes.data);

            // Infer service_type from product_title if not set
            let leadDataWithServiceType = { ...fallbackRes.data };
            if (!leadDataWithServiceType.service_type && leadDataWithServiceType.product_title) {
              const productTitle = leadDataWithServiceType.product_title.toLowerCase();
              console.log('🔍 Product title for inference (catch fallback):', productTitle);

              if (productTitle.includes('review of legal pack') || productTitle.includes('review legal pack')) {
                leadDataWithServiceType.service_type = 'Review_Legal_Pack';
                console.log('✅ Inferred service_type: Review_Legal_Pack (catch fallback)');
              } else if (productTitle.includes('purchase') && productTitle.includes('sale')) {
                leadDataWithServiceType.service_type = 'Purchase_Sale';
                console.log('✅ Inferred service_type: Purchase_Sale (catch fallback)');
              } else if (productTitle.includes('sale')) {
                leadDataWithServiceType.service_type = 'Sale';
                console.log('✅ Inferred service_type: Sale (catch fallback)');
              } else if (productTitle.includes('purchase')) {
                leadDataWithServiceType.service_type = 'Purchase';
                console.log('✅ Inferred service_type: Purchase (catch fallback)');
              }
            }

            setLeadData(leadDataWithServiceType);
            setContactAttempts(leadDataWithServiceType?.contact_attempts || 0);
            setQuoteHistory(leadDataWithServiceType.quote_history || []);
            setRevisionCount(leadDataWithServiceType.revision_count || 0);
          }
        } catch (fallbackErr) {
          console.error("❌ Both primary and fallback APIs failed:", fallbackErr);
        }
      }
      
      console.log('🔍 === FETCH COMPLETE ===');
    }

    fetchConveyancingLead();
    fetchQuoteHistory(); // 📚 Fetch quote history from database
  }, [lead_id, authCtx.token]);

  // Fetch email templates and communication logs
  React.useEffect(() => {
    if (leadData && authCtx.token) {
      fetchEmailTemplates();
      fetchEmailLogs();
    }
  }, [leadData, authCtx.token]);

  // Effect to clear legalFeeData when lead changes
  React.useEffect(() => {
    if (leadData?._id && lastFetchedLeadId && leadData._id !== lastFetchedLeadId) {
      console.log('🔄 Lead changed - clearing old fee data');
      setLegalFeeData(null);
      setLastFetchedLeadId(null);
    }
  }, [leadData?._id]);

  // Effect to auto-fetch quote fee data on page load for Quote Summary
  // ✅ FIXED: Now uses GET API to fetch existing calculation (NO NEW ENTRIES CREATED)
  React.useEffect(() => {
    const fetchQuoteFeeOnLoad = async () => {
      console.log('🔍 Auto-fetch check:', {
        hasLeadData: !!leadData,
        hasToken: !!authCtx.token,
        hasLegalFeeData: !!legalFeeData,
        serviceType: leadData?.service_type,
        leadId: leadData?.lead_id || leadData?._id
      });

      if (!leadData || !authCtx.token) {
        console.log('⏭️ Skipping auto-fetch:', !leadData ? 'No leadData' : 'No token');
        return;
      }

      // Skip if legalFeeData already exists and matches current lead
      if (legalFeeData && lastFetchedLeadId === leadData._id) {
        console.log('⏭️ Skipping auto-fetch: legalFeeData already exists for this lead');
        return;
      }

      console.log('📊 Auto-fetching existing calculation from database...');

      try {
        // Get lead_id (custom ID, not MongoDB _id)
        const leadId = leadData.lead_id || leadData._id;

        // ✅ USE GET API - Just fetch existing calculation, don't create new one
        const apiEndpoint = `private/getFormattedLeadCalculation/${leadId}`;

        console.log('📡 Fetching existing calculation:', apiEndpoint);
        const res = await fetch(`${API_BASE_URL}${apiEndpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authCtx.token}`
          }
        });

        const data = await res.json();

        if (data?.status === 200 && data?.data) {
          console.log('✅ Existing calculation fetched successfully:', data.data);

          // 🎯 Check if Purchase_Sale with separate breakdowns
          if (data.data.purchase_breakdown && data.data.sale_breakdown) {
            console.log('📊 Purchase_Sale detected - combining purchase + sale data');

            // Store separate purchase and sale data
            const transformedData = {
              branch_name: data.data.branch_name,
              branch_id: data.data.branch_id,
              purchase_breakdown: data.data.purchase_breakdown,
              sale_breakdown: data.data.sale_breakdown,
              isPurchaseSale: true
            };

            console.log('🔄 Transformed Purchase_Sale data for Quote Summary:', transformedData);
            setLegalFeeData(transformedData);

            // 🎯 Combine purchase + sale totals for Quote Summary display
            const purchaseTotals = data.data.purchase_breakdown.totals || {};
            const saleTotals = data.data.sale_breakdown.totals || {};

            const combinedTotals = {
              total: {
                net: (purchaseTotals.total?.net || 0) + (saleTotals.total?.net || 0),
                vat: (purchaseTotals.total?.vat || 0) + (saleTotals.total?.vat || 0),
                gross: (purchaseTotals.total?.gross || 0) + (saleTotals.total?.gross || 0)
              },
              legalCost: {
                net: (purchaseTotals.legalCost?.net || 0) + (saleTotals.legalCost?.net || 0),
                vat: (purchaseTotals.legalCost?.vat || 0) + (saleTotals.legalCost?.vat || 0),
                gross: (purchaseTotals.legalCost?.gross || 0) + (saleTotals.legalCost?.gross || 0)
              },
              disbursementCost: {
                net: (purchaseTotals.disbursementCost?.net || 0) + (saleTotals.disbursementCost?.net || 0),
                vat: (purchaseTotals.disbursementCost?.vat || 0) + (saleTotals.disbursementCost?.vat || 0),
                gross: (purchaseTotals.disbursementCost?.gross || 0) + (saleTotals.disbursementCost?.gross || 0)
              }
            };

            console.log('💰 Setting combinedTotals for Purchase_Sale Quote Summary:', combinedTotals);
            setCalculationTotals(combinedTotals);
          } else {
            // Single service type (Purchase, Sale, Remortgage, etc.)
            const transformedData = {
              branch_name: data.data.branch_name,
              branch_id: data.data.branch_id,
              legal_fee: data.data.fees?.legalFee?.amount || 0,
              tt_fee: data.data.fees?.ttFee?.amount || 0,
              identity_check: data.data.fees?.identityCheck?.amount || 0,
              mortgage_admin_fee: data.data.fees?.mortgageAdminFee?.amount || 0,
              help_to_buy_isa_fee: data.data.fees?.helpToBuyISAFee?.amount || 0,
              help_to_buy_fee: data.data.fees?.helpToBuyFee?.amount || 0,
              right_to_buy_fee: data.data.fees?.rightToBuyFee?.amount || 0,
              islamic_mortgage_fee: data.data.fees?.islamicMortgageFee?.amount || 0,
              gifted_deposit_fee_first_giftor: data.data.fees?.giftedDepositFee?.amount || 0,
              gifted_deposit_fee_additional_giftors: data.data.fees?.additionalGiftorFee?.amount || 0,
              buy_to_let_fee: data.data.fees?.buyToLetFee?.amount || 0,
              shared_ownership_fee: data.data.fees?.sharedOwnershipFee?.amount || 0,
              new_build_fee: data.data.fees?.newBuildFee?.amount || 0,
              auction_repossession_fee: data.data.fees?.auctionRepossessionFee?.amount || 0,
              arranging_indemnity_policy: data.data.fees?.arrangingIndemnityPolicy?.amount || 0,
              leasehold_fee: data.data.fees?.leaseholdFee?.amount || 0,
              stamp_duty: data.data.fees?.stampDuty?.amount || 0,
              hm_land_registry_fee: data.data.fees?.hmLandRegistryFee?.amount || 0,
              land_registry_search: data.data.fees?.landRegistrySearch?.amount || 0,
              land_charges_search: data.data.fees?.landChargesSearch?.amount || 0,
              search_pack: data.data.fees?.searchPack?.amount || 0,
              official_copies: data.data.fees?.officialCopies?.amount || 0,
              total_quote: data.data.total || 0,
              all_fixed_fees: data.data.all_fixed_fees || [],
              all_disbursements: data.data.all_disbursements || []
            };

            console.log('🔄 Transformed data for Quote Summary:', transformedData);
            setLegalFeeData(transformedData);

            // 🎯 IMPORTANT: Set calculationTotals from API response
            // This ensures Quote Summary displays Net + VAT = Gross breakdown automatically
            if (data.data.totals) {
              console.log('💰 Setting calculationTotals from fetched data:', data.data.totals);
              setCalculationTotals(data.data.totals);
            }
          }

          setLastFetchedLeadId(leadData._id);
          console.log('✅ Marked lead as fetched:', leadData._id);
        } else if (data?.status === 404) {
          console.log('⚠️ No calculation found in database for this lead');
          // Calculation doesn't exist yet - this is OK, it will be created when lead is edited
        } else {
          console.error('❌ API response not successful:', {
            status: data?.status,
            message: data?.message
          });
        }
      } catch (error) {
        console.error('❌ Error fetching existing calculation:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
      }
    };

    fetchQuoteFeeOnLoad();
  }, [leadData, authCtx.token, lastFetchedLeadId]);

  // Effect to handle postcode matching and cost calculation
  React.useEffect(() => {
    const handlePostcodeMatching = async () => {
      if (!leadData || !leadData.property_address) return;
      
      console.log('🔍 === STARTING POSTCODE MATCHING ===');
      console.log('Lead data:', leadData);
      
      // Extract postcode from property address
      let postcode = null;
      const addressObj = leadData.property_address || leadData.property;
      
      if (typeof addressObj === 'string') {
        // If it's a string, try to extract postcode from the end
        const addressParts = addressObj.split(',').map(p => p.trim());
        postcode = addressParts[addressParts.length - 1];
      } else if (addressObj) {
        postcode = addressObj.postcode || addressObj.postal_code || addressObj.post_code;
      }
      
      if (!postcode) {
        console.log('❌ No postcode found in property address');
        return;
      }
      
      console.log('📮 Extracted postcode:', postcode);
      
      // Find matching branch
      const branch = await fetchBranchByPostcode(postcode);
      if (branch) {
        setMatchedBranch(branch);
        setBranchInfo(branch);
        console.log('✅ Matched branch:', branch);
        
        // Fetch conveyancing costs for this branch
        if (leadData.product_id) {
          const costs = await fetchConveyancingCosts(branch._id, leadData.product_id);
              if (costs) {
            setConveyancingCosts(costs);
            
            // Calculate new quote based on branch costs
            const branchQuote = calculateBranchBasedQuote(
              leadData.property_value || 0,
              leadData.service_type || 'Purchase',
              costs,
              leadData.service_type === 'Sale' ? {
                // Sale-specific options
                mortgage_outstanding: leadData.mortgage_outstanding,
                estate_agent: leadData.estate_agent,
                chain_free: leadData.chain_free
              } : {
                // Purchase-specific options
                leasehold: leadData.leasehold,
                mortgage_lender: leadData.mortgage_lender,
                help_to_buy: leadData.help_to_buy,
                shared_ownership: leadData.shared_ownership
              }
            );
            
            if (branchQuote) {
              setCalculatedQuote(branchQuote);
              console.log('✅ Branch-based quote calculated:', branchQuote);
            }
          }
        }
      }
    };
    
    handlePostcodeMatching();
  }, [leadData?.property_address, leadData?.product_id]);

  // Effect to populate vendors and purchasers with customer details
  React.useEffect(() => {
    const populateCustomers = async () => {
      if (!leadData || !authCtx.token) {
        console.log('⚠️ Cannot populate - leadData or token missing');
        return;
      }

      console.log('🔍 ====== POPULATING VENDORS AND PURCHASERS ======');
      console.log('📊 leadData.vendors:', leadData.vendors);
      console.log('📊 leadData.purchasers:', leadData.purchasers);
      console.log('📊 service_type:', leadData.service_type);

      // Populate vendors
      if (leadData.vendors && Array.isArray(leadData.vendors) && leadData.vendors.length > 0) {
        console.log('✅ Vendors array found with', leadData.vendors.length, 'items');
        const firstVendor = leadData.vendors[0];
        console.log('🔍 First vendor:', firstVendor, 'Type:', typeof firstVendor);
        const isPopulated = firstVendor && typeof firstVendor === 'object' && firstVendor.forename;
        console.log('🔍 Is populated?', isPopulated);

        if (!isPopulated) {
          console.log('📞 Fetching vendor details from API...');
          setIsLoadingVendors(true);
          try {
            const vendorPromises = leadData.vendors.map(async (vendorId) => {
              const id = typeof vendorId === 'string' ? vendorId : vendorId._id;
              console.log('🔍 Fetching vendor with ID:', id);
              try {
                const customerRes = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
                console.log('📋 Vendor API response for ID', id, ':', customerRes);
                return customerRes?.data || null;
              } catch (err) {
                console.error('❌ Error fetching vendor ID', id, ':', err);
                return null;
              }
            });

            const vendorDetails = await Promise.all(vendorPromises);
            const validVendors = vendorDetails.filter(v => v && v._id);
            console.log('✅ Populated vendors:', validVendors);
            console.log('✅ Setting populatedVendors state with', validVendors.length, 'vendors');
            setPopulatedVendors(validVendors);
          } catch (err) {
            console.error('❌ Error fetching vendors:', err);
          } finally {
            setIsLoadingVendors(false);
          }
        } else {
          console.log('✅ Vendors already populated');
          setPopulatedVendors(leadData.vendors);
          setIsLoadingVendors(false);
        }
      } else {
        console.log('⚠️ No vendors array found or vendors array is empty');
        setPopulatedVendors([]);
        setIsLoadingVendors(false);
      }

      // Populate purchasers
      if (leadData.purchasers && Array.isArray(leadData.purchasers) && leadData.purchasers.length > 0) {
        console.log('✅ Purchasers array found with', leadData.purchasers.length, 'items');
        const firstPurchaser = leadData.purchasers[0];
        console.log('🔍 First purchaser:', firstPurchaser, 'Type:', typeof firstPurchaser);
        const isPopulated = firstPurchaser && typeof firstPurchaser === 'object' && firstPurchaser.forename;
        console.log('🔍 Is populated?', isPopulated);

        if (!isPopulated) {
          console.log('📞 Fetching purchaser details from API...');
          setIsLoadingPurchasers(true);
          try {
            const purchaserPromises = leadData.purchasers.map(async (purchaserId) => {
              const id = typeof purchaserId === 'string' ? purchaserId : purchaserId._id;
              console.log('🔍 Fetching purchaser with ID:', id);
              try {
                const customerRes = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
                console.log('📋 Purchaser API response for ID', id, ':', customerRes);
                return customerRes?.data || null;
              } catch (err) {
                console.error('❌ Error fetching purchaser ID', id, ':', err);
                return null;
              }
            });

            const purchaserDetails = await Promise.all(purchaserPromises);
            const validPurchasers = purchaserDetails.filter(p => p && p._id);
            console.log('✅ Populated purchasers:', validPurchasers);
            console.log('✅ Setting populatedPurchasers state with', validPurchasers.length, 'purchasers');
            setPopulatedPurchasers(validPurchasers);
          } catch (err) {
            console.error('❌ Error fetching purchasers:', err);
          } finally {
            setIsLoadingPurchasers(false);
          }
        } else {
          console.log('✅ Purchasers already populated');
          setPopulatedPurchasers(leadData.purchasers);
          setIsLoadingPurchasers(false);
        }
      } else {
        console.log('⚠️ No purchasers array found or purchasers array is empty');
        setPopulatedPurchasers([]);
        setIsLoadingPurchasers(false);
      }

      console.log('🔍 ====== POPULATION COMPLETE ======');
    };

    populateCustomers();
  }, [leadData?.vendors, leadData?.purchasers, authCtx.token]);

  // NOTE: Legal fee data is now fetched only when View Quote Detail button is clicked
  // This prevents unnecessary API calls on page load
  // The API call happens in handleViewQuoteDetail() function

  const calculateQuote = () => {
    if (!leadData) return { base: 0, vat: 0, total: 0, breakdown: {} };
    
    // If we have backend-calculated values, use them
    if (leadData.total_quote && leadData.legal_fee !== undefined) {
      return {
        base: parseFloat(leadData.legal_fee || 0).toFixed(2),
        vat: parseFloat(leadData.vat_amount || 0).toFixed(2),
        total: parseFloat(leadData.total_quote || 0).toFixed(2),
        breakdown: {
          fixedFees: leadData.fixed_fees || {},
          disbursements: leadData.disbursements || {}
        }
      };
    }
    
    // Fallback calculation for legacy data
    let baseFee = parseFloat(leadData.legal_fee || 575);
    let additionalFees = 0;
    
    // Add fixed fees based on options
    if (leadData.leasehold) additionalFees += 150;
    if (leadData.mortgage_lender) additionalFees += 150;
    if (leadData.help_to_buy) additionalFees += 150;
    if (leadData.shared_ownership) additionalFees += 150;
    additionalFees += 30; // TT fee
    
    const subtotal = baseFee + additionalFees;
    const disbursements = 340; // Search pack + Identity check
    const vat = subtotal * 0.20;
    const total = subtotal + vat + disbursements;
    
    return { 
      base: subtotal.toFixed(2), 
      vat: vat.toFixed(2), 
      total: total.toFixed(2),
      breakdown: {
        baseFee: baseFee,
        additionalFees: additionalFees,
        disbursements: disbursements
      }
    };
  };

  // Fetch Quote Fee using fetch_lead_calculations API
  // Backend auto-calculates using calculateAndSaveLeadQuote if needed
  const fetchQuoteFee = async (fromReviseButton = false) => {
    if (!leadData) return;

    // If quote has been revised, use current legalFeeData instead of fetching from API
    if (revisionCount > 0 && legalFeeData && !fromReviseButton) {
      console.log('🔄 Quote has been revised (revision count:', revisionCount, ') - using current legalFeeData');
      setSelectedQuote({ legalFeeData, leadData });
      setShowQuoteDetailModal(true);
      return;
    }

    console.log('📡 Fetching quote data from DATABASE for View Quote Detail...');

    try {
      setLoadingFee(true);

      const serviceType = leadData.service_type || 'Purchase';
      const leadId = leadData.lead_id || leadData._id;

      console.log(`💾 Fetching calculation from DATABASE for lead:`, leadId);

      // Get partner_id if exists
      const partnerId = leadData.partner_id?._id || leadData.partner_id;
      console.log('🤝 Partner ID for fetchQuoteFee:', partnerId);

      // 🎯 NEW: Fetch from database using fetch_lead_calculations API
      const fetchUrl = partnerId
        ? `private/fetch_lead_calculations/${leadId}?partner_id=${partnerId}`
        : `private/fetch_lead_calculations/${leadId}`;

      console.log('📞 Fetching quote from:', fetchUrl);

      const res = await getRequest(fetchUrl, authCtx.token);

      console.log('📡 Database Response:', res);

      console.log('📡 Database Response:', res);

      if (res?.status === 200 && res?.data) {
        console.log('✅ Calculation loaded from DATABASE:', res.data);

        // 🎯 NEW: Data is already in nested format from database
        const { fees, disbursements, totals } = res.data;

        // 🔍 DEBUG: Log complete API response
        console.log('📊 ========== FETCH_LEAD_CALCULATIONS RESPONSE ==========');
        console.log('💰 Fees:', fees);
        console.log('💳 Disbursements:', disbursements);
        console.log('📈 Totals:', totals);
        console.log('📋 Fee Keys:', Object.keys(fees || {}));
        console.log('📋 Disbursement Keys:', Object.keys(disbursements || {}));
        console.log('=========================================================');

        // Set quoteFeeData for Revise Quote Modal (nested format)
        setQuoteFeeData({ fees, disbursements, totals });

        // Transform to flat structure for ViewQuoteDetailModal
        const transformedData = {
          branch_name: res.data.branch_name,
          branch_id: res.data.branch_id,

          // Common fees (check both camelCase variations)
          legal_fee: fees?.legalFee?.amount || 0,
          tt_fee: fees?.ttFee?.amount || fees?.telegraphicTransferFee?.amount || 0,  // ✅ Check both names
          mortgage_admin_fee: fees?.mortgageAdminFee?.amount || 0,
          leasehold_fee: fees?.leaseholdFee?.amount || 0,

          // Purchase-specific fees (check both camelCase variations)
          identity_check: fees?.identityCheck?.amount || disbursements?.identityCheck?.amount || 0,
          help_to_buy_isa_fee: fees?.helpToBuyISAFee?.amount || 0,
          help_to_buy_fee: fees?.helpToBuyFee?.amount || 0,
          help_to_buy_equity_loan_fee: fees?.helpToBuyEquityLoanFee?.amount || 0,
          right_to_buy_fee: fees?.rightToBuyFee?.amount || 0,
          first_time_buyer_fee: fees?.firstTimeBuyerFee?.amount || 0,
          shared_ownership_fee: fees?.sharedOwnershipFee?.amount || 0,
          new_build_fee: fees?.newBuildFee?.amount || 0,
          islamic_mortgage_fee: fees?.islamicMortgageFee?.amount || 0,
          auction_repossession_fee: fees?.auctionRepossessionFee?.amount || 0,
          gifted_deposit_fee_first_giftor: fees?.giftedDepositFee?.amount || fees?.giftedDepositFeeFirstGiftor?.amount || 0,  // ✅ Check both
          buy_to_let_fee: fees?.buyToLetFee?.amount || 0,
          arranging_indemnity_policy: fees?.arrangingIndemnityPolicy?.amount || 0,

          // Disbursements (check variations)
          stamp_duty: disbursements?.stampDuty?.amount || disbursements?.stamp_duty_land_tax?.amount || 0,  // ✅ Check both
          hm_land_registry_fee: disbursements?.hmLandRegistryFee?.amount || 0,
          land_registry_search: disbursements?.landRegistrySearch?.amount || 0,
          land_charges_search: disbursements?.landChargesSearch?.amount || 0,
          search_pack: disbursements?.searchPack?.amount || 0,
          official_copies: disbursements?.officialCopiesFee?.amount || disbursements?.officialCopies?.amount || 0,  // ✅ Check both

          total_quote: totals?.total?.gross || 0,

          // 🎯 Store raw fees and disbursements for dynamic rendering
          all_fees: fees || {},
          all_disbursements: disbursements || {}
        };

        console.log('🔄 Transformed data for ViewQuoteDetailModal:', transformedData);

        // Set legalFeeData
        setLegalFeeData(transformedData);

        // 🎯 Store totals from database
        setCalculationTotals(totals);
        console.log('💰 Totals from database:', totals);

        // Open modal if not from Revise button
        if (!fromReviseButton) {
          setSelectedQuote({ legalFeeData: transformedData, leadData, totals });
          setShowQuoteDetailModal(true);
        }
      } else {
        console.log('⚠️ No calculation found in database or error:', res);
        Swal.fire('Error', 'No quote calculation found. Please regenerate the quote.', 'error');
      }
    } catch (error) {
      console.error('❌ Error fetching quote fee:', error);
      Swal.fire('Error', 'Failed to load quote fees', 'error');
    } finally {
      setLoadingFee(false);
    }
  };

  // Handle viewing quote details - Uses fetchQuoteFee() to get data from DB
  const handleViewQuoteDetail = async (quote) => {
    console.log('🔴 handleViewQuoteDetail called - calling fetchQuoteFee()');
    try {
      // Fetch quote data (auto-calculates if not in DB)
      await fetchQuoteFee();
    } catch (error) {
      console.error('❌ Error in handleViewQuoteDetail:', error);
      Swal.fire('Error', 'Failed to load quote details', 'error');
    }
  };

  // OLD COMPLEX CODE REMOVED - Now using calculateAndSaveLeadQuote unified API
  const handleViewQuoteDetailOLD_BACKUP = async (quote) => {
    try {
      if (quote && quote.fees) {
        console.log('📜 Viewing historical quote:', quote);
        // Convert historical quote format to legalFeeData format for the modal
        const historicalFeeData = {
          legal_fee: quote.fees.legal_fee || 0,
          tt_fee: quote.fees.tt_fee || 0,
          identity_check: quote.fees.identity_check || 0,
          stamp_duty: quote.fees.stamp_duty || 0,
          hm_land_registry_fee: quote.fees.hm_land_registry_fee || 0,
          land_registry_search: quote.fees.land_registry_search || 0,
          land_charges_search: quote.fees.land_charges_search || 0,
          search_pack: quote.fees.search_pack || 0,
          branch_name: quote.branch_name || legalFeeData?.branch_name,
          branch_id: legalFeeData?.branch_id,
          all_fixed_fees: legalFeeData?.all_fixed_fees || [],
          all_disbursements: legalFeeData?.all_disbursements || []
        };
        const quoteDetailsData = { legalFeeData: historicalFeeData, leadData };
        setSelectedQuote(quoteDetailsData);
        setShowQuoteDetailModal(true);
        return;
      }

      // Always fetch fresh data from API when View Quote Detail is clicked
      // This ensures we get the latest fees from the database based on postcode and property value
      console.log('🔄 Fetching fresh quote data from API...');

      // Get postcode from lead data
      let postcode = '';

      // For Purchase_Sale, prioritize purchase property address
      if (leadData?.service_type === 'Purchase_Sale' && leadData?.purchasePropertyDetails?.address) {
        const purchaseAddress = leadData.purchasePropertyDetails.address;
        postcode = purchaseAddress.postcode || '';
      }
      // Otherwise use general property_address
      else if (leadData?.property_address) {
        const addressObj = leadData.property_address;
        postcode = addressObj.postcode || addressObj.postal_code || addressObj.post_code || '';
      }

      if (!postcode) {
        console.log('⚠️ No postcode found for legal fee calculation');
        // If no postcode, open modal with existing data
        const quoteDetailsData = { legalFeeData, leadData };
        setSelectedQuote(quoteDetailsData);
        setShowQuoteDetailModal(true);
        return;
      }

      console.log('📮 Fetching legal fee for postcode:', postcode);
      console.log('🏠 Service Type:', leadData?.service_type);
      console.log('🔍 Checking service type condition - is Sale?:', leadData?.service_type === 'Sale');
      console.log('🔍 Checking service type condition - is Purchase?:', leadData?.service_type === 'Purchase');
      console.log('🔍 Checking service type condition - is Purchase_Sale?:', leadData?.service_type === 'Purchase_Sale');

      // SALE flow: branch-based costs and calculation
      if (leadData?.service_type === 'Sale') {
        console.log('🏢 Sale service type detected - fetching branch-based costs');
        const branch = await fetchBranchByPostcode(postcode);
        if (branch && leadData.product_id) {
          const costs = await fetchConveyancingCosts(branch._id, leadData.product_id);
          if (costs) {
            console.log('💰 Got conveyancing costs for Sale:', costs);
            const saleQuote = calculateBranchBasedQuote(
              leadData.property_value || 0,
              'Sale',
              costs,
              {
                mortgage_outstanding: leadData.mortgage_outstanding,
                estate_agent: leadData.estate_agent,
                chain_free: leadData.chain_free
              }
            );

            // Build saleFeeData from costs and saleQuote (fallback to costs-derived values)
            // Use legal_fee from backend API response (already calculated)
            let ttFee = costs.tt_fee || 30;
            let identityCheck = costs.identity_check || 10;
            let officialCopies = costs.official_copies || 14;
            let legalFeeFromRange = costs.legal_fee || saleQuote?.legal_fee || 0;
            let extractedMortgageAdminFee = costs.mortgage_admin_fee || (saleQuote && saleQuote.mortgage_admin_fee) || 0;

            console.log('🔍 Using fees from backend API:', {
              legal_fee: costs.legal_fee,
              tt_fee: costs.tt_fee,
              identity_check: costs.identity_check,
              mortgage_admin_fee: costs.mortgage_admin_fee,
              official_copies: costs.official_copies
            });

            if (costs) {
              const propertyValue = leadData.property_value || 0;

              // Root-level propertyRangeFees (preferred) - check both minValue and minPropertyValue formats
              if (costs.propertyRangeFees && Array.isArray(costs.propertyRangeFees)) {
                const matchingRange = costs.propertyRangeFees.find(r =>
                  propertyValue >= (r.minValue || r.minPropertyValue || 0) &&
                  propertyValue <= (r.maxValue || r.maxPropertyValue || Infinity)
                );
                if (matchingRange) {
                  const candidate = matchingRange.legalFee ?? matchingRange.legal_fee ?? matchingRange.legalFees ?? matchingRange.legal_fees ?? matchingRange.amount ?? matchingRange.fee ?? null;
                  if (candidate !== null && candidate !== undefined) {
                    legalFeeFromRange = parseFloat(candidate) || legalFeeFromRange;
                    console.log('✅ Found legal fee from range (root):', legalFeeFromRange);
                  }
                }
              }

              // Transactions may contain transaction-level fixedFees and propertyRangeFees
              if (costs.transactions && Array.isArray(costs.transactions)) {
                const normalizeTransactionType = (t) => {
                  if (!t) return '';
                  if (typeof t === 'string') return t;
                  if (typeof t === 'object') return (t.name || t.label || t.type || t.transactionType || '') + '';
                  return '';
                };

                let tx = costs.transactions.find(t => {
                  const txType = (normalizeTransactionType(t.transactionType) || '').toLowerCase();
                  return txType === 'sale' || txType.includes('sale');
                });
                if (!tx) tx = costs.transactions.find(t => (normalizeTransactionType(t.transactionType) || '').toLowerCase().includes('sale')) || costs.transactions[0];

                if (tx) {
                  // Transaction-level propertyRangeFees array
                  if (tx.propertyRangeFees && Array.isArray(tx.propertyRangeFees)) {
                    const matchingRange = tx.propertyRangeFees.find(r => propertyValue >= (r.minValue||0) && propertyValue <= (r.maxValue||Infinity));
                    if (matchingRange) {
                      const candidate = matchingRange.legalFee ?? matchingRange.legal_fee ?? matchingRange.legalFees ?? matchingRange.legal_fees ?? matchingRange.amount ?? matchingRange.fee ?? null;
                      if (candidate !== null && candidate !== undefined) {
                        legalFeeFromRange = parseFloat(candidate) || legalFeeFromRange;
                        console.log('✅ Found legal fee from range (transaction):', legalFeeFromRange);
                      }
                    }
                  }

                  // Transaction.propertyValues object (legacy shape)
                  if ((!legalFeeFromRange || legalFeeFromRange === 0) && tx.propertyValues) {
                    const propertyRanges = Object.keys(tx.propertyValues || {}).sort((a, b) => {
                      const aMax = parseInt(a.split('-')[1]?.replace(/[^\d]/g, '') || '999999999');
                      const bMax = parseInt(b.split('-')[1]?.replace(/[^\d]/g, '') || '999999999');
                      return aMax - bMax;
                    });
                    for (const range of propertyRanges) {
                      const [minStr, maxStr] = range.split('-');
                      const min = parseInt((minStr || '').replace(/[^\d]/g, '')) || 0;
                      const max = parseInt((maxStr || '').replace(/[^\d]/g, '')) || Infinity;
                      if (propertyValue >= min && propertyValue <= max) {
                        const v = parseFloat(tx.propertyValues[range]) || 0;
                        if (v) { legalFeeFromRange = v; break; }
                      }
                    }
                  }

                  // Extract fixedFees: array or object
                  if (tx.fixedFees) {
                    if (Array.isArray(tx.fixedFees)) {
                      tx.fixedFees.forEach(f => {
                        const label = (f.label || f.name || '').toString().toLowerCase();
                        const amt = parseFloat(f.amount || f.value || 0) || 0;
                        if (/telegraphic|tt|transfer/.test(label) && amt) ttFee = amt;
                        if (/mortgage\s*admin|mortgageadmin|mortgage_admin|mortgage admin|mortgageadminfee/i.test(label) && amt) extractedMortgageAdminFee = amt;
                      });
                    } else if (typeof tx.fixedFees === 'object') {
                      Object.values(tx.fixedFees).forEach(f => {
                        const label = (f.label || f.name || '') + '';
                        const l = label.toLowerCase();
                        const amt = parseFloat(f.amount || f || 0) || 0;
                        if (/telegraphic|tt|transfer/.test(l) && amt) ttFee = amt;
                        if (/mortgage\s*admin|mortgageadmin|mortgage_admin|mortgage admin|mortgageadminfee/i.test(l) && amt) extractedMortgageAdminFee = amt;
                      });
                    }
                  }

                  // Transaction-level disbursements
                  if (tx.disbursements && Array.isArray(tx.disbursements)) {
                    const idItem = tx.disbursements.find(d => /identity/i.test(d.label || d.name || ''));
                    if (idItem) identityCheck = parseFloat(idItem.amount || 0) || identityCheck;
                    const ocItem = tx.disbursements.find(d => /official/i.test(d.label || d.name || ''));
                    if (ocItem) officialCopies = parseFloat(ocItem.amount || 0) || officialCopies;
                  }
                }
              }

              // Root-level fixedFees/disbursements fallback extraction
              if (costs.fixedFees && Array.isArray(costs.fixedFees)) {
                const ttItem = costs.fixedFees.find(f => /telegraphic|tt|transfer/i.test(f.label || f.name || ''));
                if (ttItem) ttFee = parseFloat(ttItem.amount || 0) || ttFee;
                const mItem = costs.fixedFees.find(f => /mortgage\s*admin|mortgageadmin|mortgage_admin|mortgage admin|mortgageadminfee/i.test(f.label || f.name || ''));
                if (mItem) extractedMortgageAdminFee = parseFloat(mItem.amount || 0) || extractedMortgageAdminFee;
              }

              if (costs.disbursements && Array.isArray(costs.disbursements)) {
                const idItem = costs.disbursements.find(d => /identity/i.test(d.label || d.name || ''));
                if (idItem) identityCheck = parseFloat(idItem.amount || 0) || identityCheck;
                const ocItem = costs.disbursements.find(d => /official/i.test(d.label || d.name || ''));
                if (ocItem) officialCopies = parseFloat(ocItem.amount || 0) || officialCopies;
              }

              // Extract common disbursements (HM Land Registry, Land Registry Search, Land Charges Search, Search Pack)
              let hmLandRegistryFee = 0;
              let landRegistrySearch = 0;
              let landChargesSearch = 0;
              let searchPack = 0;

              // Prefer saleQuote disbursements if present
              if (saleQuote && saleQuote.all_disbursements && Array.isArray(saleQuote.all_disbursements)) {
                saleQuote.all_disbursements.forEach(d => {
                  const label = (d.label || d.name || '').toString().toLowerCase();
                  const amt = parseFloat(d.amount || d.value || 0) || 0;
                  if (label.includes('hm land registry')) hmLandRegistryFee = amt;
                  if (label.includes('land registry') && label.includes('search')) landRegistrySearch = amt;
                  if (label.includes('land charges')) landChargesSearch = amt;
                  if (label.includes('search pack') || label.includes('searches')) searchPack = amt;
                });
              }

              // Fall back to root-level costs.disbursements
              if (costs.disbursements && Array.isArray(costs.disbursements)) {
                costs.disbursements.forEach(d => {
                  const label = (d.label || d.name || '').toString().toLowerCase();
                  const amt = parseFloat(d.amount || d.value || 0) || 0;
                  if (!hmLandRegistryFee && label.includes('hm land registry')) hmLandRegistryFee = amt;
                  if (!landRegistrySearch && label.includes('land registry') && label.includes('search')) landRegistrySearch = amt;
                  if (!landChargesSearch && label.includes('land charges')) landChargesSearch = amt;
                  if (!searchPack && (label.includes('search pack') || label.includes('searches'))) searchPack = amt;
                });
              }

              // Also check transaction-level disbursements (already partially handled above)
              if (costs.transactions && Array.isArray(costs.transactions)) {
                costs.transactions.forEach(tx => {
                  if (tx.disbursements && Array.isArray(tx.disbursements)) {
                    tx.disbursements.forEach(d => {
                      const label = (d.label || d.name || '').toString().toLowerCase();
                      const amt = parseFloat(d.amount || d.value || 0) || 0;
                      if (!hmLandRegistryFee && label.includes('hm land registry')) hmLandRegistryFee = amt;
                      if (!landRegistrySearch && label.includes('land registry') && label.includes('search')) landRegistrySearch = amt;
                      if (!landChargesSearch && label.includes('land charges')) landChargesSearch = amt;
                      if (!searchPack && (label.includes('search pack') || label.includes('searches'))) searchPack = amt;
                    });
                  }
                });
              }

              // Attach the discovered disbursement values back to variables used by the modal
              // These will be included in saleFeeData below
              var _hmLandRegistryFee = hmLandRegistryFee;
              var _landRegistrySearch = landRegistrySearch;
              var _landChargesSearch = landChargesSearch;
              var _searchPack = searchPack;
            }

            const saleFeeData = {
              // Prefer the legal fee determined from propertyRangeFees (exact match),
              // otherwise use the calculated saleQuote. This ensures property_value range mapping drives legal fee.
              legal_fee: (legalFeeFromRange && Number(legalFeeFromRange)) || saleQuote?.legal_fee || 0,
              branch_name: branch.name,
              branch_id: branch._id,
              tt_fee: ttFee,
              identity_check: identityCheck,
              // Prefer extracted mortgage admin fee from branch transaction fixedFees, then saleQuote, otherwise fallback to 150 when mortgage outstanding
              mortgage_admin_fee: (extractedMortgageAdminFee && Number(extractedMortgageAdminFee)) || (saleQuote && saleQuote.mortgage_admin_fee) || (leadData.mortgage_outstanding ? 150 : 0),
              official_copies: officialCopies,
              stamp_duty: saleQuote?.stamp_duty || 0,
              land_registry_search: saleQuote?.land_registry_search || _landRegistrySearch || 0,
              land_charges_search: saleQuote?.land_charges_search || _landChargesSearch || 0,
              hm_land_registry_fee: saleQuote?.hm_land_registry_fee || _hmLandRegistryFee || 0,
              search_pack: saleQuote?.search_pack || _searchPack || 0,
              additional_fees: saleQuote?.additional_fees || 0,
              property_value: leadData?.property_value || 0,
              service_type: 'Sale',
              calculation_source: saleQuote?.calculation_source || 'branch_costs',
              // If the branch returned vat/total use it, otherwise compute totals in the same way Purchase flow does
              vat_amount: saleQuote?.vat_amount || (function() {
                const lf = Number(((legalFeeFromRange && Number(legalFeeFromRange)) || saleQuote?.legal_fee || 0));
                const tt = Number(ttFee || 0);
                const idc = Number(identityCheck || 0);
                const ma = Number(((extractedMortgageAdminFee && Number(extractedMortgageAdminFee)) || (saleQuote && saleQuote.mortgage_admin_fee) || (leadData.mortgage_outstanding ? 150 : 0)) || 0);
                const oc = Number(officialCopies || 0);
                // VAT applies to legal fee, TT and mortgage admin fee (official copies and many disbursements are 0% VAT)
                return Math.round(((lf * 0.20) + (tt * 0.20) + (ma * 0.20)) * 100) / 100;
              })(),
              total_quote: saleQuote?.total_quote || (function() {
                const lf = Number(((legalFeeFromRange && Number(legalFeeFromRange)) || saleQuote?.legal_fee || 0));
                const tt = Number(ttFee || 0);
                const idc = Number(identityCheck || 0);
                const sd = Number(saleQuote?.stamp_duty || 0);
                const hm = Number(saleQuote?.hm_land_registry_fee || _hmLandRegistryFee || 0);
                const lrs = Number(saleQuote?.land_registry_search || _landRegistrySearch || 0);
                const lcs = Number(saleQuote?.land_charges_search || _landChargesSearch || 0);
                const sp = Number(saleQuote?.search_pack || _searchPack || 0);
                const ma = Number(((extractedMortgageAdminFee && Number(extractedMortgageAdminFee)) || (saleQuote && saleQuote.mortgage_admin_fee) || (leadData.mortgage_outstanding ? 150 : 0)) || 0);
                const oc = Number(officialCopies || 0);

                const totalNet = lf + tt + idc + sd + hm + lrs + lcs + sp + (leadData?.service_type === 'Sale' ? (ma + oc) : 0);
                const totalVAT = Math.round((lf * 0.20 + tt * 0.20 + ma * 0.20) * 100) / 100;
                return Math.round((totalNet + totalVAT) * 100) / 100;
              })(),
              all_fixed_fees: saleQuote?.all_fixed_fees || costs?.fixedFees || [],
              all_disbursements: saleQuote?.all_disbursements || costs?.disbursements || [],
              costs_data: costs
            };

            setLegalFeeData(saleFeeData);
            setSelectedQuote({ legalFeeData: saleFeeData, leadData });
            setShowQuoteDetailModal(true);
            return;
          }
        }
        // If branch or costs missing, fall through to generic fallback
        console.log('⚠️ Could not obtain branch costs for Sale; falling back to existing data');
        setSelectedQuote({ legalFeeData, leadData });
        setShowQuoteDetailModal(true);
        return;
      }

      // PURCHASE_SALE flow: handle both sale and purchase quotes
      if (leadData?.service_type === 'Purchase_Sale') {
        console.log('🏢🏠 Purchase_Sale service type detected - fetching costs for both sale and purchase');

        const branch = await fetchBranchByPostcode(postcode);
        if (branch && leadData.product_id) {
          // Helper function to transform fee data to renderQuoteTable format
          const transformToQuoteFormat = (feeData, serviceType) => {
            const fixedFees = [];
            const disbursements = [];

            // Add fixed fees
            if (feeData.tt_fee) {
              fixedFees.push({ label: 'Telegraphic Transfer (TT) Fee', amount: feeData.tt_fee });
            }
            if (feeData.mortgage_admin_fee && feeData.mortgage_admin_fee > 0) {
              fixedFees.push({ label: 'Mortgage Admin Fee', amount: feeData.mortgage_admin_fee });
            }
            if (serviceType === 'Sale') {
              // Sale-specific fees from salePropertyDetails.info
              if (leadData.salePropertyDetails?.info?.sharedOwnership) {
                fixedFees.push({ label: 'Shared Ownership Fee', amount: 150 });
              }
              if (leadData.salePropertyDetails?.info?.auctionRepossession) {
                fixedFees.push({ label: 'Auction/Repossession Fee', amount: 150 });
              }
            } else if (serviceType === 'Purchase') {
              // Purchase-specific fees from purchasePropertyDetails.info
              if (leadData.purchasePropertyDetails?.info?.helpToBuyISA) {
                fixedFees.push({ label: 'Help to Buy ISA Fee', amount: 100 });
              }
              if (leadData.purchasePropertyDetails?.info?.helpToBuy || leadData.purchasePropertyDetails?.info?.helpToBuyEquityLoan) {
                fixedFees.push({ label: 'Help to Buy Fee', amount: 150 });
              }
            }

            // Add disbursements
            if (feeData.identity_check) {
              disbursements.push({ label: 'Identity Check', amount: feeData.identity_check });
            }
            if (feeData.hm_land_registry_fee) {
              disbursements.push({ label: 'HM Land Registry Fee', amount: feeData.hm_land_registry_fee });
            }
            if (feeData.stamp_duty && serviceType === 'Purchase') {
              disbursements.push({ label: 'Stamp Duty Land Tax', amount: feeData.stamp_duty });
            }
            if (feeData.land_registry_search) {
              disbursements.push({ label: 'Land Registry Priority Search', amount: feeData.land_registry_search });
            }
            if (feeData.land_charges_search) {
              disbursements.push({ label: 'Land Charges Search', amount: feeData.land_charges_search });
            }
            if (feeData.search_pack) {
              disbursements.push({ label: 'Search Pack - Local Authority, Water & Drainage, Environmental Search', amount: feeData.search_pack });
            }
            if (feeData.official_copies) {
              disbursements.push({ label: 'Official Copies', amount: feeData.official_copies });
            }

            return {
              base: feeData.legal_fee || 0,
              branchName: feeData.branch_name,
              branchAddress: branch.address,
              fixedFees,
              disbursements
            };
          };

          // Fetch costs for SALE part
          const salePropertyValue = leadData.salePropertyDetails?.salePrice || 0;
          const saleCosts = await fetchConveyancingCosts(branch._id, leadData.product_id, salePropertyValue, 'Sale');
          let saleFeeData = null;

          if (saleCosts) {
            console.log('💰 Got conveyancing costs for Sale part:', saleCosts);

            let saleLegalFee = saleCosts.legal_fee || 0;
            let ttFee = saleCosts.tt_fee || 30;
            let identityCheck = saleCosts.identity_check || 10;
            let officialCopies = saleCosts.official_copies || 14;
            let mortgageAdminFee = 0;

            // Check if selling with mortgage
            if (leadData.salePropertyDetails?.info?.sellingWithMortgage) {
              mortgageAdminFee = saleCosts.mortgage_admin_fee || 150;
            }

            saleFeeData = {
              legal_fee: saleLegalFee,
              branch_name: branch.name,
              branch_id: branch._id,
              tt_fee: ttFee,
              identity_check: identityCheck,
              mortgage_admin_fee: mortgageAdminFee,
              official_copies: officialCopies,
              stamp_duty: 0,
              land_registry_search: 0,
              land_charges_search: 0,
              hm_land_registry_fee: saleCosts.hm_land_registry_fee || 0,
              search_pack: saleCosts.search_pack || 0,
              property_value: salePropertyValue,
              service_type: 'Sale'
            };
          }

          // Fetch costs for PURCHASE part
          const purchaseLegalFeeResponse = await postRequest(`private/getLegalFeeByPostcode`, {
            postcode: postcode,
            purchase_price: leadData.purchasePropertyDetails?.purchasePrice || 0,
            service_type: 'Purchase'
          }, authCtx.token);

          let purchaseFeeData = null;
          if (purchaseLegalFeeResponse?.status === 200 && purchaseLegalFeeResponse?.data) {
            const legalFeeApiData = purchaseLegalFeeResponse.data;
            console.log('💰 Got legal fee for Purchase part:', legalFeeApiData);

            purchaseFeeData = {
              legal_fee: Number(legalFeeApiData.legal_fee) || 0,
              branch_name: legalFeeApiData.branch_name,
              branch_id: legalFeeApiData.branch_id,
              tt_fee: legalFeeApiData.tt_fee || 30,
              identity_check: legalFeeApiData.identity_check || 10,
              stamp_duty: legalFeeApiData.stamp_duty || 0,
              land_registry_search: legalFeeApiData.land_registry_search || 0,
              land_charges_search: legalFeeApiData.land_charges_search || 0,
              hm_land_registry_fee: legalFeeApiData.hm_land_registry_fee || 0,
              search_pack: legalFeeApiData.search_pack || 0,
              property_value: leadData.purchasePropertyDetails?.purchasePrice || 0,
              service_type: 'Purchase',
              mortgage_admin_fee: 0,
              official_copies: 0
            };
          }

          // Transform to renderQuoteTable format and attach to leadData
          if (saleFeeData && purchaseFeeData) {
            const saleQuote = transformToQuoteFormat(saleFeeData, 'Sale');
            const purchaseQuote = transformToQuoteFormat(purchaseFeeData, 'Purchase');

            // Attach quotes to leadData for modal access
            const enrichedLeadData = {
              ...leadData,
              saleQuote,
              purchaseQuote
            };

            console.log('✅ Generated quotes for Purchase_Sale:', { saleQuote, purchaseQuote });
            setSelectedQuote({ legalFeeData: purchaseFeeData, leadData: enrichedLeadData });
            setShowQuoteDetailModal(true);
            return;
          }
        }

        // Fallback if costs couldn't be fetched
        console.log('⚠️ Could not obtain costs for Purchase_Sale; falling back to existing data');
        setSelectedQuote({ legalFeeData, leadData });
        setShowQuoteDetailModal(true);
        return;
      }

      // PURCHASE flow - use the new comprehensive API
      console.log('💰 Calling getQuoteCalculationByPostcode API for Purchase');
      const legalFeeResponse = await postRequest(`private/getQuoteCalculationByPostcode`, {
        postcode: postcode,
        purchase_price: leadData?.property_value || 0,
        service_type: leadData?.service_type || 'Purchase'
      }, authCtx.token);

      if (legalFeeResponse?.status === 200 && legalFeeResponse?.data) {
        const legalFeeApiData = legalFeeResponse.data;
        console.log('✅ Quote calculation API response:', legalFeeApiData);

        const purchaseFeeData = {
          legal_fee: Number(legalFeeApiData.legal_fee) || 0,
          branch_name: legalFeeApiData.branch_name,
          branch_id: legalFeeApiData.branch_id,
          postcode: legalFeeApiData.postcode,
          purchase_price: legalFeeApiData.purchase_price,
          property_range: legalFeeApiData.property_range,

          // Fixed Fees
          tt_fee: legalFeeApiData.tt_fee || 0,
          tt_fee_label: legalFeeApiData.tt_fee_label,

          // Disbursements
          identity_check: legalFeeApiData.identity_check || 0,
          identity_check_label: legalFeeApiData.identity_check_label,
          stamp_duty: legalFeeApiData.stamp_duty || 0,
          stamp_duty_label: legalFeeApiData.stamp_duty_label,
          land_registry_search: legalFeeApiData.land_registry_search || 0,
          land_registry_search_label: legalFeeApiData.land_registry_search_label,
          land_charges_search: legalFeeApiData.land_charges_search || 0,
          land_charges_search_label: legalFeeApiData.land_charges_search_label,
          hm_land_registry_fee: legalFeeApiData.hm_land_registry_fee || 0,
          hm_land_registry_fee_label: legalFeeApiData.hm_land_registry_fee_label,
          search_pack: legalFeeApiData.search_pack || 0,
          search_pack_label: legalFeeApiData.search_pack_label,

          // Conditional Purchase Fees
          help_to_buy_isa_fee: legalFeeApiData.help_to_buy_isa_fee || 0,
          help_to_buy_isa_fee_label: legalFeeApiData.help_to_buy_isa_fee_label,
          help_to_buy_fee: legalFeeApiData.help_to_buy_fee || 0,
          help_to_buy_fee_label: legalFeeApiData.help_to_buy_fee_label,
          right_to_buy_fee: legalFeeApiData.right_to_buy_fee || 0,
          right_to_buy_fee_label: legalFeeApiData.right_to_buy_fee_label,
          islamic_mortgage_fee: legalFeeApiData.islamic_mortgage_fee || 0,
          islamic_mortgage_fee_label: legalFeeApiData.islamic_mortgage_fee_label,
          gifted_deposit_fee: legalFeeApiData.gifted_deposit_fee || 0,
          gifted_deposit_fee_label: legalFeeApiData.gifted_deposit_fee_label,
          buy_to_let_fee: legalFeeApiData.buy_to_let_fee || 0,
          buy_to_let_fee_label: legalFeeApiData.buy_to_let_fee_label,
          shared_ownership_fee: legalFeeApiData.shared_ownership_fee || 0,
          shared_ownership_fee_label: legalFeeApiData.shared_ownership_fee_label,
          new_build_fee: legalFeeApiData.new_build_fee || 0,
          new_build_fee_label: legalFeeApiData.new_build_fee_label,
          auction_repossession_fee: legalFeeApiData.auction_repossession_fee || 0,
          auction_repossession_fee_label: legalFeeApiData.auction_repossession_fee_label,
          mortgage_admin_fee: legalFeeApiData.mortgage_admin_fee || 0,
          mortgage_admin_fee_label: legalFeeApiData.mortgage_admin_fee_label,

          // All fees arrays for fallback
          all_fixed_fees: legalFeeApiData.all_fixed_fees || [],
          all_disbursements: legalFeeApiData.all_disbursements || [],

          property_value: leadData?.property_value || 0,
          service_type: leadData?.service_type || 'Purchase'
        };

        console.log('💾 Setting purchaseFeeData:', purchaseFeeData);
        setLegalFeeData(purchaseFeeData);
        setSelectedQuote({ legalFeeData: purchaseFeeData, leadData });
        setShowQuoteDetailModal(true);
        return;
      }

      // If we reach here, fallback to existing data
      setSelectedQuote({ legalFeeData, leadData });
      setShowQuoteDetailModal(true);
    } catch (error) {
      console.error('Error fetching quote calculation:', error);
      // Fallback to original data
      setSelectedQuote({ legalFeeData, leadData });
      setShowQuoteDetailModal(true);
    }
  };

  // Function to fetch branches and match by postcode
  const fetchBranchByPostcode = async (postcode) => {
    if (!postcode) return null;
    
    console.log('🏢 Fetching branches for postcode:', postcode);
    
    try {
      const branchesResponse = await postRequest('private/getAllBranches', {}, authCtx.token);
      
      if (branchesResponse.status === 200 && branchesResponse.data) {
        console.log('📋 All branches:', branchesResponse.data);
        
        // Match branch by postcode (exact match first, then partial)
        const exactMatch = branchesResponse.data.find(branch => 
          branch.address?.postcode?.toLowerCase() === postcode.toLowerCase()
        );
        
        if (exactMatch) {
          console.log('✅ Exact postcode match found:', exactMatch);
          return exactMatch;
        }
        
        // Partial match - first 4 characters of postcode
        const postcodePrefix = postcode.replace(/\s/g, '').substring(0, 4).toLowerCase();
        const partialMatch = branchesResponse.data.find(branch => 
          branch.address?.postcode?.replace(/\s/g, '').substring(0, 4).toLowerCase() === postcodePrefix
        );
        
        if (partialMatch) {
          console.log('✅ Partial postcode match found:', partialMatch);
          return partialMatch;
        }
        
        console.log('❌ No postcode match found, using default branch');
        return branchesResponse.data[0] || null; // Default to first branch
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
    
    return null;
  };

  // Function to fetch conveyancing costs for a branch
  const fetchConveyancingCosts = async (branchId, productId, overridePropertyValue = null, overrideServiceType = null) => {
    if (!branchId || !productId) return null;

    console.log('💰 Fetching conveyancing costs for branch:', branchId, 'product:', productId);

    // Get property value and service type from leadData or use overrides
    const propertyValue = overridePropertyValue !== null
      ? overridePropertyValue
      : (leadData?.service_type === 'Sale'
        ? (leadData?.salePropertyDetails?.salePrice || leadData?.property_value)
        : leadData?.property_value);
    const serviceType = overrideServiceType || leadData?.service_type || 'Purchase';

    console.log('💰 Property Value:', propertyValue, 'Service Type:', serviceType);

    try {
      const costsResponse = await postRequest('private/getConveyancingCost', {
        branch_id: branchId,
        product_id: productId,
        property_value: propertyValue,
        service_type: serviceType
      }, authCtx.token);
      
      if (costsResponse.status === 'success' && costsResponse.data) {
        console.log('✅ Conveyancing costs found:', costsResponse);
        // Return the entire response to include calculated fees (legal_fee, tt_fee, etc.)
        return costsResponse;
      } else {
        console.log('❌ No conveyancing costs found for this branch');
      }
    } catch (err) {
      console.error('Error fetching conveyancing costs:', err);
    }
    
    return null;
  };

  // Function to calculate quote based on branch costs
  const calculateBranchBasedQuote = (propertyValue, serviceType, costs, options = {}) => {
    if (!costs || !costs.transactions) {
      console.log('❌ No cost data available for calculation');
      return null;
    }
    
    console.log('🧮 Calculating quote with branch costs:', {
      propertyValue,
      serviceType,
      costs: costs.transactions,
      options
    });
    
    // Find the cost breakdown for the service type.
    // Be tolerant: transaction.transactionType can be a string or an object.
    const normalizeTransactionType = (t) => {
      if (!t) return '';
      if (typeof t === 'string') return t;
      if (typeof t === 'object') return (t.name || t.label || t.type || t.transactionType || '') + '';
      return '';
    };

    let transaction = costs.transactions.find(t => {
      const txType = (normalizeTransactionType(t.transactionType) || '').toLowerCase();
      return txType === (serviceType || '').toLowerCase() || txType.includes((serviceType || '').toLowerCase());
    });

    // Fallbacks: try matching keys, or pick first transaction if nothing matches
    if (!transaction) {
      transaction = costs.transactions.find(t => {
        const txType = (normalizeTransactionType(t.transactionType) || '').toLowerCase();
        return txType.includes('sale') && (serviceType || '').toLowerCase() === 'sale' || txType.includes('purchase') && (serviceType || '').toLowerCase() === 'purchase';
      });
    }

    if (!transaction) {
      // As a last resort, pick the first available transaction and continue
      transaction = costs.transactions[0];
      console.log('⚠️ No exact transaction match found; using first transaction as fallback:', transaction ? normalizeTransactionType(transaction.transactionType) : 'none');
    }
    
    // Calculate based on property value ranges
    let baseFee = 575; // Default
    if (transaction.propertyValues) {
      const propertyRanges = Object.keys(transaction.propertyValues).sort((a, b) => {
        const aMax = parseInt(a.split('-')[1]?.replace(/[^\d]/g, '') || '999999999');
        const bMax = parseInt(b.split('-')[1]?.replace(/[^\d]/g, '') || '999999999');
        return aMax - bMax;
      });
      
      for (const range of propertyRanges) {
        const [min, max] = range.split('-').map(val => 
          parseInt(val.replace(/[^\d]/g, '')) || 0
        );
        
        if (propertyValue >= min && (!max || propertyValue <= max)) {
          baseFee = parseFloat(transaction.propertyValues[range]) || baseFee;
          break;
        }
      }
    }
    
    // For Sale, we don't include fixed fees in additional fees as they're extracted separately
    let additionalFees = 0;
    
    // Add option-based fees for Purchase
    if (serviceType?.toLowerCase() === 'purchase') {
      // Include fixed fees for Purchase
      if (transaction.fixedFees) {
        Object.values(transaction.fixedFees).forEach(fee => {
          additionalFees += parseFloat(fee) || 0;
        });
      }
      
      if (options.leasehold) additionalFees += 150;
      if (options.mortgage_lender) additionalFees += 150;
      if (options.help_to_buy) additionalFees += 150;
      if (options.shared_ownership) additionalFees += 150;
    }
    
    // Add option-based fees for Sale
    if (serviceType?.toLowerCase() === 'sale') {
      // For Sale, fixed fees (TT, Identity) are extracted separately in handleViewQuoteDetail
      // Only add option-based fees here
      if (options.mortgage_outstanding) {
        // Mortgage Admin Fee
        additionalFees += 150;
      }
      // Add other Sale-specific fees if needed
    }
    
    // Add disbursements
    let disbursements = 0;
    if (transaction.disbursements) {
      Object.values(transaction.disbursements).forEach(fee => {
        disbursements += parseFloat(fee) || 0;
      });
    }
    
    const subtotal = baseFee + additionalFees;
    const vat = Math.round(subtotal * 0.20 * 100) / 100;
    const total = Math.round((subtotal + vat + disbursements) * 100) / 100;
    
    const quote = {
      legal_fee: baseFee,
      additional_fees: additionalFees,
      disbursements,
      vat_amount: vat,
      total_quote: total,
      calculation_source: 'branch_costs'
    };
    
    console.log('✅ Calculated branch-based quote:', quote);
    return quote;
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `View Conveyancing (${leadData?.service_type === 'Sale' ? 'Sale' : leadData?.service_type?.replace(/_/g, ' ') || 'Purchase'}) Lead`, path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3 mb-3'>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className='mt-0 mb-0' style={{ fontSize: '1.5rem', fontWeight: '500' }}>
              Conveyancing ({leadData?.service_type === 'Sale' ? 'Sale' : leadData?.service_type?.replace(/_/g, ' ') || 'Purchase'}) 
              <span style={{ color: '#6c757d', marginLeft: '10px' }}>#{lead_id}</span>
            </h1>
          </div>
          <div className="d-flex gap-2">
            {/* Status Badge */}
            {leadData?.status && (
              <span className="badge" style={{ 
                backgroundColor: leadData.status === 'active' ? '#28a745' : '#6c757d', 
                color: 'white', 
                padding: '6px 12px', 
                fontSize: '0.875rem' 
              }}>
                {leadData.status.charAt(0).toUpperCase() + leadData.status.slice(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <div className='customer-tab-view leads-tab-view'>
          <Tabs defaultActiveKey="overview" id="uncontrolled-tab-example">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <div className='mt-3'>

                {/* ------------------------ Quote Box UI ------------------------ */}
                <div className="card shadow-sm mb-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <div className="card-header" style={{ 
                    backgroundColor: '#f8f9fa', 
                    borderBottom: '2px solid #e9ecef',
                    padding: '1rem 1.25rem'
                  }}>
                    <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                      Quote Summary
                    </h5>
                  </div>
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                  {(() => {
                    if (!leadData) {
                      return (
                        <div className="text-center py-3">
                          <div className="spinner-border text-primary spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 mb-0 text-muted">Loading lead data...</p>
                        </div>
                      );
                    }
                    
                    const propertyValue = leadData.property_value || 0;
                    
                    console.log('=== CONVEYANCING DISPLAY CALCULATION ===');
                    console.log('Property Value from lead:', propertyValue);
                    console.log('Branch info available:', !!branchInfo);
                    console.log('Conveyancing costs available:', !!conveyancingCosts);
                    
                    // Get service type
                    const serviceType = leadData.service_type || 'Purchase';
                    
                    let totalCost = 0;
                    let legalFee = 0;
                    let vatAmount = 0;
                    let dataSource = 'No data';

                    // IMPORTANT: Use total_quote from API (same as View Quote Detail popup)
                    // This ensures Quote Summary and popup show identical values
                    if (legalFeeData && legalFeeData.total_quote) {
                      console.log('📊 Using total from legalFeeData:', legalFeeData.total_quote);

                      // Simply use the total from the API - no recalculation needed
                      totalCost = legalFeeData.total_quote || 0;
                      dataSource = 'Legal Fee API Data';

                      console.log('✅ Using API total:', totalCost);
                    } else if (leadData?.legal_fee || leadData?.total_quote) {
                      // Use lead data if available
                      totalCost = leadData.total_quote || 0;
                      legalFee = leadData.legal_fee || 0;
                      vatAmount = leadData.vat_amount || 0;
                      dataSource = 'Lead Data';

                      console.log('⚠️ Using lead data:', {
                        totalCost, legalFee, vatAmount
                      });
                    } else {
                      // Default values
                      totalCost = 0;
                      legalFee = 0;
                      vatAmount = 0;
                      dataSource = 'No Data';
                    }
                    
                    console.log('Final conveyancing display values:', {
                      totalCost, legalFee, vatAmount, serviceType, dataSource
                    });
                    
                    // Determine service type badge color
                    const getServiceTypeColor = (serviceType) => {
                      switch(serviceType) {
                        case 'Purchase': return 'success';
                        case 'Sale': return 'warning';
                        case 'Purchase_Sale': return 'primary';
                        case 'Remortgage': return 'info';
                        case 'Transfer_of_Equity': return 'secondary';
                        default: return 'secondary';
                      }
                    };
                    
                    return (
                      <>
                        {calculationTotals?.total ? (
                          // Show Net + VAT = Gross breakdown when calculationTotals available
                          <div className="d-flex align-items-baseline gap-2">
                            <span className="text-muted small">Total Cost</span>
                            <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.1rem' }}>
                              £{Number(calculationTotals.total.net || 0).toFixed(2)} Net + £{Number(calculationTotals.total.vat || 0).toFixed(2)} VAT = £{Number(calculationTotals.total.gross || 0).toFixed(2)} Gross
                            </span>
                          </div>
                        ) : (
                          // Fallback to simple total display
                          <div className="d-flex align-items-baseline gap-3">
                            <span className="text-muted small">Total Cost (Legal + Disbursements)</span>
                            <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                              £{totalCost.toFixed(2)}
                            </span>
                          </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              console.log('🚀 View Quote Detail clicked from Quote Summary for', leadData?.service_type);
                              fetchQuoteFee();
                            }}
                            style={{
                              color: '#0d6efd',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            View Quote Detail
                          </a>
                          <div className="text-muted small">
                            {(() => {
                              const date = leadData?.quote_date || leadData?.createdAt || leadData?.created_at;
                              if (!date) return 'Date not available';
                              const dateObj = new Date(date);
                              return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                            })()}
                          </div>
                        </div>

                        <hr className="my-3" />

                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="badge bg-secondary me-2">
                              <i className="fas fa-building me-1"></i>
                              {branchInfo?.name || leadData?.branch_name || 'Branch'}
                            </span>
                            <span className="badge bg-info text-dark me-2">
                              <i className="fas fa-user me-1"></i>
                              {leadData?.created_by || localStorage.getItem('username') || 'User'}
                            </span>
                          </div>
                        </div>

                      </>
                    );
                  })()}

                  </div>
                  
                  {/* Action Buttons */}
                  <div className="card-footer bg-light" style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      {canReviseQuotes && leadData?.status !== 'Instructed' && (
                        <Button
                          variant="contained"
                          className='text-capitalize'
                          onClick={async () => {
                            // Fetch quote data first if not already available
                            if (!quoteFeeData && !legalFeeData) {
                              console.log('🔄 Fetching quote data for Revise Quote...');
                              await fetchQuoteFee(true);
                            }
                            setReviseModalShow(true);
                          }}
                          disabled={revisionCount >= 2 || loadingFee}
                          style={{
                            backgroundColor: revisionCount >= 2 ? '#6c757d' : '#dc3545',
                            borderColor: revisionCount >= 2 ? '#6c757d' : '#dc3545',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" style={{ marginRight: '6px' }} />
                          {loadingFee ? 'Loading...' : revisionCount >= 2 ? 'Revision Limit Reached' : 'Revise Quote'}
                        </Button>
                      )}
                      {leadData?.status !== 'Instructed' && (
                        <Button
                          variant="outlined"
                          className='text-capitalize'
                          onClick={handleRegenerateQuote}
                          style={{
                            color: '#6c757d',
                            borderColor: '#6c757d',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <RefreshIcon fontSize="small" style={{ marginRight: '6px' }} />
                          Regenerate Quote
                        </Button>
                      )}
                      {revisionCount > 0 && (
                        <Button 
                          variant="text" 
                          className='text-capitalize'
                          onClick={() => console.log("View Quote History")}
                          style={{ 
                            color: '#6c757d',
                            fontSize: '0.875rem',
                            padding: '8px 16px'
                          }}
                        >
                          <i className="fas fa-history me-1"></i>
                          View History ({revisionCount})
                        </Button>
                      )}
                    </div>
                  </div>
                </div>


                {/* Sale Details Section - for Purchase_Sale type */}
                {leadData?.service_type === 'Purchase_Sale' && (
                  <div className="card mb-3" style={{ border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
                    <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h5 className="mb-0" style={{ fontSize: '1.125rem', fontWeight: '500' }}>Sale Details</h5>
                      <a
                        href={`/leads/view/sale-purchase/${leadData?.lead_id || lead_id}`}
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Sale-Purchase →
                      </a>
                    </div>
                    <div className="card-body p-0">
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Vendors</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {(() => {
                                console.log('📺 DISPLAY - isLoadingVendors:', isLoadingVendors);
                                console.log('📺 DISPLAY - populatedVendors:', populatedVendors);
                                console.log('📺 DISPLAY - populatedVendors.length:', populatedVendors.length);
                                console.log('📺 DISPLAY - leadData.vendors:', leadData?.vendors);

                                // Check if vendors exist but not populated yet
                                const hasVendorIds = leadData?.vendors && Array.isArray(leadData.vendors) && leadData.vendors.length > 0;
                                const firstVendorIsId = hasVendorIds && typeof leadData.vendors[0] === 'string';

                                console.log('📺 DISPLAY - hasVendorIds:', hasVendorIds);
                                console.log('📺 DISPLAY - firstVendorIsId:', firstVendorIsId);

                                // If we have vendor IDs but they're not populated yet, show loading
                                if (firstVendorIsId && populatedVendors.length === 0) {
                                  console.log('⏳ DISPLAY - Vendors are IDs, waiting for population');
                                  return (
                                    <div className="text-muted">
                                      <small>Loading vendor details...</small>
                                    </div>
                                  );
                                }

                                // If still loading, show loading message
                                if (isLoadingVendors) {
                                  console.log('⏳ DISPLAY - Still loading vendors');
                                  return (
                                    <div className="text-muted">
                                      <small>Loading vendor details...</small>
                                    </div>
                                  );
                                }

                                // If loaded but no vendors
                                if (populatedVendors.length === 0) {
                                  console.log('⚠️ DISPLAY - No vendors found');
                                  return 'No vendors specified';
                                }

                                // Display populated vendors
                                console.log('✅ DISPLAY - Showing populated vendor data');
                                return populatedVendors.map((customer, index) => (
                                  <div key={customer._id || index} className="mb-1">
                                    <strong>{customer.title || ''} {customer.forename || ''} {customer.surname || ''}</strong>
                                    <br />
                                    <small className="text-muted">
                                      📧 {customer.email || 'N/A'} | 📞 {customer.phone_number || 'N/A'}
                                    </small>
                                  </div>
                                ));
                              })()}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Sale Address</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {(() => {
                                const address = leadData?.salePropertyDetails?.address;
                                if (!address) return 'No address specified';
                                if (typeof address === 'string') return address;

                                const parts = [];
                                if (address.address1) parts.push(address.address1);
                                if (address.address2) parts.push(address.address2);
                                if (address.town) parts.push(address.town);
                                if (address.country) parts.push(address.country);
                                if (address.postcode) parts.push(address.postcode);
                                return parts.length > 0 ? parts.join(', ') : 'No address specified';
                              })()}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Sale Price</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              £{(leadData?.salePropertyDetails?.salePrice || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Ownership Type</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {(() => {
                                const ownershipType = leadData?.salePropertyDetails?.ownershipType || 'freehold';
                                const isLeasehold = ownershipType.toLowerCase() === 'leasehold';
                                return (
                                  <span className={`badge ${isLeasehold ? 'bg-warning' : 'bg-success'}`}>
                                    {ownershipType.charAt(0).toUpperCase() + ownershipType.slice(1)}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Sale Info</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div className="d-flex flex-wrap gap-1">
                                {(() => {
                                  const info = [];
                                  const saleInfo = leadData?.salePropertyDetails?.info || leadData?.saleInfo || {};
                                  const conv = leadData?.conveyancing_details || {};

                                  // Sale-specific info fields (5 fields as per SalePurchaseDetails form)
                                  if (saleInfo.sellingWithMortgage === true || leadData?.selling_with_mortgage === true || conv.selling_with_mortgage === true) info.push('Selling with Mortgage');
                                  if (saleInfo.arrangingIndemnityPolicy === true || leadData?.arranging_indemnity_policy === true || conv.arranging_indemnity_policy === true) info.push('Arranging Indemnity Policy');
                                  if (saleInfo.unregisteredProperty === true || leadData?.unregistered_property === true || conv.unregistered_property === true) info.push('Unregistered Property');
                                  if (saleInfo.sharedOwnership === true || leadData?.shared_ownership === true || conv.shared_ownership === true) info.push('Shared Ownership');
                                  if (saleInfo.auctionRepossession === true || leadData?.auction_repossession === true || conv.auction_repossession === true) info.push('Auction/Repossession');

                                  if (info.length === 0) return <span className="text-muted">No additional sale info</span>;

                                  return info.map((item, index) => (
                                    <span key={index} className="badge bg-danger me-1">{item}</span>
                                  ));
                                })()}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Purchase/Sale Details Section */}
                <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
                  <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '0.75rem 1rem' }}>
                    <h5 className="mb-0" style={{ fontSize: '1.125rem', fontWeight: '500' }}>
                      {leadData?.service_type === 'Purchase_Sale' ? 'Purchase Details' : (leadData?.service_type === 'Sale' ? 'Sale Details' : (leadData?.service_type === 'Review_Legal_Pack' ? 'Review of Legal Pack Details' : (leadData?.service_type === 'First_Registration' ? 'First Registration Details' : 'Purchase Details')))}
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-borderless mb-0">
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Address</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              console.log('🏠 === ADDRESS DISPLAY DEBUG ===');
                              console.log('window.purchaseDetailsData:', window.purchaseDetailsData);
                              console.log('leadData:', leadData);
                              console.log('leadData.property_address:', leadData?.property_address);
                              console.log('leadData.address:', leadData?.address);

                              // For Purchase_Sale type, use purchasePropertyDetails
                              if (leadData?.service_type === 'Purchase_Sale') {
                                const address = leadData?.purchasePropertyDetails?.address;
                                if (!address) return 'No address specified';
                                if (typeof address === 'string') return address;

                                const parts = [];
                                if (address.address1) parts.push(address.address1);
                                if (address.address2) parts.push(address.address2);
                                if (address.town) parts.push(address.town);
                                if (address.country) parts.push(address.country);
                                if (address.postcode) parts.push(address.postcode);
                                return parts.length > 0 ? parts.join(', ') : 'No address specified';
                              }

                              // First check if we have data from Purchase Details API
                              if (window.purchaseDetailsData && window.purchaseDetailsData.address) {
                                console.log('Using window.purchaseDetailsData.address:', window.purchaseDetailsData.address);
                                return window.purchaseDetailsData.address;
                              }

                              // Fallback to existing logic
                              const address = leadData?.property_address || leadData?.address || leadData?.property || leadData?.conveyancingDetails?.propertyDetails?.address;
                              console.log('Address object found:', address);
                              
                              if (!address) {
                                console.log('No address object found!');
                                return 'No address specified';
                              }
                              
                              if (typeof address === 'string') {
                                console.log('Address is string:', address);
                                return address;
                              } else {
                                console.log('Address is object, building from fields:', address);
                                const parts = [];
                                // Check for address1/address2 format (what we save)
                                if (address.address1) parts.push(address.address1);
                                if (address.address2) parts.push(address.address2);
                                if (address.town) parts.push(address.town);
                                if (address.country) parts.push(address.country);
                                if (address.postcode) parts.push(address.postcode);
                                
                                // If no parts yet, check alternate field names
                                if (parts.length === 0) {
                                  console.log('No address1/address2 format found, checking alternate fields');
                                  if (address.house_flat_number) parts.push(address.house_flat_number);
                                  if (address.line1) parts.push(address.line1);
                                  if (address.street_name) parts.push(address.street_name);
                                  if (address.line2) parts.push(address.line2);
                                  if (address.town || address.city || address.town_city) parts.push(address.town || address.city || address.town_city);
                                  if (address.county || address.state) parts.push(address.county || address.state);
                                  if (address.postcode || address.postal_code || address.post_code) parts.push(address.postcode || address.postal_code || address.post_code);
                                }
                                
                                console.log('Address parts:', parts);
                                return parts.length > 0 ? parts.join(', ') : 'No address specified';
                              }
                            })()}
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>{leadData?.service_type === 'Sale' ? 'Vendors' : 'Purchasers'}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              console.log('📺 DISPLAY - isLoadingPurchasers:', isLoadingPurchasers);
                              console.log('📺 DISPLAY - populatedPurchasers:', populatedPurchasers);
                              console.log('📺 DISPLAY - populatedPurchasers.length:', populatedPurchasers.length);
                              console.log('📺 DISPLAY - leadData.purchasers:', leadData?.purchasers);

                              // First check if we have data from Purchase Details API
                              if (window.purchaseDetailsData && window.purchaseDetailsData.purchasers) {
                                const purchasers = window.purchaseDetailsData.purchasers;
                                if (Array.isArray(purchasers) && purchasers.length > 0) {
                                  return purchasers.map((customer, index) => (
                                    <div key={customer._id || index} className="mb-1">
                                      <strong>{customer.title} {customer.forename} {customer.surname}</strong>
                                      <br />
                                      <small className="text-muted">
                                        📧 {customer.email} | 📞 {customer.phone_number}
                                      </small>
                                    </div>
                                  ));
                                }
                              }

                              // Check if purchasers exist but not populated yet
                              console.log('🔍 ====== PURCHASERS DISPLAY LOGIC ======');
                              console.log('📊 leadData:', leadData);
                              console.log('📊 leadData.purchasers:', leadData?.purchasers);
                              console.log('📊 populatedPurchasers:', populatedPurchasers);
                              console.log('📊 isLoadingPurchasers:', isLoadingPurchasers);

                              const hasPurchaserIds = leadData?.purchasers && Array.isArray(leadData.purchasers) && leadData.purchasers.length > 0;
                              const firstPurchaserIsId = hasPurchaserIds && typeof leadData.purchasers[0] === 'string';

                              console.log('📺 DISPLAY - hasPurchaserIds:', hasPurchaserIds);
                              console.log('📺 DISPLAY - leadData.purchasers type:', typeof leadData?.purchasers);
                              console.log('📺 DISPLAY - leadData.purchasers length:', leadData?.purchasers?.length);
                              console.log('📺 DISPLAY - firstPurchaserIsId:', firstPurchaserIsId);
                              console.log('📺 DISPLAY - First purchaser:', leadData?.purchasers?.[0]);

                              // If we have purchaser IDs but they're not populated yet, show loading
                              if (firstPurchaserIsId && populatedPurchasers.length === 0) {
                                console.log('⏳ DISPLAY - Purchasers are IDs, waiting for population');
                                return (
                                  <div className="text-muted">
                                    <small>Loading purchaser details...</small>
                                  </div>
                                );
                              }

                              // If still loading, show loading message
                              if (isLoadingPurchasers) {
                                console.log('⏳ DISPLAY - Still loading purchasers');
                                return (
                                  <div className="text-muted">
                                    <small>Loading purchaser details...</small>
                                  </div>
                                );
                              }

                              // If loaded but no purchasers
                              if (populatedPurchasers.length === 0) {
                                // Check for fallback customer data
                                if (leadData?.customer_name) {
                                  return leadData.customer_name;
                                } else if (leadData?.customer_names) {
                                  return leadData.customer_names;
                                } else if (leadData?.customers && Array.isArray(leadData.customers) && leadData.customers.length > 0) {
                                  // Display customers array
                                  return leadData.customers.map((customer, index) => (
                                    <div key={customer._id || index} className="mb-1">
                                      <strong>{customer.title || ''} {customer.forename || ''} {customer.surname || ''}</strong>
                                      <br />
                                      <small className="text-muted">
                                        📧 {customer.email || 'N/A'} | 📞 {customer.phone_number || 'N/A'}
                                      </small>
                                    </div>
                                  ));
                                } else {
                                  return 'No purchasers specified';
                                }
                              }

                              // Display populated purchasers
                              console.log('✅ DISPLAY - Showing populated purchaser data');
                              return populatedPurchasers.map((customer, index) => (
                                <div key={customer._id || index} className="mb-1">
                                  <strong>{customer.title || ''} {customer.forename || ''} {customer.surname || ''}</strong>
                                  <br />
                                  <small className="text-muted">
                                    📧 {customer.email || 'N/A'} | 📞 {customer.phone_number || 'N/A'}
                                  </small>
                                </div>
                              ));
                            })()}
                          </td>
                        </tr>
                        {leadData?.service_type !== 'Review_Legal_Pack' && leadData?.service_type !== 'First_Registration' && (
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>{leadData?.service_type === 'Sale' ? 'Sale Price' : 'Purchase Price'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              £{(() => {
                                // For Purchase_Sale type, use purchasePropertyDetails
                                if (leadData?.service_type === 'Purchase_Sale') {
                                  return (leadData?.purchasePropertyDetails?.purchasePrice || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }

                                // First check if we have data from Purchase Details API
                                if (window.purchaseDetailsData && window.purchaseDetailsData.purchasePrice !== undefined) {
                                  return window.purchaseDetailsData.purchasePrice.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                }
                                // Fallback to existing logic
                                return (leadData?.property_value || leadData?.net_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </td>
                          </tr>
                        )}
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Ownership Type</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              // For Purchase_Sale type, use purchasePropertyDetails
                              if (leadData?.service_type === 'Purchase_Sale') {
                                const ownershipType = leadData?.purchasePropertyDetails?.ownershipType || 'freehold';
                                const isLeasehold = ownershipType.toLowerCase() === 'leasehold';
                                return (
                                  <span className={`badge ${isLeasehold ? 'bg-warning' : 'bg-success'}`}>
                                    {ownershipType.charAt(0).toUpperCase() + ownershipType.slice(1)}
                                  </span>
                                );
                              }

                              // First check if we have data from Purchase Details API
                              if (window.purchaseDetailsData && window.purchaseDetailsData.ownershipType) {
                                const ownershipType = window.purchaseDetailsData.ownershipType;
                                const isLeasehold = ownershipType.toLowerCase() === 'leasehold';
                                return (
                                  <span className={`badge ${isLeasehold ? 'bg-warning' : 'bg-success'}`}>
                                    {ownershipType}
                                  </span>
                                );
                              }
                              // Fallback to existing logic
                              return (
                                <span className={`badge ${leadData?.leasehold ? 'bg-warning' : 'bg-success'}`}>
                                  {leadData?.ownership_type || (leadData?.leasehold ? 'Leasehold' : 'Freehold')}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                        {leadData?.service_type !== 'Sale' && leadData?.service_type !== 'Review_Legal_Pack' && leadData?.service_type !== 'First_Registration' && (
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Est. Completion Date</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              {(() => {
                                // For Purchase_Sale type, use purchasePropertyDetails
                                if (leadData?.service_type === 'Purchase_Sale') {
                                  return leadData?.purchasePropertyDetails?.completionDate
                                    ? new Date(leadData.purchasePropertyDetails.completionDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })
                                    : 'Not specified';
                                }

                                // First check if we have data from Purchase Details API
                                if (window.purchaseDetailsData && window.purchaseDetailsData.completionDate) {
                                  return new Date(window.purchaseDetailsData.completionDate).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  });
                                }
                                // Fallback to existing logic
                                return leadData?.completion_date
                                  ? new Date(leadData.completion_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                  : 'Not specified';
                              })()}
                            </td>
                          </tr>
                        )}

                        {/* Lender Panel */}
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Lender Panel</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              console.log('🔍 FULL LEAD DATA:', {
                                lender_panel_id: leadData?.lender_panel_id,
                                lender_panel_name: leadData?.lender_panel_name,
                                type: typeof leadData?.lender_panel_id
                              });

                              if (leadData?.lender_panel_id) {
                                console.log('✅ Lender Panel Object:', JSON.stringify(leadData.lender_panel_id, null, 2));
                              }

                              console.log('📊 Checking conditions:', {
                                'has lender_panel_name': !!leadData?.lender_panel_name,
                                'is object': typeof leadData?.lender_panel_id === 'object',
                                'has name': !!leadData?.lender_panel_id?.name,
                                'has lender_numbers': !!leadData?.lender_panel_id?.lender_numbers,
                                'lender_numbers length': leadData?.lender_panel_id?.lender_numbers?.length
                              });

                              // Check for lender_panel_name field first (from backend aggregation)
                              if (leadData?.lender_panel_name) {
                                return (
                                  <div>
                                    <div style={{ color: '#495057' }}>
                                      {leadData.lender_panel_name}
                                    </div>
                                    {leadData?.lender_panel_id?.lender_numbers && leadData.lender_panel_id.lender_numbers.length > 0 && (
                                      <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                        {leadData.lender_panel_id.lender_numbers.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              const lenderPanel = leadData?.lender_panel_id;

                              // If lender panel is populated object
                              if (lenderPanel && typeof lenderPanel === 'object' && lenderPanel.name) {
                                console.log('🎯 RENDERING LENDER PANEL:', {
                                  name: lenderPanel.name,
                                  has_lender_numbers: !!lenderPanel?.lender_numbers,
                                  lender_numbers: lenderPanel?.lender_numbers,
                                  length: lenderPanel?.lender_numbers?.length
                                });

                                return (
                                  <div>
                                    <div style={{ color: '#495057' }}>
                                      {lenderPanel.name}
                                    </div>
                                    {lenderPanel?.lender_numbers && lenderPanel.lender_numbers.length > 0 ? (
                                      <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                        {lenderPanel.lender_numbers.join(', ')}
                                      </div>
                                    ) : (
                                      <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        DEBUG: No lender numbers - {JSON.stringify(lenderPanel?.lender_numbers)}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              // If no lender panel selected
                              return <span className="text-muted">Not specified</span>;
                            })()}
                          </td>
                        </tr>

                        {/* Partner */}
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Partner</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              console.log('🔍 Partner display check:', {
                                partner_id: leadData?.partner_id,
                                partner_name: leadData?.partner_name,
                                type: typeof leadData?.partner_id
                              });

                              // Check for partner_name field first (from backend aggregation)
                              if (leadData?.partner_name) {
                                return leadData.partner_name;
                              }

                              // If partner_id is populated object with name
                              if (leadData?.partner_id && typeof leadData.partner_id === 'object' && leadData.partner_id.name) {
                                return leadData.partner_id.name;
                              }

                              return <span className="text-muted">Not assigned</span>;
                            })()}
                          </td>
                        </tr>

                        {/* Sale-specific fields - removed Estate Agent, Mortgage Outstanding, Chain Status, Onward Purchase */}

                        {leadData?.service_type !== 'First_Registration' && (
                          <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Purchase Info</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div className="d-flex flex-wrap gap-1">
                                {(() => {
                                  // For Purchase_Sale type, use purchasePropertyDetails.info
                                  const info = [];
                                  const purchaseInfo = leadData?.purchasePropertyDetails?.info || leadData?.purchaseInfo || {};
                                  const conv = leadData?.conveyancing_details || {};
                                  const infoObj = leadData?.info || {};

                                  // Purchase-specific info fields (All 18 fields)
                                  if (purchaseInfo.firstTimeBuyer === true || leadData?.first_time_buyer === true || conv.first_time_buyer === true || infoObj.firstTimeBuyer === true) info.push('First Time Buyer');
                                  if (purchaseInfo.helpToBuyISA === true || leadData?.help_to_buy_isa === true || conv.help_to_buy_isa === true || infoObj.helpToBuyISA === true) info.push('Help to Buy ISA');
                                  if (purchaseInfo.helpToBuy === true || leadData?.help_to_buy === true || conv.help_to_buy === true || infoObj.helpToBuy === true) info.push('Help to Buy');
                                  if (purchaseInfo.helpToBuyEquityLoan === true || leadData?.help_to_buy_equity_loan === true || conv.help_to_buy_equity_loan === true || infoObj.helpToBuyEquityLoan === true) info.push('Help to Buy Equity Loan');
                                  if (purchaseInfo.rightToBuy === true || leadData?.right_to_buy === true || conv.right_to_buy === true || infoObj.rightToBuy === true) info.push('Right to Buy');
                                  if (purchaseInfo.sharedOwnership === true || leadData?.shared_ownership === true || conv.shared_ownership === true || infoObj.sharedOwnership === true) info.push('Shared Ownership');
                                  if (purchaseInfo.buyingWithMortgage === true || leadData?.buying_with_mortgage === true || conv.buying_with_mortgage === true || infoObj.buyingWithMortgage === true) info.push('Buying with Mortgage');
                                  if (purchaseInfo.newBuild === true || leadData?.new_build === true || conv.new_build === true || infoObj.newBuild === true) info.push('New Build');
                                  if (purchaseInfo.islamicMortgage === true || leadData?.islamic_mortgage === true || conv.islamic_mortgage === true || infoObj.islamicMortgage === true) info.push('Islamic Mortgage');
                                  if (purchaseInfo.auctionRepossession === true || leadData?.auction_repossession === true || conv.auction_repossession === true || infoObj.auctionRepossession === true) info.push('Auction / Repossession');
                                  if (purchaseInfo.giftedDeposit === true || leadData?.gifted_deposit === true || conv.gifted_deposit === true || infoObj.giftedDeposit === true) info.push('Gifted Deposit');
                                  if (purchaseInfo.multipleProperties === true || leadData?.multiple_properties === true || conv.multiple_properties === true || infoObj.multipleProperties === true) info.push('Multiple Properties');
                                  if (purchaseInfo.buyToLet === true || leadData?.buy_to_let === true || conv.buy_to_let === true || infoObj.buyToLet === true) info.push('Buy to Let');
                                  if (purchaseInfo.arrangingIndemnityPolicy === true || leadData?.arranging_indemnity_policy === true || conv.arranging_indemnity_policy === true || infoObj.arrangingIndemnityPolicy === true) info.push('Arranging an Indemnity Policy');
                                  if (purchaseInfo.unregisteredProperty === true || leadData?.unregistered_property === true || conv.unregistered_property === true || infoObj.unregisteredProperty === true) info.push('Unregistered Property');
                                  if (purchaseInfo.staircasing === true || leadData?.staircasing === true || conv.staircasing === true || infoObj.staircasing === true) info.push('Staircasing');
                                  if (purchaseInfo.nonResidentialMixed === true || leadData?.non_residential_mixed === true || conv.non_residential_mixed === true || infoObj.nonResidentialMixed === true) info.push('Non-Residential / Mixed Use');

                                  // Non-UK Resident field (dropdown)
                                  const isNonUK = purchaseInfo.isNonUKResident || leadData?.is_non_uk_resident || conv.is_non_uk_resident || infoObj.isNonUKResident;
                                  if (isNonUK === 'Yes' || isNonUK === true) info.push('Non-UK Resident');

                                  if (info.length === 0) return <span className="text-muted">No additional purchase info</span>;

                                  return info.map((item, index) => (
                                    <span key={index} className="badge bg-primary me-1">{item}</span>
                                  ));
                                })()}
                              </div>
                            </td>
                          </tr>
                        )}
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Group Code</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              // First check if we have data from Purchase Details API
                              if (window.purchaseDetailsData && window.purchaseDetailsData.groupCode) {
                                return window.purchaseDetailsData.groupCode.name || window.purchaseDetailsData.groupCode;
                              }
                              // Fallback to existing logic
                              return leadData?.group_code?.name || leadData?.group_code || 'Not specified';
                            })()}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: '30%', padding: '0.75rem 1rem', fontWeight: '500' }}>Acquisition</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            {(() => {
                              // First check if we have data from Purchase Details API
                              if (window.purchaseDetailsData && window.purchaseDetailsData.acquisition) {
                                return window.purchaseDetailsData.acquisition;
                              }
                              // Fallback to existing logic
                              return leadData?.acquisition || 'Not specified';
                            })()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Branch Information Section - Removed as per user request */}
                
                {/* Edit Button */}
                {canEditLeads && (
                  <div className="mt-3">
                    <Button
                      variant="outlined"
                      className='text-capitalize'
                      onClick={() => {
                        console.log('Edit button clicked - Service Type:', leadData?.service_type);
                        let editPath;

                        // Get first customer ID if available
                        const firstCustomerId = leadData?.customers && leadData.customers.length > 0
                          ? (leadData.customers[0]._id || leadData.customers[0].customer_id || leadData.customers[0])
                          : null;
                        const customerParam = firstCustomerId ? `?customerId=${firstCustomerId}` : '';

                        if (leadData?.service_type === 'Sale') {
                          editPath = `/leads/edit/conveyancing-sale/${leadData?.lead_id || lead_id}`;
                        } else if (leadData?.service_type === 'Purchase') {
                          editPath = `/leads/edit/conveyancing-purchase/${leadData?.lead_id || lead_id}`;
                        } else if (leadData?.service_type === 'Purchase_Sale') {
                          editPath = `/conveyancing/sale-purchase/${leadData?.lead_id || lead_id}${customerParam}`;
                        } else if (leadData?.service_type === 'First_Registration') {
                          editPath = `/conveyancing/first-registration/${leadData?.lead_id || lead_id}${customerParam}`;
                        } else {
                          editPath = `/leads/edit/conveyancing/${leadData?.lead_id || lead_id}`;
                        }
                        console.log('Navigating to:', editPath);
                        navigate(editPath);
                    }}
                    style={{ 
                      color: '#6c757d',
                      borderColor: '#6c757d',
                      fontSize: '0.875rem'
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" className="me-1" />
                    Edit
                  </Button>
                  </div>
                )}
                
                {/* Additional content or creation info */}
                <div className="mt-3 text-end">
                  <small className="text-muted">
                    Created: {leadData?.createdAt ? new Date(leadData.createdAt).toLocaleDateString('en-GB') : 'Date not available'} - {leadData?.created_by || localStorage.getItem('username') || 'Unknown'}
                  </small>
                  <div className="mt-1">
                    <Dropdown>
                      <Dropdown.Toggle 
                        variant="outlined" 
                        size="small"
                        className='text-capitalize'
                        style={{ 
                          color: '#6c757d',
                          borderColor: '#6c757d',
                          fontSize: '0.75rem',
                          backgroundColor: 'transparent'
                        }}
                      >
                        More Actions
                      </Dropdown.Toggle>
                      
                      <Dropdown.Menu>
                        <Dropdown.Item 
                          onClick={() => {
                            navigate('/leads/changestatus', {
                              state: { 
                                leadId: lead_id,
                                currentStatus: leadData?.status,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Status
                        </Dropdown.Item>
                        
                        {canChangeGroupCode && (
                          <Dropdown.Item 
                            onClick={() => {
                              navigate('/leads/changegroupcode', {
                                state: { 
                                  leadId: lead_id,
                                  currentGroupCode: leadData?.group_code,
                                  returnUrl: window.location.pathname
                                }
                              });
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                            Change Group Code
                          </Dropdown.Item>
                        )}
                        
                        {(canDeleteLeads || canChangeGroupCode) && <Dropdown.Divider />}
                        
                        {canDeleteLeads && (
                          <Dropdown.Item 
                            onClick={() => handleDeleteLead()}
                            style={{ color: '#dc3545' }}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                            Delete Lead
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </Tab>
            
            {/* QUOTES TAB */}
            <Tab eventKey="quotes" title="Quotes">
              <div className="mt-3">
                {/* Current Quote Card */}
                {leadData && legalFeeData && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-1" style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                            {(() => {
                              // Calculate current total - EXACT MATCH with ViewQuoteDetailModal & Quote Summary
                              const legalFee = Number(legalFeeData.legal_fee || 0);
                              const ttFee = Number(legalFeeData.tt_fee || 0);

                              // Get purchaser count from leadData
                              const purchaserCount = leadData?.customer_ids?.length || leadData?.customers?.length || 1;

                              // Calculate Identity Check - multiply by purchaser count (FIXED: was missing this multiplication)
                              const identityCheckBase = Number(legalFeeData.identity_check || 0);
                              const identityCheck = identityCheckBase * purchaserCount;

                              console.log('🔍 QUOTES TAB CALCULATION DEBUG:');
                              console.log('  - legalFeeData from API:', legalFeeData);
                              console.log('  - Legal Fee:', legalFee);
                              console.log('  - TT Fee:', ttFee);
                              console.log('  - Identity Check Base:', identityCheckBase);
                              console.log('  - Purchaser Count:', purchaserCount);
                              console.log('  - Identity Check Total:', identityCheck);

                              // Check conditions for conditional fees - EXACT MATCH with ViewQuoteDetailModal
                              const purchaseInfo = leadData?.purchase_details_info || leadData?.purchasePropertyDetails;
                              const saleInfo = leadData?.sale_details_info || leadData?.salePropertyDetails;
                              const infoObj = leadData?.info || {};
                              const conveyancingDetails = leadData?.conveyancing_details || leadData?.conveyancing || {};

                              // Disbursements (no VAT on these)
                              const stampDuty = Number(legalFeeData.stamp_duty || 0);
                              const hmLandRegistryFee = Number(legalFeeData.hm_land_registry_fee || 0);
                              const landRegistrySearch = Number(legalFeeData.land_registry_search || 0);
                              const landChargesSearch = Number(legalFeeData.land_charges_search || 0);
                              const searchPack = Number(legalFeeData.search_pack || 0);
                              const officialCopies = legalFeeData?.official_copies || (leadData?.service_type === 'Sale' ? 14 : 0);

                              // Conditional fees - calculated EXACTLY as in ViewQuoteDetailModal
                              const showHelpToBuyISAFee = purchaseInfo?.help_to_buy_isa || leadData?.help_to_buy_isa || infoObj?.helpToBuyISA || conveyancingDetails?.help_to_buy_isa || false;
                              const helpToBuyISAFee = showHelpToBuyISAFee
                                ? (Number(legalFeeData?.help_to_buy_isa_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'helpToBuyISAFee')?.amount || 0))
                                : 0;

                              const showHelpToBuyFee = purchaseInfo?.help_to_buy || leadData?.help_to_buy || infoObj?.helpToBuy || conveyancingDetails?.help_to_buy || false;
                              const helpToBuyFee = showHelpToBuyFee
                                ? (Number(legalFeeData?.help_to_buy_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'helpToBuyFee')?.amount || 0))
                                : 0;

                              const showRightToBuyFee = purchaseInfo?.right_to_buy || leadData?.right_to_buy || infoObj?.rightToBuy || conveyancingDetails?.right_to_buy || false;
                              const rightToBuyFee = showRightToBuyFee
                                ? (Number(legalFeeData?.right_to_buy_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'rightToBuyFee')?.amount || 0))
                                : 0;

                              const showIslamicMortgageFee = purchaseInfo?.islamic_mortgage || leadData?.islamic_mortgage || infoObj?.islamicMortgage || conveyancingDetails?.islamic_mortgage || false;
                              const islamicMortgageFee = showIslamicMortgageFee
                                ? (Number(legalFeeData?.islamic_mortgage_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'islamicMortgageFee')?.amount || 0))
                                : 0;

                              const giftedDepositCount = Number(purchaseInfo?.gifted_deposit || leadData?.gifted_deposit || infoObj?.giftedDeposit || conveyancingDetails?.gifted_deposit) || 0;
                              let giftedDepositFee = 0;
                              if (giftedDepositCount > 0) {
                                const firstGiftorFee = Number(legalFeeData?.gifted_deposit_fee_first_giftor || 0);
                                giftedDepositFee = firstGiftorFee;
                                if (giftedDepositCount > 1) {
                                  const additionalGiftorFee = Number(legalFeeData?.gifted_deposit_fee_additional_giftors || 0);
                                  giftedDepositFee += additionalGiftorFee * (giftedDepositCount - 1);
                                }
                              }

                              const showBuyToLetFee = purchaseInfo?.buy_to_let || leadData?.buy_to_let || infoObj?.buyToLet || conveyancingDetails?.buy_to_let || false;
                              const buyToLetFee = showBuyToLetFee
                                ? (Number(legalFeeData?.buy_to_let_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'buyToLetFee')?.amount || 0))
                                : 0;

                              const showSharedOwnershipFee = purchaseInfo?.shared_ownership || leadData?.shared_ownership || infoObj?.sharedOwnership || conveyancingDetails?.shared_ownership || false;
                              const sharedOwnershipFee = showSharedOwnershipFee
                                ? (Number(legalFeeData?.shared_ownership_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'sharedOwnershipFee')?.amount || 0))
                                : 0;

                              const showNewBuildFee = purchaseInfo?.new_build || leadData?.new_build || infoObj?.newBuild || conveyancingDetails?.new_build || false;
                              const newBuildFee = showNewBuildFee
                                ? (Number(legalFeeData?.new_build_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'newBuildFee')?.amount || 0))
                                : 0;

                              const showAuctionRepossessionFee = (leadData?.service_type === 'Sale') || saleInfo?.auction_repossession || purchaseInfo?.auction_repossession || leadData?.auction_repossession || infoObj?.auctionRepossession || conveyancingDetails?.auction_repossession || leadData?.info?.auctionRepossession || false;
                              const auctionRepossessionFee = showAuctionRepossessionFee
                                ? (Number(legalFeeData?.auction_repossession_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'auctionRepossessionFee')?.amount || 0))
                                : 0;

                              const arrangingIndemnityPolicy = Number(legalFeeData?.arranging_indemnity_policy || 0);

                              // Mortgage Admin Fee - for both Sale (if mortgage_outstanding) and Purchase (if buying_with_mortgage)
                              const showMortgageAdminFeeForPurchase = purchaseInfo?.buying_with_mortgage || leadData?.buying_with_mortgage || infoObj?.buyingWithMortgage || conveyancingDetails?.buying_with_mortgage || !!leadData?.mortgage_lender || false;
                              const showMortgageAdminFee = (leadData?.service_type === 'Sale' && (leadData?.mortgage_outstanding || saleInfo?.sellingWithMortgage || leadData?.info?.sellingWithMortgage)) ||
                                                            (leadData?.service_type === 'Purchase' && showMortgageAdminFeeForPurchase);
                              const mortgageAdminFee = showMortgageAdminFee
                                ? (Number(legalFeeData?.mortgage_admin_fee) || Number(legalFeeData?.all_fixed_fees?.find(fee => fee.label === 'mortgageAdminFee')?.amount || 0))
                                : 0;

                              // Leasehold Fee - show if ownership type is Leasehold
                              const isLeasehold = leadData?.ownership_type === 'Leasehold' || leadData?.leasehold === true;
                              const leaseholdFee = isLeasehold ? Number(legalFeeData?.leasehold_fee || 0) : 0;

                              // Use totals from database if available (quoteFeeData.totals.total.gross), otherwise use stored total_quote
                              const totalQuote = quoteFeeData?.totals?.total?.gross || legalFeeData?.total_quote || leadData?.total_quote || 0;

                              // Return JSX with just the total amount
                              return (
                                <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>£{Number(totalQuote).toFixed(2)}</span>
                              );
                            })()}
                          </h5>
                          <p className="text-muted mb-0">
                            {(() => {
                              // Use quote_date (when quote was generated) instead of updated_at
                              const quoteDate = legalFeeData?.created_at || leadData?.quote_date || leadData?.updated_at || leadData?.created_at || new Date();
                              return new Date(quoteDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(quoteDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                            })()}
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {legalFeeData?.branch_name ? (
                            <span className="badge bg-secondary">{legalFeeData.branch_name}</span>
                          ) : (
                            <span className="badge bg-secondary">Partner rate card</span>
                          )}
                          <span className="badge bg-primary">{localStorage.getItem('username') || leadData?.created_by || 'User'}</span>
                          <Button
                            variant="link"
                            className="text-decoration-none p-0"
                            onClick={() => {
                              console.log('🔴 View Quote Detail button clicked for', leadData?.service_type);
                              fetchQuoteFee();
                            }}
                          >
                            View Quote Detail
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Quote History Section - Enhanced UI */}
                {quoteHistory && quoteHistory.length > 0 && (
                  <div className="quote-history-section">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                        <i className="fas fa-history me-2" style={{ color: '#3498db' }}></i>
                        Quote History
                      </h5>
                      <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                        {quoteHistory.length} {quoteHistory.length === 1 ? 'Version' : 'Versions'}
                      </span>
                    </div>

                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                      {/* Timeline line */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '20px',
                        bottom: '20px',
                        width: '2px',
                        backgroundColor: '#e0e0e0'
                      }}></div>

                      {quoteHistory.map((quote, index) => {
                        // 🎯 Handle Purchase_Sale totals (combine purchase + sale)
                        let totalGross, totalNet, totalVat, legalCostGross;
                        if (quote.purchase_breakdown && quote.sale_breakdown) {
                          // Purchase_Sale: Combine both
                          totalGross = (quote.purchase_breakdown.totals?.total?.gross || 0) + (quote.sale_breakdown.totals?.total?.gross || 0);
                          totalNet = (quote.purchase_breakdown.totals?.total?.net || 0) + (quote.sale_breakdown.totals?.total?.net || 0);
                          totalVat = (quote.purchase_breakdown.totals?.total?.vat || 0) + (quote.sale_breakdown.totals?.total?.vat || 0);
                          legalCostGross = (quote.purchase_breakdown.totals?.legalCost?.gross || 0) + (quote.sale_breakdown.totals?.legalCost?.gross || 0);
                        } else {
                          // Single service type
                          totalGross = quote.totals?.total?.gross || 0;
                          totalNet = quote.totals?.total?.net || 0;
                          totalVat = quote.totals?.total?.vat || 0;
                          legalCostGross = quote.totals?.legalCost?.gross || 0;
                        }
                        const isActive = quote.is_active || false;
                        const revisionNumber = quote.revision_number || 1;
                        const source = quote.calculation_source || 'api';

                        // ✅ Use revision_label directly from API
                        const versionLabel = quote.revision_label || `Revision ${revisionNumber}`;

                        // Format date
                        const displayDate = quote.revised_at || quote.createdAt;

                        // Icon and color based on type
                        let iconClass = 'fa-file-invoice';
                        let iconColor = '#95a5a6';
                        let borderColor = '#e0e0e0';

                        if (isActive) {
                          iconClass = 'fa-check-circle';
                          iconColor = '#27ae60';
                          borderColor = '#27ae60';
                        } else if (versionLabel === 'Original') {
                          iconClass = 'fa-star';
                          iconColor = '#f39c12';
                        } else if (source === 'manual_revision') {
                          iconClass = 'fa-edit';
                          iconColor = '#3498db';
                        }

                        return (
                          <div key={quote._id || index} style={{ position: 'relative', marginBottom: '24px' }}>
                            {/* Timeline dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-26px',
                              top: '16px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: iconColor,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px ' + iconColor,
                              zIndex: 1
                            }}></div>

                            {/* Card */}
                            <div className="card" style={{
                              border: `2px solid ${isActive ? borderColor : '#e9ecef'}`,
                              borderRadius: '12px',
                              boxShadow: isActive ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease',
                              backgroundColor: isActive ? '#f8fff9' : 'white'
                            }}>
                              <div className="card-body" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  {/* Left: Version & Amount */}
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '18px' }}></i>
                                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                                        {versionLabel}
                                      </h5>
                                      {isActive && (
                                        <span className="badge" style={{
                                          backgroundColor: '#27ae60',
                                          color: 'white',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          CURRENT
                                        </span>
                                      )}
                                    </div>

                                    {/* Date & Time */}
                                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="far fa-clock me-1"></i>
                                      {new Date(displayDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })} at {new Date(displayDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>

                                    {/* Source Badge */}
                                    <span className="badge" style={{
                                      backgroundColor: source === 'manual_revision' ? '#3498db' : '#95a5a6',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem'
                                    }}>
                                      {source === 'manual_revision' ? 'MANUAL REVISION' :
                                       source === 'api' ? 'AUTO CALCULATED' :
                                       source.toUpperCase()}
                                    </span>
                                  </div>

                                  {/* Right: Branch & Action */}
                                  <div className="d-flex flex-column align-items-end gap-2">
                                    {quote.branch_name && (
                                      <span className="badge" style={{
                                        backgroundColor: '#e8f4f8',
                                        color: '#3498db',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        <i className="fas fa-building me-1"></i>
                                        {quote.branch_name}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        borderWidth: '2px'
                                      }}
                                      onClick={() => {
                                        console.log('🔍 View Quote clicked for:', versionLabel);

                                        // 🎯 Check if this is Purchase_Sale with separate purchase and sale data
                                        if (quote.purchase_breakdown && quote.sale_breakdown) {
                                          console.log('📊 Purchase_Sale quote detected');
                                          // Pass purchase and sale data separately for Purchase_Sale
                                          setSelectedQuote({
                                            legalFeeData: {
                                              purchase: {
                                                fees: quote.purchase_breakdown.fees,
                                                disbursements: quote.purchase_breakdown.disbursements,
                                                totals: quote.purchase_breakdown.totals
                                              },
                                              sale: {
                                                fees: quote.sale_breakdown.fees,
                                                disbursements: quote.sale_breakdown.disbursements,
                                                totals: quote.sale_breakdown.totals
                                              }
                                            },
                                            totals: {
                                              purchase: quote.purchase_breakdown.totals,
                                              sale: quote.sale_breakdown.totals
                                            },
                                            leadData: leadData,
                                            revision_label: versionLabel,
                                            is_history: !isActive
                                          });
                                        } else {
                                          // Transform single service quote data
                                          const transformedFeeData = {
                                            branch_name: quote.branch_name,
                                            service_type: quote.service_type,
                                            legal_fee: quote.fees?.legalFee?.amount || 0,
                                            tt_fee: quote.fees?.ttFee?.amount || quote.fees?.telegraphicTransferFee?.amount || 0,
                                            identity_check: quote.fees?.identityCheck?.amount || 0,
                                            mortgage_admin_fee: quote.fees?.mortgageAdminFee?.amount || 0,
                                            leasehold_fee: quote.fees?.leaseholdFee?.amount || 0,
                                            help_to_buy_isa_fee: quote.fees?.helpToBuyISAFee?.amount || 0,
                                            help_to_buy_fee: quote.fees?.helpToBuyFee?.amount || 0,
                                            right_to_buy_fee: quote.fees?.rightToBuyFee?.amount || 0,
                                            shared_ownership_fee: quote.fees?.sharedOwnershipFee?.amount || 0,
                                            new_build_fee: quote.fees?.newBuildFee?.amount || 0,
                                            islamic_mortgage_fee: quote.fees?.islamicMortgageFee?.amount || 0,
                                            auction_repossession_fee: quote.fees?.auctionRepossessionFee?.amount || 0,
                                            gifted_deposit_fee_first_giftor: quote.fees?.giftedDepositFeeFirstGiftor?.amount || 0,
                                            buy_to_let_fee: quote.fees?.buyToLetFee?.amount || 0,
                                            arranging_indemnity_policy: quote.fees?.arrangingIndemnityPolicy?.amount || 0,
                                            stamp_duty: quote.disbursements?.stampDuty?.amount || 0,
                                            hm_land_registry_fee: quote.disbursements?.hmLandRegistryFee?.amount || 0,
                                            land_registry_search: quote.disbursements?.landRegistrySearch?.amount || 0,
                                            land_charges_search: quote.disbursements?.landChargesSearch?.amount || 0,
                                            search_pack: quote.disbursements?.searchPack?.amount || 0,
                                            official_copies: quote.disbursements?.officialCopies?.amount || 0,
                                            all_fees: quote.fees,
                                            all_disbursements: quote.disbursements
                                          };

                                          setSelectedQuote({
                                            legalFeeData: transformedFeeData,
                                            leadData: leadData,
                                            revision_label: versionLabel,
                                            is_history: !isActive
                                          });
                                        }
                                        setShowQuoteDetailModal(true);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" style={{ fontSize: '14px', marginRight: '4px' }} />
                                      View Details
                                    </Button>
                                  </div>
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                  {/* 🎯 Purchase_Sale: Show separate Purchase and Sale sections */}
                                  {quote.purchase_breakdown && quote.sale_breakdown ? (
                                    <div className="row g-3">
                                      {/* PURCHASE Section */}
                                      <div className="col-md-6">
                                        <div style={{
                                          backgroundColor: '#f0f8ff',
                                          border: '2px solid #0d6efd',
                                          borderRadius: '8px',
                                          padding: '12px'
                                        }}>
                                          <h6 className="mb-2" style={{ color: '#0d6efd', fontWeight: 700, textTransform: 'uppercase' }}>
                                            <i className="fas fa-home me-2"></i>PURCHASE
                                          </h6>
                                          <div className="text-center mb-2">
                                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Total Cost</span>
                                            <h5 className="mb-0" style={{ color: '#0d6efd', fontWeight: 700 }}>
                                              £{Number(quote.purchase_breakdown.totals?.total?.gross || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </h5>
                                          </div>
                                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                            <div className="d-flex justify-content-between">
                                              <span>Legal Cost:</span>
                                              <span>£{Number(quote.purchase_breakdown.totals?.legalCost?.gross || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                              <span>VAT:</span>
                                              <span>£{Number(quote.purchase_breakdown.totals?.total?.vat || 0).toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* SALE Section */}
                                      <div className="col-md-6">
                                        <div style={{
                                          backgroundColor: '#fee',
                                          border: '2px solid #dc3545',
                                          borderRadius: '8px',
                                          padding: '12px'
                                        }}>
                                          <h6 className="mb-2" style={{ color: '#dc3545', fontWeight: 700, textTransform: 'uppercase' }}>
                                            <i className="fas fa-tag me-2"></i>SALE
                                          </h6>
                                          <div className="text-center mb-2">
                                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Total Cost</span>
                                            <h5 className="mb-0" style={{ color: '#dc3545', fontWeight: 700 }}>
                                              £{Number(quote.sale_breakdown.totals?.total?.gross || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </h5>
                                          </div>
                                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                            <div className="d-flex justify-content-between">
                                              <span>Legal Cost:</span>
                                              <span>£{Number(quote.sale_breakdown.totals?.legalCost?.gross || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                              <span>VAT:</span>
                                              <span>£{Number(quote.sale_breakdown.totals?.total?.vat || 0).toFixed(2)}</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Combined Total */}
                                      <div className="col-12">
                                        <div style={{
                                          backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                          border: `2px solid ${isActive ? '#27ae60' : '#dee2e6'}`,
                                          borderRadius: '8px',
                                          padding: '12px',
                                          textAlign: 'center'
                                        }}>
                                          <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600 }}>COMBINED TOTAL: </span>
                                          <span style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700, fontSize: '1.2rem' }}>
                                            £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    /* Single service type display */
                                    <div className="row g-3">
                                      {/* Total Amount - Highlighted */}
                                      <div className="col-md-4">
                                        <div style={{
                                          backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                          padding: '12px',
                                          borderRadius: '8px',
                                          textAlign: 'center'
                                        }}>
                                          <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                            Total Amount
                                          </p>
                                          <h4 className="mb-0" style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700 }}>
                                            £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </h4>
                                        </div>
                                      </div>

                                      {/* Legal Costs */}
                                      <div className="col-md-4">
                                        <div style={{ padding: '12px' }}>
                                          <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                            <i className="fas fa-balance-scale me-1"></i>
                                            Legal Costs
                                          </p>
                                          <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                            £{Number(legalCostGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </h6>
                                        </div>
                                      </div>

                                      {/* VAT */}
                                      <div className="col-md-4">
                                        <div style={{ padding: '12px' }}>
                                          <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                            <i className="fas fa-receipt me-1"></i>
                                            VAT Included
                                          </p>
                                          <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                            £{Number(totalVat).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </h6>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* No quotes message */}
                {(!leadData || !legalFeeData) && (!quoteHistory || quoteHistory.length === 0) && (
                  <div className="text-center py-5">
                    <i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Quotes Available</h5>
                    <p className="text-muted">No quotes have been generated yet.</p>
                  </div>
                )}
              </div>
            </Tab>

            {/* NOTES TAB */}
            <Tab eventKey="notes" title="Notes">
              <Notes lead_id={lead_id} product_id={leadData?.product_id} />
            </Tab>

            {/* COMMUNICATION TAB */}
            <Tab eventKey="communication" title="Communication">
              <div className='communication-table'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Contact Attempt ({contactAttempts})
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('increase')}>
                        <MailOutlineIcon fontSize="small" /> &nbsp; Increase Contact Attempt Count
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('reset')}>
                        <CallOutlinedIcon fontSize="small" /> &nbsp; Reset
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Send Email Dropdown - Right Aligned */}
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="send-email-dropdown" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {emailTemplates.length === 0 ? (
                          <Dropdown.Item disabled>No templates available (Count: {emailTemplates.length})</Dropdown.Item>
                        ) : (
                          emailTemplates.map((template) => (
                            <Dropdown.Item
                              key={template._id}
                              onClick={() => handleSendEmail(template._id, template.name)}
                            >
                              {template.name}
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Refresh Button for debugging */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        fetchEmailTemplates();
                      }}
                      title="Refresh templates"
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </div>

                  {/* Send SMS Button - Hidden for now */}
                  {/* <Button variant="outline-primary" disabled>
                    Send SMS
                  </Button> */}
                </div>

                {/* Email History Table */}
                <div className="mt-4">
                  <Table striped responsive bordered>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th>Customer</th>
                        <th>Content</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingEmails ? (
                        <tr>
                          <td colSpan="4" className="text-center">Loading...</td>
                        </tr>
                      ) : emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">No communication logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log, index) => (
                          <tr key={log._id || index}>
                            <td>
                              {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : (log.isAutomation ? <span className="text-muted">Not triggered</span> : 'N/A')}
                            </td>
                            <td>
                              <div>
                                {log.recipient_name || log.customer_id?.forename || 'Unknown'}
                                <div className="text-muted small">{log.recipient_email}</div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.template_name || log.subject || 'No title'}</strong>
                                <div className="text-muted small">
                                  {log.subject && log.subject !== log.template_name && (
                                    <span>Subject: {log.subject}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewEmailTemplate(log)}
                                title="View email content"
                                sx={{ minWidth: '40px', padding: '4px 8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="appointment" title="Tasks">
              <div className='mt-4'>
                <Table striped responsive bordered>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Date / Time</th>
                      <th>Case Ref</th>
                      <th>Booked</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="8" className='text-center'>No tasks available</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Tab>

          </Tabs>
        </div>
      </div>

      <ConveyancingQuoteModal 
        show={modalShow} 
        onHide={() => {
          setModalShow(false);
          setSelectedQuote(null);
        }}
        quoteData={selectedQuote}
        leadData={leadData}
      />
      
      {/* Revise Quote Modal */}
      <ReviseQuoteModal
        show={reviseModalShow}
        onHide={() => setReviseModalShow(false)}
        leadData={leadData}
        authToken={authCtx.token}
        onReviseQuote={handleReviseQuote}
        legalFeeData={legalFeeData}
        quoteFeeData={quoteFeeData}
      />

      {/* View Quote Modal - Using calculateAndSaveLeadQuote unified API */}
      <Modal show={showQuoteModal} onHide={() => setShowQuoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Quote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFee ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading quote details...</p>
            </div>
          ) : quoteFeeData ? (
            <div>
              <h5>{leadData?.service_type || 'Purchase'}</h5>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th></th>
                    <th>NET</th>
                    <th>VAT</th>
                    <th>GROSS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="4"><strong>Legal Fees</strong></td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.legalFee?.label || 'Legal Fees'}</td>
                    <td>£{quoteFeeData.fees?.legalFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.legalFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.legalFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td colSpan="4"><strong>Fixed Fees</strong></td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.ttFee?.label || 'TT Fee'}</td>
                    <td>£{quoteFeeData.fees?.ttFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.ttFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.ttFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.identityCheck?.label || 'Identity Check'}</td>
                    <td>£{quoteFeeData.fees?.identityCheck?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.identityCheck?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.identityCheck?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.mortgageAdminFee?.label || 'Mortgage Admin Fee'}</td>
                    <td>£{quoteFeeData.fees?.mortgageAdminFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.mortgageAdminFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.mortgageAdminFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.helpToBuyISAFee?.label || 'Help to Buy ISA Fee'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyISAFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyISAFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyISAFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.helpToBuyFee?.label || 'Help to Buy'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.helpToBuyFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.rightToBuyFee?.label || 'Right to Buy Fee'}</td>
                    <td>£{quoteFeeData.fees?.rightToBuyFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.rightToBuyFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.rightToBuyFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.islamicMortgageFee?.label || 'Islamic Mortgage Fee'}</td>
                    <td>£{quoteFeeData.fees?.islamicMortgageFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.islamicMortgageFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.islamicMortgageFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.giftedDepositFee?.label || 'Gifted Deposit Fee (1st Giftor)'}</td>
                    <td>£{quoteFeeData.fees?.giftedDepositFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.giftedDepositFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.giftedDepositFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.buyToLetFee?.label || 'Buy to Let Fee'}</td>
                    <td>£{quoteFeeData.fees?.buyToLetFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.buyToLetFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.buyToLetFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.sharedOwnershipFee?.label || 'Shared Ownership Fee'}</td>
                    <td>£{quoteFeeData.fees?.sharedOwnershipFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.sharedOwnershipFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.sharedOwnershipFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.newBuildFee?.label || 'New Build Fee'}</td>
                    <td>£{quoteFeeData.fees?.newBuildFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.newBuildFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.newBuildFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.auctionRepossessionFee?.label || 'Auction/Repossession Fee'}</td>
                    <td>£{quoteFeeData.fees?.auctionRepossessionFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.auctionRepossessionFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.auctionRepossessionFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.arrangingIndemnityPolicy?.label || 'Arranging Indemnity Policy'}</td>
                    <td>£{quoteFeeData.fees?.arrangingIndemnityPolicy?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.arrangingIndemnityPolicy?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.arrangingIndemnityPolicy?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  {/* Sale-specific fees */}
                  {quoteFeeData.fees?.mortgageRedemptionFee && (
                    <tr>
                      <td>{quoteFeeData.fees?.mortgageRedemptionFee?.label || 'Mortgage Redemption Fee'}</td>
                      <td>£{quoteFeeData.fees?.mortgageRedemptionFee?.amount?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.mortgageRedemptionFee?.vat?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.mortgageRedemptionFee?.gross?.toFixed(2) || '0.00'}</td>
                    </tr>
                  )}
                  {quoteFeeData.fees?.leaseholdFee && (
                    <tr>
                      <td>{quoteFeeData.fees?.leaseholdFee?.label || 'Leasehold Fee'}</td>
                      <td>£{quoteFeeData.fees?.leaseholdFee?.amount?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.leaseholdFee?.vat?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.leaseholdFee?.gross?.toFixed(2) || '0.00'}</td>
                    </tr>
                  )}
                  {quoteFeeData.fees?.managementPackFee && (
                    <tr>
                      <td>{quoteFeeData.fees?.managementPackFee?.label || 'Management Pack Fee'}</td>
                      <td>£{quoteFeeData.fees?.managementPackFee?.amount?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.managementPackFee?.vat?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.managementPackFee?.gross?.toFixed(2) || '0.00'}</td>
                    </tr>
                  )}
                  {quoteFeeData.fees?.fittingsFormFee && (
                    <tr>
                      <td>{quoteFeeData.fees?.fittingsFormFee?.label || 'Fittings and Contents Form (TA10)'}</td>
                      <td>£{quoteFeeData.fees?.fittingsFormFee?.amount?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.fittingsFormFee?.vat?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.fittingsFormFee?.gross?.toFixed(2) || '0.00'}</td>
                    </tr>
                  )}
                  {quoteFeeData.fees?.energyPerformanceFee && (
                    <tr>
                      <td>{quoteFeeData.fees?.energyPerformanceFee?.label || 'Energy Performance Certificate'}</td>
                      <td>£{quoteFeeData.fees?.energyPerformanceFee?.amount?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.energyPerformanceFee?.vat?.toFixed(2) || '0.00'}</td>
                      <td>£{quoteFeeData.fees?.energyPerformanceFee?.gross?.toFixed(2) || '0.00'}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{quoteFeeData.fees?.stampDuty?.label || 'Stamp Duty Land Tax'}</td>
                    <td>£{quoteFeeData.fees?.stampDuty?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.stampDuty?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.stampDuty?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td colSpan="4"><strong>Disbursements</strong></td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.hmLandRegistryFee?.label || 'HM Land Registry Fee'}</td>
                    <td>£{quoteFeeData.fees?.hmLandRegistryFee?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.hmLandRegistryFee?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.hmLandRegistryFee?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.landRegistrySearch?.label || 'Land Registry Priority Search'}</td>
                    <td>£{quoteFeeData.fees?.landRegistrySearch?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.landRegistrySearch?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.landRegistrySearch?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.landChargesSearch?.label || 'Land Charges Search'}</td>
                    <td>£{quoteFeeData.fees?.landChargesSearch?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.landChargesSearch?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.landChargesSearch?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <td>{quoteFeeData.fees?.searchPack?.label || 'Search Pack - Local Authority, Water & Drainage'}</td>
                    <td>£{quoteFeeData.fees?.searchPack?.amount?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.searchPack?.vat?.toFixed(2) || '0.00'}</td>
                    <td>£{quoteFeeData.fees?.searchPack?.gross?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr style={{backgroundColor: '#f8f9fa', fontWeight: 'bold'}}>
                    <td>TOTAL</td>
                    <td>£{(() => {
                      const fees = quoteFeeData.fees || {};
                      const total = Object.values(fees).reduce((sum, fee) => sum + (fee?.amount || 0), 0);
                      return total.toFixed(2);
                    })()}</td>
                    <td>£{(() => {
                      const fees = quoteFeeData.fees || {};
                      const total = Object.values(fees).reduce((sum, fee) => sum + (fee?.vat || 0), 0);
                      return total.toFixed(2);
                    })()}</td>
                    <td>£{(() => {
                      const fees = quoteFeeData.fees || {};
                      const total = Object.values(fees).reduce((sum, fee) => sum + (fee?.gross || 0), 0);
                      return total.toFixed(2);
                    })()}</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-muted small">Source: {quoteFeeData.branch_name}</p>
            </div>
          ) : (
            <p>No quote data available</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuoteModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Quote Detail Modal - Sale or Purchase specific */}
      {console.log('🟦 ViewQuoteDetailModal props:', {
        show: showQuoteDetailModal,
        serviceType: leadData?.service_type,
        legalFeeData: selectedQuote?.legalFeeData || legalFeeData,
        leadData: selectedQuote?.leadData || leadData
      })}
      {leadData?.service_type === 'Sale' ? (
        <ViewQuoteDetailModalSale
          show={showQuoteDetailModal}
          onHide={() => {
            console.log('🔵 Closing ViewQuoteDetailModalSale');
            setShowQuoteDetailModal(false);
            setSelectedQuote(null);
          }}
          leadId={leadData?._id || lead_id}
          legalFeeData={selectedQuote?.legalFeeData || legalFeeData}
          leadData={selectedQuote?.leadData || leadData}
        />
      ) : (
        <ViewQuoteDetailModal
          show={showQuoteDetailModal}
          onHide={() => {
            console.log('🔵 Closing ViewQuoteDetailModal');
            setShowQuoteDetailModal(false);
            setSelectedQuote(null);
          }}
          leadId={leadData?._id || lead_id}
          authToken={authCtx.token}
          legalFeeData={selectedQuote?.legalFeeData || legalFeeData}
          leadData={selectedQuote?.leadData || leadData}
          calculationTotals={selectedQuote?.totals || calculationTotals || null}
        />
      )}

      {/* Email View Modal */}
      <Modal show={viewEmailModal} onHide={() => setViewEmailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEmail?.template_name || selectedEmail?.subject || 'Email Template'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail && (
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                padding: '10px'
              }}
              dangerouslySetInnerHTML={{ __html: selectedEmail.content || 'No content available' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <Button
            variant="outlined"
            onClick={handleDownloadEmailTemplate}
          >
            <i className="bi bi-download" style={{ marginRight: '5px' }}></i>
            Download
          </Button>
          <Button variant="contained" onClick={() => setViewEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Conveyancing Modal - Commented out as we're using route navigation now */}
      {/* <EditConveyancingModal
        show={editModalShow}
        onHide={() => setEditModalShow(false)}
        leadData={leadData}
        authToken={authCtx.token}
        onUpdate={(updatedData) => {
          // Update local state with new data
          setLeadData(prev => ({ ...prev, ...updatedData }));
          // Reload page to reflect changes
          setTimeout(() => window.location.reload(), 1000);
        }}
      /> */}
    </>
  );
}